/**
 * Course Detail Page (Teacher)
 * D2L Brightspace-style tabs: Content (table of contents + detail pane),
 * Assessments (Quizzes | Scaffolds), Classlist, Insights
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import Modal from '../../components/shared/Modal';
import FilePreviewModal from '../../components/shared/FilePreviewModal';
import { analyzeLink } from '../../utils/embedProviders';
import Input from '../../components/shared/Input';
import Button from '../../components/shared/Button';
import {
  getCourse,
  updateCourse,
  deleteLecture,
  getCourseSlides,
  deleteSlide,
  getCourseRecommendedVideos,
  addRecommendedVideo,
  deleteRecommendedVideo,
  processRecommendedVideo,
  getCourseQuizzes,
  deleteQuiz,
  getQuiz,
  updateQuiz,
  getCourseScaffolds,
  deleteScaffold,
  getScaffold,
  updateScaffold,
  CourseResponse,
  CourseUpdateRequest,
  LectureResponse,
  SlideResponse,
  RecommendedVideoResponse,
  QuizListItem,
  QuizResponse,
  QuizQuestion,
  QuizUpdateRequest,
  ScaffoldListItem,
  ScaffoldResponse,
  getCourseReflectionAnalytics,
  CourseReflectionAnalyticsResponse,
  startReflectionAnalysis,
  getReflectionAnalysis,
  ReflectionAnalysisResults,
  getReflectionHeatmap,
  ReflectionHeatmap,
  getCourseStructure,
  saveCourseStructure,
  CourseStructure,
  StructureLecture,
  LearningObjective,
  getLearningPath,
  LearningPath,
  LearningPathConcept,
  startReflectionOutcomes,
  getReflectionOutcomes,
  ReflectionOutcomes,
  getCourseStudents,
  EnrolledStudentsListResponse,
  getGradebook,
  Gradebook,
  GradebookRow,
  setGradeOverride,
  getQuizAttempts,
  getQuizAttemptDetail,
  setAttemptScores,
  reviewStudentGrade,
  releaseQuizGrades,
  QuizAttemptSummary,
  QuizAttemptDetail,
  createAssignment,
  listAssignments,
  updateAssignment,
  deleteAssignment,
  addAssignmentAttachment,
  getAssignmentSubmissions,
  gradeSubmission,
  releaseAssignmentGrades,
  AssignmentResponse,
  AssignmentSubmission,
  publishScaffoldsAsQuiz,
  getContinueWatching,
  ContinueWatchingItem,
  listContentFolders,
  listCourseFiles,
  listQuizGenerationJobs,
  extractFileTopics,
  addCourseLink,
  createContentFolder,
  renameContentFolder,
  deleteContentFolder,
  uploadCourseFile,
  deleteCourseFile,
  setContentVisibility,
  ContentFolder,
  CourseFile,
  importRoster,
  RosterImportResult,
} from '../../api/courses';
import { useUpload } from '../../context/UploadContext';
import { useScaffoldGeneration } from '../../context/ScaffoldGenerationContext';
import { useQuizGeneration } from '../../context/QuizGenerationContext';
import { getStaticBaseUrl } from '../../api/client';
import { getCourseVisual } from '../../utils/courseVisuals';
import SubjectGlyph from '../../components/courses/SubjectGlyph';
import Mascot from '../../components/shared/Mascot';
import BloomLadderDemo from '../../components/shared/BloomLadderDemo';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie,
} from 'recharts';

type TabId = 'home' | 'content' | 'assessments' | 'grades' | 'classlist' | 'insights';
type ContentView = 'lecture' | 'slides' | 'recommended' | 'folder' | 'files';
type AssessmentsTab = 'quizzes' | 'scaffolds' | 'assignments';
type QuizListFilter = 'all' | 'needs_review' | 'drafts' | 'open';
type AssignmentListFilter = 'all' | 'needs_grading' | 'drafts' | 'open';
type ScaffoldListFilter = 'all' | 'published' | 'drafts';
type InsightsTab = 'trends' | 'paths' | 'outcomes' | 'structure';

// Concept-state palette for learning paths (dot pills + chips)
const CONCEPT_STATES: Record<
  'engaged' | 'flipped' | 'recurring' | 'raised',
  { label: string; pill: string; dot: string }
> = {
  engaged: {
    label: 'Engaged',
    pill: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
  flipped: {
    label: 'Flipped',
    pill: 'bg-sky-50 text-sky-700 dark:bg-sky-900/25 dark:text-sky-300',
    dot: 'bg-sky-400',
  },
  recurring: {
    label: 'Recurring',
    pill: 'bg-red-50 text-red-700 dark:bg-red-900/25 dark:text-red-300',
    dot: 'bg-red-500',
  },
  raised: {
    label: 'Raised',
    pill: 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
};

// Roster import action palette (dot pills in the import results table)
const ROSTER_ACTIONS: Record<
  'created' | 'enrolled' | 'skipped' | 'error',
  { label: string; pill: string; dot: string }
> = {
  created: {
    label: 'Created',
    pill: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
  enrolled: {
    label: 'Enrolled',
    pill: 'bg-sky-50 text-sky-700 dark:bg-sky-900/25 dark:text-sky-300',
    dot: 'bg-sky-400',
  },
  skipped: {
    label: 'Skipped',
    pill: 'bg-gray-100 text-gray-600 dark:bg-secondary-700 dark:text-gray-300',
    dot: 'bg-gray-400',
  },
  error: {
    label: 'Error',
    pill: 'bg-red-50 text-red-700 dark:bg-red-900/25 dark:text-red-300',
    dot: 'bg-red-500',
  },
};

// Verdict palette for student outcome cards
const OUTCOME_VERDICTS: Record<string, { pill: string; dot: string }> = {
  'On track': {
    pill: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
  'Some gaps': {
    pill: 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
  'Needs support': {
    pill: 'bg-red-50 text-red-700 dark:bg-red-900/25 dark:text-red-300',
    dot: 'bg-red-500',
  },
};

// Outcome color palette for the Course structure editor: cycled by outcome index,
// used consistently for dots, module chips, and the structure tree preview
const OUTCOME_PALETTE = [
  {
    dot: 'bg-sky-500',
    chip: 'bg-sky-50 text-sky-800 ring-2 ring-sky-300 dark:bg-sky-900/30 dark:text-sky-300 dark:ring-sky-700',
  },
  {
    dot: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-800 ring-2 ring-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-700',
  },
  {
    dot: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-800 ring-2 ring-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-700',
  },
  {
    dot: 'bg-violet-500',
    chip: 'bg-violet-50 text-violet-800 ring-2 ring-violet-300 dark:bg-violet-900/30 dark:text-violet-300 dark:ring-violet-700',
  },
  {
    dot: 'bg-pink-500',
    chip: 'bg-pink-50 text-pink-800 ring-2 ring-pink-300 dark:bg-pink-900/30 dark:text-pink-300 dark:ring-pink-700',
  },
  {
    dot: 'bg-teal-500',
    chip: 'bg-teal-50 text-teal-800 ring-2 ring-teal-300 dark:bg-teal-900/30 dark:text-teal-300 dark:ring-teal-700',
  },
];

// Objective-status palette for per-objective rows on outcome cards
const OBJECTIVE_STATUS: Record<string, { label: string; dot: string; text: string }> = {
  strong: { label: 'Strong', dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-400' },
  developing: { label: 'Developing', dot: 'bg-sky-400', text: 'text-sky-700 dark:text-sky-400' },
  unresolved: { label: 'Unresolved', dot: 'bg-red-500', text: 'text-red-700 dark:text-red-400' },
  'no-evidence': { label: 'No evidence', dot: 'bg-gray-400', text: 'text-gray-500 dark:text-gray-400' },
};

/** "challenging in L3, L5 · interesting in L9" from 0-based lecture orders. */
const lectureRefs = (orders: number[]): string => orders.map((o) => `L${o + 1}`).join(', ');

/** Human explanation line for a concept row on the Learning paths view. */
const conceptExplanation = (c: LearningPathConcept): string => {
  if (c.state === 'flipped') {
    const lastChal = c.chal_lectures.length > 0 ? Math.max(...c.chal_lectures) : -1;
    const resolved =
      c.int_lectures.find((o) => o > lastChal) ??
      (c.int_lectures.length > 0 ? Math.max(...c.int_lectures) : lastChal);
    return `Challenging L${lastChal + 1}, later interesting L${resolved + 1}`;
  }
  if (c.state === 'engaged') return `Interested in ${lectureRefs(c.int_lectures)}`;
  if (c.state === 'recurring') return `Challenging across ${lectureRefs(c.chal_lectures)}`;
  return `Challenging in ${lectureRefs(c.chal_lectures)}`;
};

/** Concept display order on the Learning paths view: struggles first. */
const CONCEPT_SORT: Record<LearningPathConcept['state'], number> = {
  recurring: 0,
  raised: 1,
  flipped: 2,
  engaged: 3,
};

/** Tinted icon chips per concept state (inline SVGs, no deps). */
const CONCEPT_ICON_CHIP: Record<LearningPathConcept['state'], { chip: string; icon: React.ReactNode }> = {
  engaged: {
    chip: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/25 dark:text-emerald-300',
    icon: (
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
        <path d="M10 2l1.8 4.6L16.5 8l-4.7 1.4L10 14l-1.8-4.6L3.5 8l4.7-1.4L10 2z" />
        <path d="M15.5 12l.8 1.9 1.9.8-1.9.8-.8 1.9-.8-1.9-1.9-.8 1.9-.8.8-1.9z" />
      </svg>
    ),
  },
  flipped: {
    chip: 'bg-sky-50 text-[#649cdd] dark:bg-sky-900/25 dark:text-sky-300',
    icon: (
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.3a1 1 0 00-1.4-1.4L9 10.6 7.7 9.3a1 1 0 00-1.4 1.4l2 2a1 1 0 001.4 0l4-4z" clipRule="evenodd" />
      </svg>
    ),
  },
  recurring: {
    chip: 'bg-red-50 text-red-600 dark:bg-red-900/25 dark:text-red-300',
    icon: (
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-13a1 1 0 011 1v4a1 1 0 11-2 0V6a1 1 0 011-1zm0 9a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5z" clipRule="evenodd" />
      </svg>
    ),
  },
  raised: {
    chip: 'bg-amber-50 text-amber-600 dark:bg-amber-900/25 dark:text-amber-300',
    icon: (
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
        <path d="M11.7 2.4c.4 2.2-.3 3.8-1.3 4.9-.7.8-1.4 1.6-1.5 2.9-.7-.4-1.2-1-1.4-1.9C6.2 9.6 5 11.2 5 13a5 5 0 0010 0c0-3.6-2.4-4.8-3.3-10.6zM10 16.5A2.5 2.5 0 017.5 14c0-.9.5-1.7 1.2-2.4.3.8.9 1.4 1.7 1.7.6.4 2.1 1 2.1 2.2a2.5 2.5 0 01-2.5 1z" />
      </svg>
    ),
  },
};

/** Stat cards on the Learning paths view. */
const PATH_STAT_CARDS: { state: LearningPathConcept['state']; label: string; num: string }[] = [
  { state: 'engaged', label: 'Engaged', num: 'text-emerald-600 dark:text-emerald-300' },
  { state: 'flipped', label: 'Later eased', num: 'text-[#649cdd] dark:text-sky-300' },
  { state: 'recurring', label: 'Recurring', num: 'text-red-600 dark:text-red-300' },
  { state: 'raised', label: 'Raised once', num: 'text-amber-600 dark:text-amber-300' },
];

const BLOOM_COLORS: Record<number, { bg: string; text: string; darkBg: string; darkText: string; dot: string }> = {
  1: { bg: 'bg-blue-100', text: 'text-blue-700', darkBg: 'dark:bg-blue-900/30', darkText: 'dark:text-blue-400', dot: 'bg-blue-500' },
  2: { bg: 'bg-teal-100', text: 'text-teal-700', darkBg: 'dark:bg-teal-900/30', darkText: 'dark:text-teal-400', dot: 'bg-teal-500' },
  3: { bg: 'bg-green-100', text: 'text-green-700', darkBg: 'dark:bg-green-900/30', darkText: 'dark:text-green-400', dot: 'bg-green-500' },
  4: { bg: 'bg-amber-100', text: 'text-amber-700', darkBg: 'dark:bg-amber-900/30', darkText: 'dark:text-amber-400', dot: 'bg-amber-500' },
  5: { bg: 'bg-orange-100', text: 'text-orange-700', darkBg: 'dark:bg-orange-900/30', darkText: 'dark:text-orange-400', dot: 'bg-orange-500' },
  6: { bg: 'bg-red-100', text: 'text-red-700', darkBg: 'dark:bg-red-900/30', darkText: 'dark:text-red-400', dot: 'bg-red-500' },
};

const BLOOM_LABELS: Record<number, string> = {
  1: 'Remember', 2: 'Understand', 3: 'Apply', 4: 'Analyze', 5: 'Evaluate', 6: 'Create',
};

/** Format a points value for display: trims float noise, keeps halves (e.g. "2.5"). */
const formatPoints = (n: number): string => (Math.round(n * 100) / 100).toString();

/** Combined quiz lifecycle status derived from the published flag + schedule dates. */
const getQuizStatus = (quiz: { published: boolean; available_from?: string | null; due_at?: string | null }): { label: 'Draft' | 'Scheduled' | 'Open' | 'Closed'; pill: string; dot: string } => {
  if (!quiz.published) {
    return { label: 'Draft', pill: 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400', dot: 'bg-gray-400' };
  }
  const now = Date.now();
  if (quiz.available_from && new Date(quiz.available_from).getTime() > now) {
    return { label: 'Scheduled', pill: 'bg-sky-50 text-sky-700 dark:bg-sky-900/25 dark:text-sky-300', dot: 'bg-sky-500' };
  }
  if (quiz.due_at && new Date(quiz.due_at).getTime() < now) {
    return { label: 'Closed', pill: 'bg-red-50 text-red-700 dark:bg-red-900/25 dark:text-red-300', dot: 'bg-red-500' };
  }
  return { label: 'Open', pill: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300', dot: 'bg-emerald-500' };
};

/** Safely read an optional bloom_level off a quiz question (present on scaffold-published quizzes). */
const getQuestionBloom = (q: QuizQuestion): number | undefined => {
  const lvl = (q as QuizQuestion & { bloom_level?: unknown }).bloom_level;
  return typeof lvl === 'number' && lvl >= 1 && lvl <= 6 ? Math.round(lvl) : undefined;
};

// ── Student-visibility (hide/show) glyphs + pill ──
type VisibilityItemType = 'lecture' | 'slide' | 'recommended_video' | 'folder' | 'file';

const eyeGlyph = (className: string) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
    <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const eyeOffGlyph = (className: string) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
  </svg>
);

/** Quiet gray dot pill flagging teacher-only content. */
const HiddenPill: React.FC<{ label?: string }> = ({ label = 'Hidden' }) => (
  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
    <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
    {label}
  </span>
);

const SCAFFOLD_PRESETS = [
  { id: 'full_scaffold', label: 'Full Scaffold', desc: 'Complete cognitive progression', levels: [1,2,3,4,5,6], recommended: true },
  { id: 'lower_order', label: 'Lower Order', desc: 'Foundational thinking skills', levels: [1,2,3], recommended: false },
  { id: 'higher_order', label: 'Higher Order', desc: 'Advanced critical thinking', levels: [4,5,6], recommended: false },
  { id: 'critical_thinking', label: 'Critical Thinking', desc: 'Evaluation and synthesis', levels: [5,6], recommended: false },
];

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  {
    id: 'home',
    label: 'Home',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    id: 'content',
    label: 'Content',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
  },
  {
    id: 'assessments',
    label: 'Assessments',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 14l2 2 4-4" />
      </svg>
    ),
  },
  {
    id: 'grades',
    label: 'Grades',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
      </svg>
    ),
  },
  {
    id: 'classlist',
    label: 'Classlist',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    id: 'insights',
    label: 'Insights',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
];

const CourseDetailPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { startLectureUpload, startSlideUpload } = useUpload();
  const { startGeneration: startScaffoldGeneration } = useScaffoldGeneration();
  const { startQuizJob, attachJobs, completionVersion } = useQuizGeneration();

  const [course, setCourse] = useState<CourseResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabId>('home');

  // Content tab: D2L-style table-of-contents selection
  const [contentView, setContentView] = useState<ContentView>('lecture');
  const [selectedLectureId, setSelectedLectureId] = useState<string | null>(null);
  const [tocLecturesOpen, setTocLecturesOpen] = useState(true);
  const [tocMaterialsOpen, setTocMaterialsOpen] = useState(true);
  const autoSelectedLectureRef = useRef<string | null>(null);

  // Assessments tab: sub-tab pills
  const [assessmentsTab, setAssessmentsTab] = useState<AssessmentsTab>('quizzes');

  // Home tab: edit course home modal
  const [isEditHomeModalOpen, setIsEditHomeModalOpen] = useState(false);
  const [homeSaving, setHomeSaving] = useState(false);
  const [homeErrors, setHomeErrors] = useState<{ general?: string }>({});
  const [homeForm, setHomeForm] = useState({
    welcomeMessage: '',
    classSchedule: '',
    classMeetingLink: '',
    officeHours: '',
    officeHoursLink: '',
    contactEmail: '',
    classLocation: '',
    meetingDays: [] as string[],
    meetingStart: '',
    meetingEnd: '',
    courseStart: '',
    courseEnd: '',
  });

  // Lectures content-manager view controls
  const [lecturesView, setLecturesView] = useState<'grid' | 'list'>('grid');
  const [lastWatched, setLastWatched] = useState<ContinueWatchingItem | null>(null);

  // Most recently watched, unfinished lecture in this course (continue-watching banner)
  useEffect(() => {
    if (!courseId) return;
    getContinueWatching()
      .then((items) => setLastWatched(items.find((i) => i.course_id === courseId) || null))
      .catch(() => {});
  }, [courseId]);
  const [lecturesSort, setLecturesSort] = useState<'order' | 'newest' | 'title'>('order');

  // Slides state
  const [slides, setSlides] = useState<SlideResponse[]>([]);
  const [slidesLoading, setSlidesLoading] = useState(false);

  // Content folders + course files (D2L-style course materials)
  const [contentFolders, setContentFolders] = useState<ContentFolder[]>([]);
  const [previewFile, setPreviewFile] = useState<CourseFile | null>(null);
  const [selectedEmbedLink, setSelectedEmbedLink] = useState<CourseFile | null>(null);
  const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({ url: '', title: '' });
  const [linkError, setLinkError] = useState('');
  const [linkSaving, setLinkSaving] = useState(false);
  const [courseFiles, setCourseFiles] = useState<CourseFile[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const materialsFileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileTitleInput, setFileTitleInput] = useState('');
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(null);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  // Folder name modal (create and rename share it)
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [folderModalMode, setFolderModalMode] = useState<'create' | 'rename'>('create');
  const [folderNameInput, setFolderNameInput] = useState('');
  const [folderModalError, setFolderModalError] = useState('');
  const [folderSaving, setFolderSaving] = useState(false);
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<ContentFolder | null>(null);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);

  // Upload Lecture Modal
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [lectureForm, setLectureForm] = useState({
    title: '',
    description: '',
    videoFile: null as File | null,
  });
  const [lectureErrors, setLectureErrors] = useState<{
    title?: string;
    video?: string;
    general?: string;
  }>({});

  // Upload Slide Modal
  const [isSlideUploadModalOpen, setIsSlideUploadModalOpen] = useState(false);
  const [slideForm, setSlideForm] = useState({
    title: '',
    file: null as File | null,
  });
  const [slideErrors, setSlideErrors] = useState<{
    title?: string;
    file?: string;
    general?: string;
  }>({});

  // Recommended Videos state
  const [recommendedVideos, setRecommendedVideos] = useState<RecommendedVideoResponse[]>([]);
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [isAddVideoModalOpen, setIsAddVideoModalOpen] = useState(false);
  const [addVideoLoading, setAddVideoLoading] = useState(false);
  const [videoForm, setVideoForm] = useState({ title: '', youtubeUrl: '' });
  const [videoErrors, setVideoErrors] = useState<{ title?: string; url?: string; general?: string }>({});

  // Quizzes state
  const [quizzes, setQuizzes] = useState<QuizListItem[]>([]);
  const [quizzesLoading, setQuizzesLoading] = useState(false);
  const [isGenerateQuizModalOpen, setIsGenerateQuizModalOpen] = useState(false);
  const [quizForm, setQuizForm] = useState({ title: '', selectedLectures: [] as string[], selectedSlides: [] as string[], selectedRecommendedVideos: [] as string[], selectedFiles: [] as string[], numQuestions: 10, openEndedCount: 0, bloomLevels: [] as number[], difficulty: '' as '' | 'easy' | 'medium' | 'hard', maxAttempts: 1 });
  const [quizErrors, setQuizErrors] = useState<{ general?: string }>({});
  // Client-side quiz list filter pills (All | Needs review | Drafts | Open)
  const [quizListFilter, setQuizListFilter] = useState<QuizListFilter>('all');

  // Quiz detail/edit state
  const [viewingQuiz, setViewingQuiz] = useState<QuizResponse | null>(null);
  const [editingQuestions, setEditingQuestions] = useState<QuizQuestion[]>([]);
  const [quizSaving, setQuizSaving] = useState(false);
  const [quizPublishing, setQuizPublishing] = useState(false);
  const [quizDetailLoading, setQuizDetailLoading] = useState(false);
  // UI-only: per-question collapse state in the quiz editor (default: first question expanded)
  const [expandedQuestions, setExpandedQuestions] = useState<Record<string, boolean>>({});
  // UI-only: raw text drafts for the per-question Points inputs (lets the field be cleared mid-edit)
  const [questionPointsDrafts, setQuestionPointsDrafts] = useState<Record<string, string>>({});
  // Quiz workspace header overflow ("...") menu
  const [quizMenuOpen, setQuizMenuOpen] = useState(false);
  const quizMenuRef = useRef<HTMLDivElement | null>(null);

  // Quiz schedule modal (shared by the publish flow and "Edit schedule")
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleModalMode, setScheduleModalMode] = useState<'publish' | 'edit'>('publish');
  const [scheduleForm, setScheduleForm] = useState({ availableFrom: '', dueAt: '', maxAttempts: '' });
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleSaving, setScheduleSaving] = useState(false);

  // Quiz workspace internal pill nav (Questions | Submissions | Settings)
  const [quizSubView, setQuizSubView] = useState<'questions' | 'submissions' | 'settings'>('questions');
  const [quizAttempts, setQuizAttempts] = useState<QuizAttemptSummary[]>([]);
  const [quizAttemptsLoading, setQuizAttemptsLoading] = useState(false);
  const [viewingAttempt, setViewingAttempt] = useState<QuizAttemptDetail | null>(null);
  const [attemptDetailLoading, setAttemptDetailLoading] = useState(false);
  const [attemptFeedbackDraft, setAttemptFeedbackDraft] = useState('');
  const [attemptFeedbackSaving, setAttemptFeedbackSaving] = useState(false);
  const [attemptReleaseSaving, setAttemptReleaseSaving] = useState(false);
  const [releasingQuizGradesAll, setReleasingQuizGradesAll] = useState(false);
  // Per-question score correction (attempt review): which question is being edited + draft value
  const [scoreEditQid, setScoreEditQid] = useState<string | null>(null);
  const [scoreDraft, setScoreDraft] = useState('');
  const [scoreSaving, setScoreSaving] = useState(false);

  // Assignments state
  const [assignments, setAssignments] = useState<AssignmentResponse[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  // Client-side assignment list filter pills (All | Needs grading | Drafts | Open)
  const [assignmentListFilter, setAssignmentListFilter] = useState<AssignmentListFilter>('all');
  const [viewingAssignment, setViewingAssignment] = useState<AssignmentResponse | null>(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState<string | null>(null);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [assignmentModalMode, setAssignmentModalMode] = useState<'create' | 'edit'>('create');
  const [assignmentForm, setAssignmentForm] = useState({ title: '', instructions: '', availableFrom: '', dueAt: '' });
  const [assignmentModalError, setAssignmentModalError] = useState('');
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [assignmentPublishing, setAssignmentPublishing] = useState(false);
  const assignmentFileInputRef = useRef<HTMLInputElement | null>(null);
  const [assignmentUploadingName, setAssignmentUploadingName] = useState<string | null>(null);
  const [assignmentUploadProgress, setAssignmentUploadProgress] = useState(0);
  const [assignmentSubmissions, setAssignmentSubmissions] = useState<AssignmentSubmission[]>([]);
  const [assignmentSubsLoading, setAssignmentSubsLoading] = useState(false);
  // Per-submission inline grading drafts, keyed by submission_id
  const [subGradeDrafts, setSubGradeDrafts] = useState<Record<string, { grade: string; feedback: string }>>({});
  const [subSavingId, setSubSavingId] = useState<string | null>(null);
  const [subReleasingId, setSubReleasingId] = useState<string | null>(null);
  const [releasingAssignmentGradesAll, setReleasingAssignmentGradesAll] = useState(false);

  // Scaffolds state
  const [scaffolds, setScaffolds] = useState<ScaffoldListItem[]>([]);
  const [scaffoldsLoading, setScaffoldsLoading] = useState(false);
  const [isGenerateScaffoldModalOpen, setIsGenerateScaffoldModalOpen] = useState(false);
  const [scaffoldWizardStep, setScaffoldWizardStep] = useState<1 | 2 | 3>(1);
  const [scaffoldForm, setScaffoldForm] = useState({
    selectedLectures: [] as string[],
    selectedSlides: [] as string[],
    selectedFiles: [] as string[],
    selectedTopics: [] as string[],
    preset: 'full_scaffold',
  });
  const [scaffoldTopicsLoading, setScaffoldTopicsLoading] = useState(false);
  const [scaffoldAvailableTopics, setScaffoldAvailableTopics] = useState<string[]>([]);
  const [scaffoldErrors, setScaffoldErrors] = useState<{ general?: string }>({});

  // Scaffold detail state
  const [viewingScaffold, setViewingScaffold] = useState<ScaffoldResponse | null>(null);
  const [scaffoldDetailLoading, setScaffoldDetailLoading] = useState(false);
  const [scaffoldPublishing, setScaffoldPublishing] = useState(false);
  const [scaffoldToDelete, setScaffoldToDelete] = useState<string | null>(null);
  const [expandedExemplar, setExpandedExemplar] = useState<string | null>(null);

  // Scaffold batch folder + publish-as-quiz state
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const [selectedScaffolds, setSelectedScaffolds] = useState<Set<string>>(new Set());
  // Client-side quiz generator list filter pills (All | Published | Drafts)
  const [scaffoldListFilter, setScaffoldListFilter] = useState<ScaffoldListFilter>('all');
  const [howQuizzesOpen, setHowQuizzesOpen] = useState(false);
  const [isPublishAsQuizModalOpen, setIsPublishAsQuizModalOpen] = useState(false);
  const [publishAsQuizTitle, setPublishAsQuizTitle] = useState('');
  const [publishAsQuizLoading, setPublishAsQuizLoading] = useState(false);

  // Course-level reflection analytics state (Reflections tab)
  const [courseReflections, setCourseReflections] = useState<CourseReflectionAnalyticsResponse | null>(null);
  const [courseReflectionsLoading, setCourseReflectionsLoading] = useState(false);
  const [expandedLecture, setExpandedLecture] = useState<string | null>(null);

  // AI reflection analysis state (Insights tab)
  const [aiAnalysis, setAiAnalysis] = useState<ReflectionAnalysisResults | null>(null);
  const [aiAnalysisStarting, setAiAnalysisStarting] = useState(false);
  const [aiExpandedLectures, setAiExpandedLectures] = useState<Set<string> | null>(null);
  const aiPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Collapsed-by-default disclosures for the demoted AI text sections + legacy charts
  const [aiSummariesOpen, setAiSummariesOpen] = useState(false);
  const [aiNarrativesOpen, setAiNarrativesOpen] = useState(false);
  const [legacyChartsOpen, setLegacyChartsOpen] = useState(false);

  // Concept heatmap (Insights > Learning trends)
  const [heatmapData, setHeatmapData] = useState<ReflectionHeatmap | null>(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapPolarity, setHeatmapPolarity] = useState<'challenging' | 'interesting'>('challenging');
  // Per-lecture top-topics selector (lecture_id); null falls back to first lecture
  const [trendsLectureId, setTrendsLectureId] = useState<string | null>(null);

  // Insights tab: sub-pill views
  const [insightsTab, setInsightsTab] = useState<InsightsTab>('structure');

  // Learning paths (Insights > Learning paths)
  const [pathStudent, setPathStudent] = useState<string | null>(null);
  const [pathData, setPathData] = useState<LearningPath | null>(null);
  const [pathLoading, setPathLoading] = useState(false);
  const pathReqRef = useRef<string | null>(null);
  // Student combobox for the Learning paths view
  const [pathSearch, setPathSearch] = useState('');
  const [pathDropOpen, setPathDropOpen] = useState(false);
  const [pathHighlight, setPathHighlight] = useState(0);
  const pathBoxRef = useRef<HTMLDivElement | null>(null);

  // Learning outcomes (Insights > Learning outcomes)
  const [outcomesData, setOutcomesData] = useState<ReflectionOutcomes | null>(null);
  const [outcomesStarting, setOutcomesStarting] = useState(false);
  const [outcomesExpanded, setOutcomesExpanded] = useState<Set<string>>(new Set());
  const outcomesPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Course structure (Insights > Course structure): outcomes -> modules -> lectures editor
  const [structureDraft, setStructureDraft] = useState<CourseStructure | null>(null);
  const [structureLoading, setStructureLoading] = useState(false);
  const [structureSaving, setStructureSaving] = useState(false);
  const [structureSaved, setStructureSaved] = useState(false);

  // Enrolled students state (Students tab)
  const [enrolledStudents, setEnrolledStudents] = useState<EnrolledStudentsListResponse | null>(null);
  const [studentsLoading, setStudentsLoading] = useState(false);

  // Roster import modal (Classlist tab)
  const [isRosterImportModalOpen, setIsRosterImportModalOpen] = useState(false);
  const [rosterFile, setRosterFile] = useState<File | null>(null);
  const [rosterImporting, setRosterImporting] = useState(false);
  const [rosterResult, setRosterResult] = useState<RosterImportResult | null>(null);
  const [copiedRosterRow, setCopiedRosterRow] = useState<number | null>(null);

  // Gradebook state (Grades tab)
  const [gradebook, setGradebook] = useState<Gradebook | null>(null);
  const [gradebookLoading, setGradebookLoading] = useState(false);

  // Per-student drill-in within the Grades tab (D2L-style)
  const [gradebookStudent, setGradebookStudent] = useState<string | null>(null);
  // Inline grade adjustment editor: quiz being edited, draft value, in-flight flag
  const [gradeEditQuizId, setGradeEditQuizId] = useState<string | null>(null);
  const [gradeEditDraft, setGradeEditDraft] = useState('');
  const [gradeEditSaving, setGradeEditSaving] = useState(false);

  // Student-visibility toggles currently in flight, keyed by `${itemType}:${itemId}`
  const [visibilityBusy, setVisibilityBusy] = useState<Set<string>>(new Set());

  // Delete confirmation
  const [lectureToDelete, setLectureToDelete] = useState<string | null>(null);
  const [slideToDelete, setSlideToDelete] = useState<string | null>(null);
  const [videoToDelete, setVideoToDelete] = useState<string | null>(null);
  const [quizToDelete, setQuizToDelete] = useState<string | null>(null);

  // Load course
  useEffect(() => {
    if (courseId) {
      loadCourse();
    }
  }, [courseId]);

  // Load slides and recommended videos eagerly: the Content tab sidebar counts,
  // the quiz generator, and the scaffold wizard all rely on them
  useEffect(() => {
    if (courseId) {
      loadSlides();
      loadRecommendedVideos();
      loadFoldersAndFiles();
    }
  }, [courseId]);

  // Load quizzes and scaffolds when the Assessments tab activates.
  // The Home tab's at-a-glance strip also needs the quiz list.
  useEffect(() => {
    if (activeTab === 'assessments' && courseId) {
      loadQuizzes();
      loadScaffolds();
      loadAssignments();
      loadFoldersAndFiles(); // question set batches resolve document names from courseFiles
    } else if (activeTab === 'home' && courseId) {
      loadQuizzes();
    }
  }, [activeTab, courseId]);

  // Load course reflections when the Insights tab activates.
  // Also fetch cached AI analysis results so they show without rerunning the job.
  useEffect(() => {
    if (activeTab === 'insights' && courseId) {
      loadCourseReflections();
      loadAiAnalysis();
    }
  }, [activeTab, courseId]);

  // Insights sub-views: fetch what each pill needs when it activates
  useEffect(() => {
    if (activeTab !== 'insights' || !courseId) return;
    if (insightsTab === 'trends') {
      loadReflectionHeatmap();
    }
    if (insightsTab === 'paths' && !enrolledStudents && !studentsLoading) {
      loadEnrolledStudents();
    }
    if (insightsTab === 'outcomes') {
      loadOutcomes();
    }
    if (insightsTab === 'structure') {
      loadStructure();
    }
  }, [activeTab, insightsTab, courseId]);

  // Close the Learning paths student combobox on outside click
  useEffect(() => {
    if (!pathDropOpen) return;
    const handler = (e: MouseEvent) => {
      if (pathBoxRef.current && !pathBoxRef.current.contains(e.target as Node)) {
        setPathDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pathDropOpen]);

  // Close the quiz workspace overflow menu on outside click
  useEffect(() => {
    if (!quizMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (quizMenuRef.current && !quizMenuRef.current.contains(e.target as Node)) {
        setQuizMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [quizMenuOpen]);

  // Stop reflection-analysis and outcomes polling when the page unmounts
  useEffect(() => {
    return () => {
      if (aiPollRef.current) {
        clearInterval(aiPollRef.current);
        aiPollRef.current = null;
      }
      if (outcomesPollRef.current) {
        clearInterval(outcomesPollRef.current);
        outcomesPollRef.current = null;
      }
    };
  }, []);

  // Load enrolled students when the Classlist tab activates
  useEffect(() => {
    if (activeTab === 'classlist' && courseId) {
      loadEnrolledStudents();
    }
  }, [activeTab, courseId]);

  // Load the gradebook when the Grades tab activates
  useEffect(() => {
    if (activeTab === 'grades' && courseId) {
      loadGradebook();
    }
  }, [activeTab, courseId]);

  // Auto-select a lecture for the Content sidebar: prefer the last-watched
  // lecture, fall back to the first lecture in course order. A manual pick
  // by the teacher is never overridden.
  useEffect(() => {
    if (!course || course.lectures.length === 0) return;
    const exists = (id?: string | null) => !!id && course.lectures.some((l) => l.lecture_id === id);
    if (exists(selectedLectureId) && selectedLectureId !== autoSelectedLectureRef.current) return;
    if (lastWatched && exists(lastWatched.lecture_id)) {
      if (selectedLectureId !== lastWatched.lecture_id) {
        autoSelectedLectureRef.current = lastWatched.lecture_id;
        setSelectedLectureId(lastWatched.lecture_id);
      }
      return;
    }
    if (!exists(selectedLectureId)) {
      const first = [...course.lectures].sort((a, b) => a.order - b.order)[0];
      autoSelectedLectureRef.current = first.lecture_id;
      setSelectedLectureId(first.lecture_id);
    }
  }, [course, lastWatched, selectedLectureId]);

  // Manual lecture pick from the table of contents
  const handleSelectLecture = (lectureId: string) => {
    setSelectedLectureId(lectureId);
    setContentView('lecture');
  };

  const loadCourse = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getCourse(courseId!);
      setCourse(data);
    } catch (err: any) {
      setError('Failed to load course');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Course home editing (welcome message, meetings, office hours, contact)
  const openEditHomeModal = () => {
    setHomeForm({
      welcomeMessage: course?.welcome_message || '',
      classSchedule: course?.class_schedule || '',
      classMeetingLink: course?.class_meeting_link || '',
      officeHours: course?.office_hours || '',
      officeHoursLink: course?.office_hours_link || '',
      contactEmail: course?.contact_email || '',
      classLocation: course?.class_location || '',
      meetingDays: course?.meeting_days || [],
      meetingStart: course?.meeting_start || '',
      meetingEnd: course?.meeting_end || '',
      courseStart: course?.course_start || '',
      courseEnd: course?.course_end || '',
    });
    setHomeErrors({});
    setIsEditHomeModalOpen(true);
  };

  const handleSaveHome = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!course) return;

    // Only send fields that actually changed
    type HomeField = 'welcome_message' | 'class_schedule' | 'class_meeting_link' | 'office_hours' | 'office_hours_link' | 'contact_email' | 'class_location';
    const changes: CourseUpdateRequest = {};
    const diff = (key: HomeField, next: string, prev?: string) => {
      if (next.trim() !== (prev || '').trim()) changes[key] = next.trim();
    };
    diff('welcome_message', homeForm.welcomeMessage, course.welcome_message);
    diff('class_schedule', homeForm.classSchedule, course.class_schedule);
    diff('class_meeting_link', homeForm.classMeetingLink, course.class_meeting_link);
    diff('office_hours', homeForm.officeHours, course.office_hours);
    diff('office_hours_link', homeForm.officeHoursLink, course.office_hours_link);
    diff('contact_email', homeForm.contactEmail, course.contact_email);
    diff('class_location', homeForm.classLocation, course.class_location);
    if (homeForm.meetingStart.trim() !== (course.meeting_start || '')) changes.meeting_start = homeForm.meetingStart.trim();
    if (homeForm.meetingEnd.trim() !== (course.meeting_end || '')) changes.meeting_end = homeForm.meetingEnd.trim();
    const prevDays = (course.meeting_days || []).join(',');
    if (homeForm.meetingDays.join(',') !== prevDays) changes.meeting_days = homeForm.meetingDays;
    if (homeForm.courseStart !== (course.course_start || '')) changes.course_start = homeForm.courseStart;
    if (homeForm.courseEnd !== (course.course_end || '')) changes.course_end = homeForm.courseEnd;

    if (Object.keys(changes).length === 0) {
      setIsEditHomeModalOpen(false);
      return;
    }

    setHomeSaving(true);
    setHomeErrors({});
    try {
      await updateCourse(course.course_id, changes);
      await loadCourse();
      setIsEditHomeModalOpen(false);
    } catch (err: any) {
      setHomeErrors({ general: err.response?.data?.detail || 'Failed to save course home details' });
    } finally {
      setHomeSaving(false);
    }
  };

  const loadSlides = async () => {
    try {
      setSlidesLoading(true);
      const data = await getCourseSlides(courseId!);
      setSlides(data);
    } catch (err: any) {
      // API not implemented yet — silently fail
      console.error('Failed to load slides:', err);
      setSlides([]);
    } finally {
      setSlidesLoading(false);
    }
  };

  // Video upload handlers
  const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('video/')) {
        setLectureErrors({ video: 'Please select a valid video file' });
        return;
      }

      setLectureForm({ ...lectureForm, videoFile: file });
      setLectureErrors({ ...lectureErrors, video: undefined });
    }
  };

  const handleUploadLecture = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors: typeof lectureErrors = {};
    if (!lectureForm.title.trim()) {
      errors.title = 'Lecture title is required';
    }
    if (!lectureForm.videoFile) {
      errors.video = 'Please select a video file';
    }

    if (Object.keys(errors).length > 0) {
      setLectureErrors(errors);
      return;
    }

    // Close modal immediately — upload continues in background via global tracker
    setIsUploadModalOpen(false);
    setLectureErrors({});

    startLectureUpload(
      courseId!,
      lectureForm.title,
      lectureForm.description,
      lectureForm.videoFile!,
      () => loadCourse(),
    );

    setLectureForm({ title: '', description: '', videoFile: null });
  };

  // Slide upload handlers
  const handleSlideFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = [
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/pdf',
        'application/vnd.oasis.opendocument.presentation',
      ];
      const allowedExtensions = ['.ppt', '.pptx', '.pdf', '.odp', '.key'];
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();

      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext)) {
        setSlideErrors({ file: 'Please select a valid presentation file (PPT, PPTX, PDF, ODP)' });
        return;
      }

      setSlideForm({ ...slideForm, file });
      setSlideErrors({ ...slideErrors, file: undefined });
    }
  };

  const handleUploadSlide = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors: typeof slideErrors = {};
    if (!slideForm.title.trim()) {
      errors.title = 'Slide title is required';
    }
    if (!slideForm.file) {
      errors.file = 'Please select a presentation file';
    }

    if (Object.keys(errors).length > 0) {
      setSlideErrors(errors);
      return;
    }

    // Close modal immediately — upload continues in background via global tracker
    setIsSlideUploadModalOpen(false);
    setSlideErrors({});

    startSlideUpload(
      courseId!,
      slideForm.title,
      slideForm.file!,
      () => loadSlides(),
    );

    setSlideForm({ title: '', file: null });
  };

  // Delete lecture
  const handleDeleteLecture = async (lectureId: string) => {
    try {
      await deleteLecture(lectureId);
      setLectureToDelete(null);
      await loadCourse();
    } catch (err: any) {
      alert('Failed to delete lecture');
      console.error(err);
    }
  };

  // Delete slide
  const handleDeleteSlide = async (slideId: string) => {
    try {
      await deleteSlide(slideId);
      setSlideToDelete(null);
      await loadSlides();
    } catch (err: any) {
      alert('Failed to delete presentation');
      console.error(err);
    }
  };

  // ── Content folders + course files handlers ──
  const loadFoldersAndFiles = async () => {
    try {
      const [folders, files] = await Promise.all([
        listContentFolders(courseId!),
        listCourseFiles(courseId!),
      ]);
      setContentFolders(folders);
      setCourseFiles(files);
    } catch (err: any) {
      console.error('Failed to load course materials:', err);
    }
  };

  // Refresh the quiz list when a background generation job finishes
  useEffect(() => {
    if (completionVersion > 0) {
      loadQuizzes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completionVersion]);

  // Reattach tracker to jobs already running for this course (e.g. after refresh)
  useEffect(() => {
    if (!courseId) return;
    listQuizGenerationJobs(courseId).then(attachJobs).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  // Leaving the current folder view closes any inline embed
  useEffect(() => {
    setSelectedEmbedLink(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentView, selectedFolderId]);

  const handleAddLink = async (e: React.FormEvent) => {
    e.preventDefault();
    const url = linkForm.url.trim();
    if (!url.toLowerCase().startsWith('https://')) {
      setLinkError('Enter a full link starting with https://');
      return;
    }
    setLinkSaving(true);
    setLinkError('');
    try {
      await addCourseLink(courseId!, {
        title: linkForm.title.trim(),
        url,
        folderId: contentView === 'folder' ? selectedFolderId : null,
      });
      setIsAddLinkModalOpen(false);
      setLinkForm({ url: '', title: '' });
      await loadFoldersAndFiles();
    } catch (err: any) {
      setLinkError(err.response?.data?.detail || 'Could not add the link');
    } finally {
      setLinkSaving(false);
    }
  };

  // Make sure documents are listed in the generate-quiz modal even when the
  // teacher opens it from the Assessments tab without visiting Content first
  useEffect(() => {
    if ((isGenerateQuizModalOpen || isGenerateScaffoldModalOpen) && courseId && courseFiles.length === 0) {
      loadFoldersAndFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenerateQuizModalOpen, isGenerateScaffoldModalOpen]);

  // Refresh file list while any document is still extracting text, so the
  // "Processing" chip flips to "Ready for quizzes" without a manual reload
  useEffect(() => {
    const anyPending = courseFiles.some(f => f.text_status === 'pending' || !f.text_status);
    if (!anyPending || !courseId) return;
    const interval = setInterval(async () => {
      try {
        const files = await listCourseFiles(courseId);
        setCourseFiles(files);
      } catch {
        // transient failure: keep polling
      }
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseFiles, courseId]);

  const openCreateFolderModal = () => {
    setFolderModalMode('create');
    setFolderNameInput('');
    setRenamingFolderId(null);
    setFolderModalError('');
    setIsFolderModalOpen(true);
  };

  const openRenameFolderModal = (folder: ContentFolder) => {
    setFolderModalMode('rename');
    setFolderNameInput(folder.name);
    setRenamingFolderId(folder.folder_id);
    setFolderModalError('');
    setIsFolderModalOpen(true);
  };

  const handleFolderModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = folderNameInput.trim();
    if (!name) {
      setFolderModalError('Folder name is required');
      return;
    }
    setFolderSaving(true);
    setFolderModalError('');
    try {
      if (folderModalMode === 'create') {
        const created = await createContentFolder(courseId!, name);
        await loadFoldersAndFiles();
        setSelectedFolderId(created.folder_id);
        setContentView('folder');
      } else if (renamingFolderId) {
        await renameContentFolder(courseId!, renamingFolderId, name);
        await loadFoldersAndFiles();
      }
      setIsFolderModalOpen(false);
    } catch (err: any) {
      setFolderModalError(err.response?.data?.detail || 'Failed to save folder');
    } finally {
      setFolderSaving(false);
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;
    try {
      await deleteContentFolder(courseId!, folderToDelete.folder_id);
      if (contentView === 'folder' && selectedFolderId === folderToDelete.folder_id) {
        setContentView('lecture');
        setSelectedFolderId(null);
      }
      setFolderToDelete(null);
      await loadFoldersAndFiles();
    } catch (err: any) {
      alert('Failed to delete folder');
      console.error(err);
    }
  };

  const handleCourseFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file || !courseId) return;
    const folderId = contentView === 'folder' && selectedFolderId ? selectedFolderId : undefined;
    const title = fileTitleInput.trim() || undefined;
    setUploadingFileName(file.name);
    setFileUploadProgress(0);
    try {
      await uploadCourseFile(courseId, file, {
        title,
        folderId,
        onUploadProgress: (ev: any) => {
          if (ev.total) setFileUploadProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      setFileTitleInput('');
      await loadFoldersAndFiles();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to upload file');
      console.error(err);
    } finally {
      setUploadingFileName(null);
      setFileUploadProgress(0);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await deleteCourseFile(courseId!, fileId);
      setFileToDelete(null);
      await loadFoldersAndFiles();
    } catch (err: any) {
      alert('Failed to delete file');
      console.error(err);
    }
  };

  // ── Student-visibility (hide/show) handlers ──

  /** Flip the local `hidden` flag on one content item in place. */
  const applyVisibilityLocally = (itemType: VisibilityItemType, itemId: string, hidden: boolean) => {
    if (itemType === 'lecture') {
      setCourse((prev) =>
        prev
          ? { ...prev, lectures: prev.lectures.map((l) => (l.lecture_id === itemId ? { ...l, hidden } : l)) }
          : prev
      );
    } else if (itemType === 'slide') {
      setSlides((prev) => prev.map((s) => (s.slide_id === itemId ? { ...s, hidden } : s)));
    } else if (itemType === 'recommended_video') {
      setRecommendedVideos((prev) => prev.map((v) => (v.video_id === itemId ? { ...v, hidden } : v)));
    } else if (itemType === 'folder') {
      setContentFolders((prev) => prev.map((f) => (f.folder_id === itemId ? { ...f, hidden } : f)));
    } else {
      setCourseFiles((prev) => prev.map((f) => (f.file_id === itemId ? { ...f, hidden } : f)));
    }
  };

  /** Optimistically toggle student visibility; revert on failure. */
  const handleToggleVisibility = async (itemType: VisibilityItemType, itemId: string, currentHidden: boolean) => {
    const key = `${itemType}:${itemId}`;
    if (visibilityBusy.has(key)) return;
    setVisibilityBusy((prev) => new Set(prev).add(key));
    applyVisibilityLocally(itemType, itemId, !currentHidden);
    try {
      await setContentVisibility(courseId!, itemType, itemId, !currentHidden);
    } catch (err: any) {
      applyVisibilityLocally(itemType, itemId, currentHidden);
      alert(err.response?.data?.detail || 'Failed to update student visibility');
      console.error(err);
    } finally {
      setVisibilityBusy((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  /** Small eye / eye-off icon button that toggles an item's student visibility. */
  const renderVisibilityToggle = (
    itemType: VisibilityItemType,
    itemId: string,
    hidden: boolean,
    className: string,
    hideTitle = 'Hide from students'
  ) => {
    const busy = visibilityBusy.has(`${itemType}:${itemId}`);
    return (
      <button
        onClick={() => handleToggleVisibility(itemType, itemId, hidden)}
        disabled={busy}
        title={hidden ? 'Show to students' : hideTitle}
        aria-label={hidden ? 'Show to students' : hideTitle}
        className={`${className} disabled:cursor-wait disabled:opacity-50`}
      >
        {hidden ? eyeOffGlyph('h-4 w-4') : eyeGlyph('h-4 w-4')}
      </button>
    );
  };

  // Recommended Videos handlers
  const loadRecommendedVideos = async () => {
    try {
      setRecommendedLoading(true);
      const data = await getCourseRecommendedVideos(courseId!);
      setRecommendedVideos(data);
    } catch (err: any) {
      console.error('Failed to load recommended videos:', err);
      setRecommendedVideos([]);
    } finally {
      setRecommendedLoading(false);
    }
  };

  const handleAddVideo = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: typeof videoErrors = {};
    if (!videoForm.title.trim()) errors.title = 'Title is required';
    if (!videoForm.youtubeUrl.trim()) errors.url = 'YouTube URL is required';
    if (Object.keys(errors).length > 0) { setVideoErrors(errors); return; }

    setAddVideoLoading(true);
    setVideoErrors({});
    try {
      await addRecommendedVideo(courseId!, videoForm.title, videoForm.youtubeUrl);
      setVideoForm({ title: '', youtubeUrl: '' });
      setIsAddVideoModalOpen(false);
      await loadRecommendedVideos();
    } catch (err: any) {
      setVideoErrors({ general: err.response?.data?.detail || 'Failed to add video' });
    } finally {
      setAddVideoLoading(false);
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    try {
      await deleteRecommendedVideo(videoId);
      setVideoToDelete(null);
      await loadRecommendedVideos();
    } catch (err: any) {
      alert('Failed to delete video');
    }
  };

  const handleProcessVideo = async (videoId: string) => {
    try {
      await processRecommendedVideo(videoId);
      // Update the local state to show processing status
      setRecommendedVideos(prev =>
        prev.map(v => v.video_id === videoId ? { ...v, processing_status: 'processing' as const } : v)
      );
      // Poll for completion every 5 seconds
      const poll = setInterval(async () => {
        const videos = await getCourseRecommendedVideos(courseId!);
        setRecommendedVideos(videos);
        const video = videos.find(v => v.video_id === videoId);
        if (video && video.processing_status !== 'processing') {
          clearInterval(poll);
        }
      }, 5000);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to process video');
    }
  };

  // Quiz handlers
  const loadQuizzes = async () => {
    try {
      setQuizzesLoading(true);
      const data = await getCourseQuizzes(courseId!);
      setQuizzes(data);
    } catch (err: any) {
      console.error('Failed to load quizzes:', err);
      setQuizzes([]);
    } finally {
      setQuizzesLoading(false);
    }
  };

  const handleGenerateQuiz = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quizForm.selectedLectures.length && !quizForm.selectedSlides.length && !quizForm.selectedRecommendedVideos.length && !quizForm.selectedFiles.length) {
      setQuizErrors({ general: 'Select at least one lecture, slide, video, or document' });
      return;
    }
    if (!quizForm.title.trim()) {
      setQuizErrors({ general: 'Quiz title is required' });
      return;
    }

    const title = quizForm.title;
    const lectures = [...quizForm.selectedLectures];
    const slides = [...quizForm.selectedSlides];
    const recVideos = [...quizForm.selectedRecommendedVideos];
    const files = [...quizForm.selectedFiles];
    const numQ = quizForm.numQuestions;
    const openEnded = Math.min(quizForm.openEndedCount, numQ);
    const blooms = [...quizForm.bloomLevels];
    const difficulty = quizForm.difficulty || undefined;
    const maxAttempts = quizForm.maxAttempts;

    // Close modal immediately: generation runs as a server-side job and
    // progress shows in the floating tracker, so the teacher can keep working
    setIsGenerateQuizModalOpen(false);
    setQuizForm({ title: '', selectedLectures: [], selectedSlides: [], selectedRecommendedVideos: [], selectedFiles: [], numQuestions: 10, openEndedCount: 0, bloomLevels: [], difficulty: '', maxAttempts: 1 });
    setQuizErrors({});

    try {
      await startQuizJob(courseId!, {
        title,
        lectureIds: lectures,
        slideIds: slides,
        recommendedVideoIds: recVideos,
        fileIds: files,
        numQuestions: numQ,
        openEndedCount: openEnded,
        bloomLevels: blooms,
        difficulty,
        maxAttempts,
      });
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Could not start quiz generation');
    }
  };

  const handleDeleteQuiz = async (quizId: string) => {
    try {
      await deleteQuiz(quizId);
      setQuizToDelete(null);
      setViewingQuiz(null);
      await loadQuizzes();
    } catch (err: any) {
      alert('Failed to delete quiz');
    }
  };

  const handleViewQuiz = async (quizId: string) => {
    try {
      setQuizDetailLoading(true);
      const quiz = await getQuiz(quizId);
      setViewingQuiz(quiz);
      setEditingQuestions([...quiz.questions]);
      setQuestionPointsDrafts({});
      resetQuizReviewState();
      // Smart default: land on Submissions when this quiz has attempts waiting on review
      const listItem = quizzes.find((q) => q.quiz_id === quizId);
      if ((listItem?.pending_review ?? 0) > 0) setQuizSubView('submissions');
      loadQuizAttempts(quizId);
    } catch (err: any) {
      alert('Failed to load quiz');
    } finally {
      setQuizDetailLoading(false);
    }
  };

  // ── Quiz submissions review (feedback + grade release) ──

  const loadQuizAttempts = async (quizId: string) => {
    try {
      setQuizAttemptsLoading(true);
      const data = await getQuizAttempts(quizId);
      setQuizAttempts(data);
    } catch (err: any) {
      console.error('Failed to load quiz attempts:', err);
      setQuizAttempts([]);
    } finally {
      setQuizAttemptsLoading(false);
    }
  };

  /** Clear the submissions sub-view when opening or leaving a quiz. */
  const resetQuizReviewState = () => {
    setQuizSubView('questions');
    setQuizAttempts([]);
    setViewingAttempt(null);
    setAttemptFeedbackDraft('');
    setScoreEditQid(null);
    setQuizMenuOpen(false);
  };

  const handleOpenAttempt = async (attemptId: string) => {
    try {
      setAttemptDetailLoading(true);
      const detail = await getQuizAttemptDetail(attemptId);
      setViewingAttempt(detail);
      setAttemptFeedbackDraft(detail.feedback || '');
      setScoreEditQid(null);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to load attempt');
    } finally {
      setAttemptDetailLoading(false);
    }
  };

  /** Patch one attempt in the local list (and open detail view) after a review call. */
  const applyAttemptReview = (attemptId: string, patch: Partial<QuizAttemptSummary>) => {
    setQuizAttempts((prev) => prev.map((a) => (a.attempt_id === attemptId ? { ...a, ...patch } : a)));
    setViewingAttempt((prev) => (prev && prev.attempt_id === attemptId ? { ...prev, ...patch } : prev));
  };

  const handleSaveAttemptFeedback = async () => {
    if (!viewingAttempt || !viewingQuiz) return;
    setAttemptFeedbackSaving(true);
    try {
      await reviewStudentGrade(courseId!, viewingAttempt.student_username, viewingQuiz.quiz_id, {
        feedback: attemptFeedbackDraft,
      });
      applyAttemptReview(viewingAttempt.attempt_id, { feedback: attemptFeedbackDraft });
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to save feedback');
    } finally {
      setAttemptFeedbackSaving(false);
    }
  };

  const handleToggleAttemptRelease = async () => {
    if (!viewingAttempt || !viewingQuiz) return;
    const next = !viewingAttempt.released;
    setAttemptReleaseSaving(true);
    try {
      await reviewStudentGrade(courseId!, viewingAttempt.student_username, viewingQuiz.quiz_id, { released: next });
      applyAttemptReview(viewingAttempt.attempt_id, { released: next });
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update release state');
    } finally {
      setAttemptReleaseSaving(false);
    }
  };

  const handleReleaseAllQuizGrades = async () => {
    if (!viewingQuiz) return;
    if (!window.confirm('Release grades to every student who attempted this quiz? Students will be notified.')) return;
    setReleasingQuizGradesAll(true);
    try {
      const { released_count } = await releaseQuizGrades(viewingQuiz.quiz_id);
      await loadQuizAttempts(viewingQuiz.quiz_id);
      alert(`Released grades for ${released_count} student${released_count !== 1 ? 's' : ''}.`);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to release grades');
    } finally {
      setReleasingQuizGradesAll(false);
    }
  };

  /** Save one question's corrected score on the open attempt; server clamps and recomputes the percentage. */
  const handleSaveQuestionScore = async () => {
    if (!viewingAttempt || !scoreEditQid) return;
    const result = viewingAttempt.results.find((r) => r.question_id === scoreEditQid);
    const max = result?.points ?? 1;
    const parsed = parseFloat(scoreDraft);
    if (isNaN(parsed) || parsed < 0 || parsed > max) {
      alert(`Enter a score between 0 and ${formatPoints(max)}`);
      return;
    }
    setScoreSaving(true);
    try {
      const detail = await setAttemptScores(viewingAttempt.attempt_id, { [scoreEditQid]: parsed });
      setViewingAttempt(detail);
      setScoreEditQid(null);
      // Refresh the attempts list so the row percentage matches the new score
      if (viewingQuiz) loadQuizAttempts(viewingQuiz.quiz_id);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to save score');
    } finally {
      setScoreSaving(false);
    }
  };

  const handleRemoveQuestion = (questionId: string) => {
    setEditingQuestions(prev => prev.filter(q => q.question_id !== questionId));
  };

  /** Update a question's point value in the editor draft (marks unsaved changes, same as removal). */
  const handleQuestionPointsChange = (questionId: string, raw: string) => {
    setQuestionPointsDrafts((prev) => ({ ...prev, [questionId]: raw }));
    const parsed = parseFloat(raw);
    if (!isNaN(parsed) && parsed >= 0) {
      setEditingQuestions((prev) => prev.map((q) => (q.question_id === questionId ? { ...q, points: parsed } : q)));
    }
  };

  // Unsaved-changes detection for the quiz editor: questions removed OR point values changed.
  const quizQuestionsRemovedCount = viewingQuiz ? viewingQuiz.questions.length - editingQuestions.length : 0;
  const quizPointsChanged = viewingQuiz
    ? editingQuestions.some((q) => {
        const orig = viewingQuiz.questions.find((o) => o.question_id === q.question_id);
        return orig != null && (q.points ?? 1) !== (orig.points ?? 1);
      })
    : false;
  const quizHasUnsavedChanges = viewingQuiz != null && (quizQuestionsRemovedCount !== 0 || quizPointsChanged);

  // Any loaded attempt not yet released: drives the workspace primary action + Submissions pill badge
  const quizHasUnreleasedAttempts = quizAttempts.some((a) => !a.released);

  // Quiz list: client-side filter pills + needs-review count for the filter badge
  const quizzesNeedingReview = quizzes.filter((q) => (q.pending_review ?? 0) > 0).length;
  const filteredQuizzes = quizzes.filter((q) => {
    if (quizListFilter === 'needs_review') return (q.pending_review ?? 0) > 0;
    if (quizListFilter === 'drafts') return !q.published;
    if (quizListFilter === 'open') return getQuizStatus(q).label === 'Open';
    return true;
  });

  // Assignment list: client-side filter pills + needs-grading count for the filter badge
  // (assignments share the same lifecycle shape as quizzes, so getQuizStatus applies)
  const assignmentsNeedingGrading = assignments.filter((a) => (a.pending_review ?? 0) > 0).length;
  const filteredAssignments = assignments.filter((a) => {
    if (assignmentListFilter === 'needs_grading') return (a.pending_review ?? 0) > 0;
    if (assignmentListFilter === 'drafts') return !a.published;
    if (assignmentListFilter === 'open') return getQuizStatus(a).label === 'Open';
    return true;
  });

  // Quiz generator list: published/draft filter applied within batches
  const scaffoldMatchesListFilter = (s: ScaffoldListItem) =>
    scaffoldListFilter === 'published' ? s.published : scaffoldListFilter === 'drafts' ? !s.published : true;

  const handleSaveQuiz = async () => {
    if (!viewingQuiz) return;
    if (editingQuestions.length === 0) {
      alert('Quiz must have at least one question');
      return;
    }
    setQuizSaving(true);
    try {
      const updated = await updateQuiz(viewingQuiz.quiz_id, {
        questions: editingQuestions,
      });
      setViewingQuiz(updated);
      setEditingQuestions([...updated.questions]);
      setQuestionPointsDrafts({});
      await loadQuizzes();
    } catch (err: any) {
      alert('Failed to save quiz');
    } finally {
      setQuizSaving(false);
    }
  };

  // ── Quiz scheduling (publish modal + edit schedule) ──

  /** ISO string -> datetime-local input value (YYYY-MM-DDTHH:mm) in LOCAL time. */
  const isoToLocalInput = (iso?: string | null): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  /** Local datetime for quiet schedule meta, e.g. "Jul 12, 2026, 3:00 PM". */
  const formatScheduleDateTime = (iso: string): string =>
    new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    });

  const openScheduleModal = (mode: 'publish' | 'edit') => {
    if (!viewingQuiz) return;
    if (mode === 'publish' && editingQuestions.length === 0) {
      alert('Quiz must have at least one question to publish');
      return;
    }
    setScheduleModalMode(mode);
    setScheduleForm({
      availableFrom: isoToLocalInput(viewingQuiz.available_from),
      dueAt: isoToLocalInput(viewingQuiz.due_at),
      maxAttempts: viewingQuiz.max_attempts != null ? String(viewingQuiz.max_attempts) : '1',
    });
    setScheduleError('');
    setIsScheduleModalOpen(true);
  };

  const handleScheduleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewingQuiz) return;
    const { availableFrom, dueAt } = scheduleForm;

    if (availableFrom && dueAt && new Date(dueAt) <= new Date(availableFrom)) {
      setScheduleError('The due date must be after the available from date.');
      return;
    }

    const attemptsValue = scheduleForm.maxAttempts.trim() === '' ? 0 : Number(scheduleForm.maxAttempts);
    if (isNaN(attemptsValue) || attemptsValue < 0 || attemptsValue > 10) {
      setScheduleError('Attempts must be a number from 0 (unlimited) to 10.');
      return;
    }
    const payload: QuizUpdateRequest = {};
    if (scheduleModalMode === 'publish') {
      // Save questions first, then publish (same path as before, now with optional dates)
      payload.questions = editingQuestions;
      payload.published = true;
      if (availableFrom) payload.available_from = new Date(availableFrom).toISOString();
      if (dueAt) payload.due_at = new Date(dueAt).toISOString();
      payload.max_attempts = attemptsValue;
    } else {
      // Edit schedule: send only changed fields; emptying a set field sends its clear flag
      const prevFrom = isoToLocalInput(viewingQuiz.available_from);
      const prevDue = isoToLocalInput(viewingQuiz.due_at);
      if (availableFrom !== prevFrom) {
        if (availableFrom) payload.available_from = new Date(availableFrom).toISOString();
        else payload.clear_available_from = true;
      }
      if (dueAt !== prevDue) {
        if (dueAt) payload.due_at = new Date(dueAt).toISOString();
        else payload.clear_due_at = true;
      }
      if (attemptsValue !== (viewingQuiz.max_attempts ?? 0)) {
        payload.max_attempts = attemptsValue;
      }
      if (Object.keys(payload).length === 0) {
        setIsScheduleModalOpen(false);
        return;
      }
    }

    setScheduleSaving(true);
    if (scheduleModalMode === 'publish') setQuizPublishing(true);
    try {
      const updated = await updateQuiz(viewingQuiz.quiz_id, payload);
      setViewingQuiz(updated);
      if (scheduleModalMode === 'publish') { setEditingQuestions([...updated.questions]); setQuestionPointsDrafts({}); }
      setIsScheduleModalOpen(false);
      await loadQuizzes();
    } catch (err: any) {
      setScheduleError(
        err.response?.data?.detail ||
        (scheduleModalMode === 'publish' ? 'Failed to publish quiz' : 'Failed to update schedule')
      );
    } finally {
      setScheduleSaving(false);
      if (scheduleModalMode === 'publish') setQuizPublishing(false);
    }
  };

  const handleUnpublishQuiz = async () => {
    if (!viewingQuiz) return;
    setQuizPublishing(true);
    try {
      const updated = await updateQuiz(viewingQuiz.quiz_id, {
        published: false,
      });
      setViewingQuiz(updated);
      await loadQuizzes();
    } catch (err: any) {
      alert('Failed to unpublish quiz');
    } finally {
      setQuizPublishing(false);
    }
  };

  const toggleLectureSelection = (lectureId: string) => {
    setQuizForm(prev => ({
      ...prev,
      selectedLectures: prev.selectedLectures.includes(lectureId)
        ? prev.selectedLectures.filter(id => id !== lectureId)
        : [...prev.selectedLectures, lectureId]
    }));
  };

  const toggleSlideSelection = (slideId: string) => {
    setQuizForm(prev => ({
      ...prev,
      selectedSlides: prev.selectedSlides.includes(slideId)
        ? prev.selectedSlides.filter(id => id !== slideId)
        : [...prev.selectedSlides, slideId]
    }));
  };

  const toggleFileSelection = (fileId: string) => {
    setQuizForm(prev => ({
      ...prev,
      selectedFiles: prev.selectedFiles.includes(fileId)
        ? prev.selectedFiles.filter(id => id !== fileId)
        : [...prev.selectedFiles, fileId]
    }));
  };

  const toggleBloomLevel = (level: number) => {
    setQuizForm(prev => ({
      ...prev,
      bloomLevels: prev.bloomLevels.includes(level)
        ? prev.bloomLevels.filter(l => l !== level)
        : [...prev.bloomLevels, level]
    }));
  };

  const toggleRecommendedVideoSelection = (videoId: string) => {
    setQuizForm(prev => ({
      ...prev,
      selectedRecommendedVideos: prev.selectedRecommendedVideos.includes(videoId)
        ? prev.selectedRecommendedVideos.filter(id => id !== videoId)
        : [...prev.selectedRecommendedVideos, videoId]
    }));
  };


  // ── Assignment handlers (instructor) ──

  const loadAssignments = async () => {
    try {
      setAssignmentsLoading(true);
      const data = await listAssignments(courseId!);
      setAssignments(data);
    } catch (err: any) {
      console.error('Failed to load assignments:', err);
      setAssignments([]);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const loadAssignmentSubmissions = async (assignmentId: string) => {
    try {
      setAssignmentSubsLoading(true);
      const data = await getAssignmentSubmissions(assignmentId);
      setAssignmentSubmissions(data);
      // Seed the inline grading drafts from the saved grades/feedback
      const drafts: Record<string, { grade: string; feedback: string }> = {};
      data.forEach((s) => {
        drafts[s.submission_id] = {
          grade: s.grade_percentage != null ? String(s.grade_percentage) : '',
          feedback: s.feedback || '',
        };
      });
      setSubGradeDrafts(drafts);
    } catch (err: any) {
      console.error('Failed to load submissions:', err);
      setAssignmentSubmissions([]);
    } finally {
      setAssignmentSubsLoading(false);
    }
  };

  const handleOpenAssignment = (assignment: AssignmentResponse) => {
    setViewingAssignment(assignment);
    loadAssignmentSubmissions(assignment.assignment_id);
  };

  const openCreateAssignmentModal = () => {
    setAssignmentModalMode('create');
    setAssignmentForm({ title: '', instructions: '', availableFrom: '', dueAt: '' });
    setAssignmentModalError('');
    setIsAssignmentModalOpen(true);
  };

  const openEditAssignmentModal = () => {
    if (!viewingAssignment) return;
    setAssignmentModalMode('edit');
    setAssignmentForm({
      title: viewingAssignment.title,
      instructions: viewingAssignment.instructions || '',
      availableFrom: isoToLocalInput(viewingAssignment.available_from),
      dueAt: isoToLocalInput(viewingAssignment.due_at),
    });
    setAssignmentModalError('');
    setIsAssignmentModalOpen(true);
  };

  const handleAssignmentModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const title = assignmentForm.title.trim();
    if (!title) {
      setAssignmentModalError('Assignment title is required');
      return;
    }
    const { availableFrom, dueAt } = assignmentForm;
    if (availableFrom && dueAt && new Date(dueAt) <= new Date(availableFrom)) {
      setAssignmentModalError('The due date must be after the available from date.');
      return;
    }
    setAssignmentSaving(true);
    setAssignmentModalError('');
    try {
      if (assignmentModalMode === 'create') {
        const created = await createAssignment(courseId!, {
          title,
          instructions: assignmentForm.instructions.trim() || undefined,
          available_from: availableFrom ? new Date(availableFrom).toISOString() : undefined,
          due_at: dueAt ? new Date(dueAt).toISOString() : undefined,
        });
        await loadAssignments();
        handleOpenAssignment(created);
      } else if (viewingAssignment) {
        // Edit: send only changed fields; emptying a set date sends its clear flag
        const payload: Parameters<typeof updateAssignment>[1] = {};
        if (title !== viewingAssignment.title) payload.title = title;
        if (assignmentForm.instructions.trim() !== (viewingAssignment.instructions || '').trim()) {
          payload.instructions = assignmentForm.instructions.trim();
        }
        const prevFrom = isoToLocalInput(viewingAssignment.available_from);
        const prevDue = isoToLocalInput(viewingAssignment.due_at);
        if (availableFrom !== prevFrom) {
          if (availableFrom) payload.available_from = new Date(availableFrom).toISOString();
          else payload.clear_available_from = true;
        }
        if (dueAt !== prevDue) {
          if (dueAt) payload.due_at = new Date(dueAt).toISOString();
          else payload.clear_due_at = true;
        }
        if (Object.keys(payload).length > 0) {
          const updated = await updateAssignment(viewingAssignment.assignment_id, payload);
          setViewingAssignment(updated);
          await loadAssignments();
        }
      }
      setIsAssignmentModalOpen(false);
    } catch (err: any) {
      setAssignmentModalError(err.response?.data?.detail || 'Failed to save assignment');
    } finally {
      setAssignmentSaving(false);
    }
  };

  const handleToggleAssignmentPublish = async () => {
    if (!viewingAssignment) return;
    setAssignmentPublishing(true);
    try {
      const updated = await updateAssignment(viewingAssignment.assignment_id, {
        published: !viewingAssignment.published,
      });
      setViewingAssignment(updated);
      await loadAssignments();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update assignment');
    } finally {
      setAssignmentPublishing(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId: string) => {
    try {
      await deleteAssignment(assignmentId);
      setAssignmentToDelete(null);
      setViewingAssignment(null);
      await loadAssignments();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete assignment');
    }
  };

  const handleAssignmentAttachmentSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file || !viewingAssignment) return;
    setAssignmentUploadingName(file.name);
    setAssignmentUploadProgress(0);
    try {
      const updated = await addAssignmentAttachment(viewingAssignment.assignment_id, file, (ev: any) => {
        if (ev.total) setAssignmentUploadProgress(Math.round((ev.loaded / ev.total) * 100));
      });
      setViewingAssignment(updated);
      await loadAssignments();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to upload attachment');
    } finally {
      setAssignmentUploadingName(null);
      setAssignmentUploadProgress(0);
    }
  };

  const handleSaveSubmissionGrade = async (submission: AssignmentSubmission) => {
    const draft = subGradeDrafts[submission.submission_id];
    if (!draft) return;
    const trimmed = draft.grade.trim();
    let grade: number | null = null;
    if (trimmed !== '') {
      const parsed = parseFloat(trimmed);
      if (isNaN(parsed) || parsed < 0 || parsed > 100) {
        alert('Grade must be a number between 0 and 100');
        return;
      }
      grade = parsed;
    }
    setSubSavingId(submission.submission_id);
    try {
      const updated = await gradeSubmission(submission.submission_id, {
        grade_percentage: grade,
        feedback: draft.feedback,
      });
      setAssignmentSubmissions((prev) => prev.map((s) => (s.submission_id === updated.submission_id ? updated : s)));
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to save grade');
    } finally {
      setSubSavingId(null);
    }
  };

  const handleToggleSubmissionRelease = async (submission: AssignmentSubmission) => {
    setSubReleasingId(submission.submission_id);
    try {
      const updated = await gradeSubmission(submission.submission_id, { released: !submission.released });
      setAssignmentSubmissions((prev) => prev.map((s) => (s.submission_id === updated.submission_id ? updated : s)));
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update release state');
    } finally {
      setSubReleasingId(null);
    }
  };

  const handleReleaseAllAssignmentGrades = async () => {
    if (!viewingAssignment) return;
    if (!window.confirm('Release every graded submission to its student? Students will be notified.')) return;
    setReleasingAssignmentGradesAll(true);
    try {
      const { released_count } = await releaseAssignmentGrades(viewingAssignment.assignment_id);
      await loadAssignmentSubmissions(viewingAssignment.assignment_id);
      alert(`Released grades for ${released_count} student${released_count !== 1 ? 's' : ''}.`);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to release grades');
    } finally {
      setReleasingAssignmentGradesAll(false);
    }
  };

  // Scaffold handlers
  const loadScaffolds = async () => {
    try {
      setScaffoldsLoading(true);
      const data = await getCourseScaffolds(courseId!);
      setScaffolds(data);
    } catch (err: any) {
      console.error('Failed to load scaffolds:', err);
      setScaffolds([]);
    } finally {
      setScaffoldsLoading(false);
    }
  };

  // Wizard step 1 → 2: Gather topics from selected lectures and documents
  const handleScaffoldStep1Next = async () => {
    if (!scaffoldForm.selectedLectures.length && !scaffoldForm.selectedSlides.length && !scaffoldForm.selectedFiles.length) {
      setScaffoldErrors({ general: 'Select at least one lecture or document' });
      return;
    }
    setScaffoldErrors({});
    setScaffoldTopicsLoading(true);

    // Topics from selected lectures (already extracted at processing time)
    const selectedLectures = course?.lectures.filter(l =>
      scaffoldForm.selectedLectures.includes(l.lecture_id)
    ) || [];
    const allTopics: string[] = [];
    selectedLectures.forEach(l => {
      if (l.topics) allTopics.push(...l.topics);
    });

    // Topics from selected documents (extracted on demand, cached server-side)
    try {
      const fileTopicLists = await Promise.all(
        scaffoldForm.selectedFiles.map(async (fileId) => {
          const file = courseFiles.find(f => f.file_id === fileId);
          if (file?.topics?.length) return file.topics;
          try {
            return await extractFileTopics(courseId!, fileId);
          } catch (err) {
            console.error('Topic extraction failed for file', fileId, err);
            return [] as string[];
          }
        })
      );
      fileTopicLists.forEach(topics => allTopics.push(...topics));
      if (scaffoldForm.selectedFiles.length > 0) {
        loadFoldersAndFiles(); // refresh cached topics on file objects
      }
    } finally {
      setScaffoldTopicsLoading(false);
    }

    const uniqueTopics = Array.from(new Set(allTopics));
    if (uniqueTopics.length === 0) {
      setScaffoldErrors({ general: 'No topics could be extracted from the selected sources' });
      return;
    }
    setScaffoldAvailableTopics(uniqueTopics);
    setScaffoldForm(prev => ({ ...prev, selectedTopics: [] }));
    setScaffoldWizardStep(2);
  };

  // Wizard step 2 → 3: Confirm topic selection
  const handleScaffoldStep2Next = () => {
    if (scaffoldForm.selectedTopics.length === 0) {
      setScaffoldErrors({ general: 'Select at least one topic to scaffold' });
      return;
    }
    setScaffoldErrors({});
    setScaffoldWizardStep(3);
  };

  // Wizard step 3: Generate scaffolds via global context (runs in background)
  const handleGenerateScaffolds = () => {
    const topics = [...scaffoldForm.selectedTopics];
    const lectureIds = [...scaffoldForm.selectedLectures];
    const slideIds = [...scaffoldForm.selectedSlides];
    const fileIds = [...scaffoldForm.selectedFiles];
    const preset = scaffoldForm.preset;

    setIsGenerateScaffoldModalOpen(false);
    setScaffoldWizardStep(1);
    setScaffoldForm({ selectedLectures: [], selectedSlides: [], selectedFiles: [], selectedTopics: [], preset: 'full_scaffold' });
    setScaffoldErrors({});

    // Start generation in the global context — progress shows in floating tracker
    startScaffoldGeneration(
      courseId!,
      topics,
      lectureIds,
      slideIds,
      preset,
      () => loadScaffolds(), // reload scaffolds when generation completes
      fileIds,
    );
  };

  const toggleScaffoldFileSelection = (fileId: string) => {
    setScaffoldForm(prev => ({
      ...prev,
      selectedFiles: prev.selectedFiles.includes(fileId)
        ? prev.selectedFiles.filter(id => id !== fileId)
        : [...prev.selectedFiles, fileId]
    }));
  };

  const toggleScaffoldTopicSelection = (topic: string) => {
    setScaffoldForm(prev => ({
      ...prev,
      selectedTopics: prev.selectedTopics.includes(topic)
        ? prev.selectedTopics.filter(t => t !== topic)
        : [...prev.selectedTopics, topic]
    }));
  };

  const selectAllTopics = () => {
    setScaffoldForm(prev => ({
      ...prev,
      selectedTopics: scaffoldAvailableTopics.length === prev.selectedTopics.length ? [] : [...scaffoldAvailableTopics]
    }));
  };

  const handleDeleteScaffold = async (scaffoldId: string) => {
    try {
      await deleteScaffold(scaffoldId);
      setScaffoldToDelete(null);
      setViewingScaffold(null);
      await loadScaffolds();
    } catch (err: any) {
      alert('Failed to delete question set');
    }
  };

  const handleViewScaffold = async (scaffoldId: string) => {
    try {
      setScaffoldDetailLoading(true);
      const scaffold = await getScaffold(scaffoldId);
      setViewingScaffold(scaffold);
    } catch (err: any) {
      alert('Failed to load question set');
    } finally {
      setScaffoldDetailLoading(false);
    }
  };

  const handlePublishScaffold = async () => {
    if (!viewingScaffold) return;
    setScaffoldPublishing(true);
    try {
      const updated = await updateScaffold(viewingScaffold.scaffold_id, { published: true });
      setViewingScaffold(updated);
      await loadScaffolds();
    } catch (err: any) {
      alert('Failed to publish question set');
    } finally {
      setScaffoldPublishing(false);
    }
  };

  const handleUnpublishScaffold = async () => {
    if (!viewingScaffold) return;
    setScaffoldPublishing(true);
    try {
      const updated = await updateScaffold(viewingScaffold.scaffold_id, { published: false });
      setViewingScaffold(updated);
      await loadScaffolds();
    } catch (err: any) {
      alert('Failed to unpublish question set');
    } finally {
      setScaffoldPublishing(false);
    }
  };

  const toggleScaffoldLectureSelection = (lectureId: string) => {
    setScaffoldForm(prev => ({
      ...prev,
      selectedLectures: prev.selectedLectures.includes(lectureId)
        ? prev.selectedLectures.filter(id => id !== lectureId)
        : [...prev.selectedLectures, lectureId]
    }));
  };

  const toggleScaffoldSlideSelection = (slideId: string) => {
    setScaffoldForm(prev => ({
      ...prev,
      selectedSlides: prev.selectedSlides.includes(slideId)
        ? prev.selectedSlides.filter(id => id !== slideId)
        : [...prev.selectedSlides, slideId]
    }));
  };

  // ── Scaffold batch grouping ──
  const groupScaffoldsIntoBatches = (scaffoldList: ScaffoldListItem[]) => {
    if (scaffoldList.length === 0) return [];
    const sorted = [...scaffoldList].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
    const batches: {
      batchKey: string;
      label: string;
      dateLabel: string;
      lectureLabel: string;
      topics: string[];
      scaffolds: ScaffoldListItem[];
      createdAt: Date;
    }[] = [];

    for (const scaffold of sorted) {
      const scaffoldTime = new Date(scaffold.created_at).getTime();
      const sourceKeyOf = (sc: ScaffoldListItem) =>
        [...(sc.source_lectures || []), ...(sc.source_files || [])].slice().sort().join(',');
      const srcKey = sourceKeyOf(scaffold);
      let matched = false;
      for (const batch of batches) {
        const batchSrcKey = sourceKeyOf(batch.scaffolds[0]);
        if (srcKey === batchSrcKey && Math.abs(scaffoldTime - batch.createdAt.getTime()) < TWO_HOURS_MS) {
          batch.scaffolds.push(scaffold);
          if (!batch.topics.includes(scaffold.topic)) batch.topics.push(scaffold.topic);
          matched = true;
          break;
        }
      }
      if (!matched) {
        const createdDate = new Date(scaffold.created_at);
        const dateLabel = createdDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const lectureNames = (scaffold.source_lectures || []).map(lid => {
          const lec = course?.lectures?.find((l: any) => l.lecture_id === lid);
          return lec ? lec.title : lid;
        });
        const fileNames = (scaffold.source_files || []).map(fid => {
          const file = courseFiles.find(f => f.file_id === fid);
          return file ? (file.title || file.file_name) : 'Document';
        });
        const sourceNames = [...lectureNames, ...fileNames];
        batches.push({
          batchKey: `${srcKey}_${Math.floor(scaffoldTime / TWO_HOURS_MS)}`,
          label: `${dateLabel} · ${sourceNames.join(', ') || 'Unknown sources'}`,
          dateLabel,
          lectureLabel: sourceNames.join(', ') || 'Unknown sources',
          topics: [scaffold.topic],
          scaffolds: [scaffold],
          createdAt: createdDate,
        });
      }
    }
    return batches;
  };
  const scaffoldBatches = groupScaffoldsIntoBatches(scaffolds);
  // List filter applied within each batch folder; folders left empty by the filter are hidden
  const visibleScaffoldBatches = scaffoldBatches
    .map((batch) => ({ ...batch, scaffolds: batch.scaffolds.filter(scaffoldMatchesListFilter) }))
    .filter((batch) => batch.scaffolds.length > 0);

  // ── Batch folder handlers ──
  const toggleBatchExpand = (batchKey: string) => {
    setExpandedBatches(prev => {
      const next = new Set(prev);
      if (next.has(batchKey)) next.delete(batchKey); else next.add(batchKey);
      return next;
    });
  };

  const toggleScaffoldSelection = (scaffoldId: string) => {
    setSelectedScaffolds(prev => {
      const next = new Set(prev);
      if (next.has(scaffoldId)) next.delete(scaffoldId); else next.add(scaffoldId);
      return next;
    });
  };

  const selectAllInBatch = (batchScaffolds: ScaffoldListItem[]) => {
    const allIds = batchScaffolds.map(s => s.scaffold_id);
    const allSelected = allIds.every(id => selectedScaffolds.has(id));
    setSelectedScaffolds(prev => {
      const next = new Set(prev);
      if (allSelected) { allIds.forEach(id => next.delete(id)); }
      else { allIds.forEach(id => next.add(id)); }
      return next;
    });
  };

  const handleOpenPublishAsQuiz = () => {
    const selectedTopics = scaffolds
      .filter(s => selectedScaffolds.has(s.scaffold_id))
      .map(s => s.topic)
      .filter(Boolean);
    // Deduplicate topics while preserving order
    const uniqueTopics = Array.from(new Set(selectedTopics));
    if (uniqueTopics.length > 0) {
      setPublishAsQuizTitle(`Quiz_${uniqueTopics.join('_')}`);
    } else {
      setPublishAsQuizTitle('Generated Quiz');
    }
    setIsPublishAsQuizModalOpen(true);
  };

  const handlePublishAsQuiz = async () => {
    if (selectedScaffolds.size === 0) return;
    setPublishAsQuizLoading(true);
    try {
      const result = await publishScaffoldsAsQuiz(
        courseId!,
        Array.from(selectedScaffolds),
        publishAsQuizTitle,
      );
      setIsPublishAsQuizModalOpen(false);
      setSelectedScaffolds(new Set());
      alert(`${result.message}\n\nView it under Assessments, in the Quizzes list.`);
      loadQuizzes();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to publish scaffolds as quiz');
    } finally {
      setPublishAsQuizLoading(false);
    }
  };

  const getPublishStats = () => {
    let mcqCount = 0;
    let openEndedCount = 0;
    for (const scaffold of scaffolds) {
      if (!selectedScaffolds.has(scaffold.scaffold_id)) continue;
      mcqCount += scaffold.type_distribution?.mcq || 0;
      openEndedCount += scaffold.type_distribution?.open_ended || 0;
    }
    return { mcqCount, openEndedCount };
  };

  // Course-level reflections loader
  const loadCourseReflections = async () => {
    try {
      setCourseReflectionsLoading(true);
      const data = await getCourseReflectionAnalytics(courseId!);
      setCourseReflections(data);
    } catch (err: any) {
      console.error('Failed to load course reflection analytics:', err);
      setCourseReflections(null);
    } finally {
      setCourseReflectionsLoading(false);
    }
  };

  // Concept heatmap loader (Insights > Learning trends)
  const loadReflectionHeatmap = async () => {
    try {
      setHeatmapLoading(true);
      const data = await getReflectionHeatmap(courseId!);
      setHeatmapData(data);
    } catch (err) {
      console.error('Failed to load reflection heatmap:', err);
      setHeatmapData(null);
    } finally {
      setHeatmapLoading(false);
    }
  };

  // ===== AI reflection analysis (Insights tab) =====
  const stopAiAnalysisPolling = () => {
    if (aiPollRef.current) {
      clearInterval(aiPollRef.current);
      aiPollRef.current = null;
    }
  };

  const startAiAnalysisPolling = () => {
    if (aiPollRef.current || !courseId) return;
    aiPollRef.current = setInterval(async () => {
      try {
        const data = await getReflectionAnalysis(courseId);
        setAiAnalysis(data);
        if (data.job?.status === 'completed' || data.job?.status === 'failed') {
          stopAiAnalysisPolling();
          if (data.job?.status === 'completed') {
            // Topic tags may have changed; refresh the concept heatmap
            loadReflectionHeatmap();
          }
        }
      } catch (err) {
        console.error('Failed to poll reflection analysis:', err);
      }
    }, 5000);
  };

  const loadAiAnalysis = async () => {
    try {
      const data = await getReflectionAnalysis(courseId!);
      setAiAnalysis(data);
      // Resume polling if a job kicked off earlier is still running
      if (data.job?.status === 'running') {
        startAiAnalysisPolling();
      }
    } catch (err) {
      console.error('Failed to load reflection analysis:', err);
    }
  };

  const handleAnalyzeReflections = async () => {
    if (!courseId) return;
    try {
      setAiAnalysisStarting(true);
      await startReflectionAnalysis(courseId);
      setAiAnalysis((prev) => ({
        job: { status: 'running', progress: 'Starting analysis' },
        lectures: prev?.lectures ?? [],
        students: prev?.students ?? [],
      }));
      startAiAnalysisPolling();
    } catch (err) {
      console.error('Failed to start reflection analysis:', err);
    } finally {
      setAiAnalysisStarting(false);
    }
  };

  const aiJobStatus = aiAnalysis?.job?.status;
  const aiRunning = aiJobStatus === 'running' || aiAnalysisStarting;
  const sortedAiLectures = aiAnalysis
    ? [...aiAnalysis.lectures].sort((a, b) => a.lecture_order - b.lecture_order)
    : [];
  const aiHasResults = sortedAiLectures.length > 0 || (aiAnalysis?.students.length ?? 0) > 0;
  // Default: the first lecture card starts expanded until the teacher toggles anything
  const aiExpandedEffective =
    aiExpandedLectures ?? new Set(sortedAiLectures.length > 0 ? [sortedAiLectures[0].lecture_id] : []);
  const toggleAiLecture = (lectureId: string) => {
    const next = new Set(aiExpandedEffective);
    if (next.has(lectureId)) {
      next.delete(lectureId);
    } else {
      next.add(lectureId);
    }
    setAiExpandedLectures(next);
  };

  // ===== Concept heatmap + per-lecture top topics (Insights > Learning trends) =====
  const heatmapLectures = heatmapData ? [...heatmapData.lectures].sort((a, b) => a.order - b.order) : [];
  // Largest single-cell count for the active polarity: drives the alpha ramp
  const heatmapMaxCell = heatmapData
    ? Math.max(1, ...heatmapData.topics.flatMap((t) => t.cells.map((c) => c[heatmapPolarity])))
    : 1;
  const heatmapRgb = heatmapPolarity === 'challenging' ? '245, 158, 11' : '100, 156, 221';

  // Lecture pills for the per-lecture top-topics charts: heatmap lectures first,
  // AI-analysis lectures as fallback so the selector works before the heatmap loads
  const trendsLectures =
    heatmapLectures.length > 0
      ? heatmapLectures.map((l) => ({ id: l.lecture_id, title: l.title, order: l.order }))
      : sortedAiLectures.map((l) => ({ id: l.lecture_id, title: l.lecture_title, order: l.lecture_order }));
  const trendsSelectedId =
    trendsLectureId && trendsLectures.some((l) => l.id === trendsLectureId)
      ? trendsLectureId
      : trendsLectures[0]?.id ?? null;
  const trendsSelectedAi = sortedAiLectures.find((l) => l.lecture_id === trendsSelectedId) ?? null;
  const trendsTopBars = (counts: Record<string, number>) =>
    Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([topic, count]) => ({
        topic: topic.length > 20 ? topic.slice(0, 18) + '...' : topic,
        fullTopic: topic,
        count,
      }));

  // ===== Learning paths (Insights > Learning paths) =====
  // Student choices for the combobox: classlist first (real names), AI-analysis usernames as fallback
  const pathStudentChoices: { username: string; fullName: string }[] = (() => {
    const map = new Map<string, string>();
    (enrolledStudents?.students ?? []).forEach((s) => map.set(s.username, s.full_name || s.username));
    (aiAnalysis?.students ?? []).forEach((s) => {
      if (!map.has(s.student_username)) map.set(s.student_username, s.student_username);
    });
    return Array.from(map, ([username, fullName]) => ({ username, fullName })).sort((a, b) =>
      a.fullName.localeCompare(b.fullName)
    );
  })();

  const pathQuery = pathSearch.trim().toLowerCase();
  const filteredPathChoices = pathQuery
    ? pathStudentChoices.filter(
        (s) => s.fullName.toLowerCase().includes(pathQuery) || s.username.toLowerCase().includes(pathQuery)
      )
    : pathStudentChoices;

  const selectedPathChoice = pathStudent
    ? pathStudentChoices.find((s) => s.username === pathStudent) ?? { username: pathStudent, fullName: pathStudent }
    : null;

  // Lecture titles in course order (for the per-concept lecture strip tooltips)
  const orderedLectureTitles = course ? [...course.lectures].sort((a, b) => a.order - b.order).map((l) => l.title) : [];
  const pathTotalLectures = Math.max(pathData?.summary.total_lectures ?? 0, orderedLectureTitles.length);

  const handleSelectPathStudent = async (choice: { username: string; fullName: string }) => {
    if (!courseId) return;
    const { username } = choice;
    setPathSearch(choice.fullName);
    setPathDropOpen(false);
    setPathHighlight(0);
    setPathStudent(username);
    setPathLoading(true);
    setPathData(null);
    pathReqRef.current = username;
    try {
      const data = await getLearningPath(courseId, username);
      if (pathReqRef.current !== username) return; // stale response
      setPathData(data);
    } catch (err) {
      console.error('Failed to load learning path:', err);
      if (pathReqRef.current === username) setPathData(null);
    } finally {
      if (pathReqRef.current === username) setPathLoading(false);
    }
  };

  const handleClearPathStudent = () => {
    setPathSearch('');
    setPathStudent(null);
    setPathData(null);
    setPathDropOpen(false);
    setPathHighlight(0);
    pathReqRef.current = null;
  };

  // ===== Learning outcomes (Insights > Learning outcomes) =====
  const stopOutcomesPolling = () => {
    if (outcomesPollRef.current) {
      clearInterval(outcomesPollRef.current);
      outcomesPollRef.current = null;
    }
  };

  const startOutcomesPolling = () => {
    if (outcomesPollRef.current || !courseId) return;
    outcomesPollRef.current = setInterval(async () => {
      try {
        const data = await getReflectionOutcomes(courseId);
        setOutcomesData(data);
        if (data.job?.status === 'completed' || data.job?.status === 'failed') {
          stopOutcomesPolling();
        }
      } catch (err) {
        console.error('Failed to poll reflection outcomes:', err);
      }
    }, 5000);
  };

  const loadOutcomes = async () => {
    try {
      const data = await getReflectionOutcomes(courseId!);
      setOutcomesData(data);
      // Resume polling if a job kicked off earlier is still running
      if (data.job?.status === 'running') {
        startOutcomesPolling();
      }
    } catch (err) {
      console.error('Failed to load reflection outcomes:', err);
    }
  };

  const handleGenerateOutcomes = async () => {
    if (!courseId) return;
    try {
      setOutcomesStarting(true);
      await startReflectionOutcomes(courseId);
      setOutcomesData((prev) => ({
        job: { status: 'running', progress: 'Starting outcome analysis' },
        learning_map: prev?.learning_map ?? null,
        outcomes: prev?.outcomes ?? null,
      }));
      startOutcomesPolling();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Failed to start outcome analysis');
      console.error('Failed to start reflection outcomes:', err);
    } finally {
      setOutcomesStarting(false);
    }
  };

  const toggleOutcomeObjective = (objectiveId: string) => {
    setOutcomesExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(objectiveId)) {
        next.delete(objectiveId);
      } else {
        next.add(objectiveId);
      }
      return next;
    });
  };

  const outcomesJobStatus = outcomesData?.job?.status;
  const outcomesRunning = outcomesJobStatus === 'running' || outcomesStarting;

  // ===== Course structure (Insights > Course structure): outcomes -> modules -> lectures =====
  const loadStructure = async () => {
    try {
      setStructureLoading(true);
      const data = await getCourseStructure(courseId!);
      setStructureDraft({ ...data, lecture_outcomes: data.lecture_outcomes ?? {} });
      setStructureSaved(false);
    } catch (err) {
      console.error('Failed to load course structure:', err);
    } finally {
      setStructureLoading(false);
    }
  };

  /** Apply a draft mutation and clear the "Saved" confirmation. */
  const mutateStructure = (fn: (draft: CourseStructure) => CourseStructure) => {
    setStructureSaved(false);
    setStructureDraft((prev) => (prev ? fn(prev) : prev));
  };

  const handleAddOutcome = () => {
    mutateStructure((d) => {
      const used = new Set(d.learning_objectives.map((o) => o.id));
      let n = d.learning_objectives.length + 1;
      while (used.has(`obj-${n}`)) n += 1;
      return {
        ...d,
        learning_objectives: [...d.learning_objectives, { id: `obj-${n}`, text: '' }],
      };
    });
  };

  const handleOutcomeTextChange = (id: string, text: string) => {
    mutateStructure((d) => ({
      ...d,
      learning_objectives: d.learning_objectives.map((o) => (o.id === id ? { ...o, text } : o)),
    }));
  };

  const handleRemoveOutcome = (id: string) => {
    mutateStructure((d) => ({
      ...d,
      learning_objectives: d.learning_objectives.filter((o) => o.id !== id),
      modules: d.modules.map((m) => ({
        ...m,
        objective_ids: m.objective_ids.filter((oid) => oid !== id),
      })),
      lecture_outcomes: Object.fromEntries(
        Object.entries(d.lecture_outcomes)
          .map(([lid, oids]) => [lid, oids.filter((oid) => oid !== id)] as const)
          .filter(([, oids]) => oids.length > 0)
      ),
    }));
  };

  const handleAddModule = () => {
    mutateStructure((d) => ({
      ...d,
      modules: [
        ...d.modules,
        {
          module_id: `mod-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          title: `Module ${d.modules.length + 1}`,
          order: d.modules.length,
          objective_ids: [],
          lecture_ids: [],
        },
      ],
    }));
  };

  const handleModuleTitleChange = (moduleId: string, title: string) => {
    mutateStructure((d) => ({
      ...d,
      modules: d.modules.map((m) => (m.module_id === moduleId ? { ...m, title } : m)),
    }));
  };

  /** Swap a module with its neighbor; the server assigns order by array position on save. */
  const handleMoveModule = (moduleId: string, dir: -1 | 1) => {
    mutateStructure((d) => {
      const idx = d.modules.findIndex((m) => m.module_id === moduleId);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= d.modules.length) return d;
      const modules = [...d.modules];
      [modules[idx], modules[target]] = [modules[target], modules[idx]];
      return { ...d, modules };
    });
  };

  const handleDeleteModule = (moduleId: string) => {
    if (!window.confirm('Delete this module? Its lectures will become unassigned.')) return;
    mutateStructure((d) => ({
      ...d,
      modules: d.modules.filter((m) => m.module_id !== moduleId),
    }));
  };

  const handleToggleModuleObjective = (moduleId: string, objectiveId: string) => {
    mutateStructure((d) => ({
      ...d,
      modules: d.modules.map((m) =>
        m.module_id === moduleId
          ? {
              ...m,
              objective_ids: m.objective_ids.includes(objectiveId)
                ? m.objective_ids.filter((oid) => oid !== objectiveId)
                : [...m.objective_ids, objectiveId],
            }
          : m
      ),
    }));
  };

  /** Move a lecture to a module (or unassign with ''); a lecture belongs to at most one module. */
  const handleSetLectureModule = (lectureId: string, moduleId: string) => {
    mutateStructure((d) => ({
      ...d,
      modules: d.modules.map((m) => {
        const without = m.lecture_ids.filter((lid) => lid !== lectureId);
        return m.module_id === moduleId
          ? { ...m, lecture_ids: [...without, lectureId] }
          : { ...m, lecture_ids: without };
      }),
    }));
  };

  /**
   * Toggle a lecture-level outcome chip. If the lecture has no explicit outcomes yet,
   * materialize its module's outcomes first, then apply the toggle. An empty result
   * deletes the explicit entry (the lecture falls back to inheriting from its module).
   */
  const handleToggleLectureOutcome = (lectureId: string, objectiveId: string) => {
    mutateStructure((d) => {
      const explicit = d.lecture_outcomes[lectureId];
      const base =
        explicit && explicit.length > 0
          ? explicit
          : (d.modules.find((m) => m.lecture_ids.includes(lectureId))?.objective_ids ?? []);
      const next = base.includes(objectiveId)
        ? base.filter((oid) => oid !== objectiveId)
        : [...base, objectiveId];
      const lecture_outcomes = { ...d.lecture_outcomes };
      if (next.length === 0) {
        delete lecture_outcomes[lectureId];
      } else {
        lecture_outcomes[lectureId] = next;
      }
      return { ...d, lecture_outcomes };
    });
  };

  /** Drop a lecture's explicit outcomes so it inherits from its module again. */
  const handleResetLectureOutcomes = (lectureId: string) => {
    mutateStructure((d) => {
      const lecture_outcomes = { ...d.lecture_outcomes };
      delete lecture_outcomes[lectureId];
      return { ...d, lecture_outcomes };
    });
  };

  const handleSaveStructure = async () => {
    if (!courseId || !structureDraft) return;
    try {
      setStructureSaving(true);
      const cleanedObjectives = structureDraft.learning_objectives
        .map((o) => ({ ...o, text: o.text.trim() }))
        .filter((o) => o.text.length > 0);
      const keptIds = new Set(cleanedObjectives.map((o) => o.id));
      const cleanedModules = structureDraft.modules.map((m, idx) => ({
        module_id: m.module_id,
        title: m.title.trim() || `Module ${idx + 1}`,
        objective_ids: m.objective_ids.filter((oid) => keptIds.has(oid)),
        lecture_ids: m.lecture_ids,
      }));
      // Only explicit, non-empty per-lecture outcome sets are persisted
      const cleanedLectureOutcomes: Record<string, string[]> = {};
      for (const [lid, oids] of Object.entries(structureDraft.lecture_outcomes)) {
        const kept = oids.filter((oid) => keptIds.has(oid));
        if (kept.length > 0) cleanedLectureOutcomes[lid] = kept;
      }
      const data = await saveCourseStructure(courseId, {
        learning_objectives: cleanedObjectives,
        modules: cleanedModules,
        lecture_outcomes: cleanedLectureOutcomes,
      });
      setStructureDraft((prev) => ({
        learning_objectives: data.learning_objectives,
        modules: data.modules,
        lecture_outcomes: data.lecture_outcomes,
        lectures: prev?.lectures ?? [],
      }));
      setStructureSaved(true);
    } catch (err) {
      console.error('Failed to save course structure:', err);
      alert('Failed to save course structure');
    } finally {
      setStructureSaving(false);
    }
  };

  const loadEnrolledStudents = async () => {
    try {
      setStudentsLoading(true);
      const data = await getCourseStudents(courseId!);
      setEnrolledStudents(data);
    } catch (err: any) {
      console.error('Failed to load enrolled students:', err);
      setEnrolledStudents(null);
    } finally {
      setStudentsLoading(false);
    }
  };

  const handleRosterImport = async () => {
    if (!rosterFile || !courseId) return;
    setRosterImporting(true);
    try {
      const result = await importRoster(courseId, rosterFile);
      setRosterResult(result);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to import roster');
    } finally {
      setRosterImporting(false);
    }
  };

  const closeRosterImportModal = () => {
    const didImport = !!rosterResult;
    setIsRosterImportModalOpen(false);
    setRosterFile(null);
    setRosterResult(null);
    setRosterImporting(false);
    setCopiedRosterRow(null);
    // Refresh the classlist so newly enrolled students show up right away
    if (didImport) loadEnrolledStudents();
  };

  const copyTempPassword = async (rowNum: number, password: string) => {
    try {
      await navigator.clipboard.writeText(password);
      setCopiedRosterRow(rowNum);
      setTimeout(() => setCopiedRosterRow((cur) => (cur === rowNum ? null : cur)), 1500);
    } catch {
      // Clipboard can be unavailable (http, permissions); the password stays visible either way
    }
  };

  const loadGradebook = async () => {
    try {
      setGradebookLoading(true);
      const data = await getGradebook(courseId!);
      setGradebook(data);
    } catch (err: any) {
      console.error('Failed to load gradebook:', err);
      setGradebook(null);
    } finally {
      setGradebookLoading(false);
    }
  };

  /** Color band for a best-attempt percentage: emerald >= 70, amber 40-69, red < 40. */
  const gradeBandClass = (pct: number): string =>
    pct >= 70
      ? 'text-emerald-600 dark:text-emerald-400'
      : pct >= 40
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-red-600 dark:text-red-400';

  /** Pill (tinted background) variant of the grade band, for the per-student header. */
  const gradeBandPillClass = (pct: number): string =>
    pct >= 70
      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
      : pct >= 40
        ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
        : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400';

  /** Per-quiz release map off a gradebook row (returned by the API alongside scores). */
  const rowReleasedMap = (row: GradebookRow): Record<string, boolean> | undefined =>
    (row as GradebookRow & { released?: Record<string, boolean> }).released;

  /** Small amber dot pill flagging a score the student cannot see yet. */
  const renderNotReleasedPill = () => (
    <span
      title="The student cannot see this grade yet"
      className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/25 dark:text-amber-300"
    >
      <span className="h-1 w-1 rounded-full bg-amber-500" />
      Not released
    </span>
  );

  /** Open the per-student gradebook view and reset any inline editor state. */
  const openGradebookStudent = (username: string) => {
    setGradebookStudent(username);
    setGradeEditQuizId(null);
    setGradeEditDraft('');
  };

  /**
   * Save (or revert, when percentage is null) an instructor grade adjustment,
   * then refetch the gradebook. Stays on the per-student view.
   */
  const handleSaveGradeOverride = async (username: string, quizId: string, percentage: number | null) => {
    try {
      setGradeEditSaving(true);
      await setGradeOverride(courseId!, username, quizId, percentage);
      setGradeEditQuizId(null);
      setGradeEditDraft('');
      await loadGradebook();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to adjust grade');
    } finally {
      setGradeEditSaving(false);
    }
  };

  /** Quote a CSV cell when it contains commas, quotes, or newlines. */
  const csvEscape = (value: string): string =>
    /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

  /** Build the gradebook as CSV client-side and download it via a temporary anchor. */
  const handleExportGradebookCsv = () => {
    if (!gradebook || !course) return;
    const header = ['Student', 'Username', ...gradebook.quizzes.map((q) => q.title), 'Average'];
    const lines = [header.map(csvEscape).join(',')];
    gradebook.rows.forEach((row) => {
      const cells = [
        row.full_name,
        row.username,
        ...gradebook.quizzes.map((q) => {
          const score = row.scores[q.quiz_id];
          if (score == null) return '';
          // Trailing asterisk marks instructor-adjusted values
          return `${Math.round(score * 10) / 10}${row.overridden?.[q.quiz_id] ? '*' : ''}`;
        }),
        row.average != null ? String(Math.round(row.average * 10) / 10) : '',
      ];
      lines.push(cells.map(csvEscape).join(','));
    });
    const slug = course.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'course';
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `gradebook-${slug}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  // Render markdown to styled HTML for notes

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${secs}s`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // File-type glyph + tint by extension group (archive/pdf/doc/sheet/image/video/other)
  const getFileGlyph = (fileName: string): { icon: React.ReactNode; tint: string } => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const svgProps = {
      className: 'h-5 w-5',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 1.8,
      strokeLinecap: 'round' as const,
      strokeLinejoin: 'round' as const,
      viewBox: '0 0 24 24',
    };
    if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(ext)) {
      return {
        tint: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
        icon: (
          <svg {...svgProps}>
            <path d="M21 8v11a2 2 0 01-2 2H5a2 2 0 01-2-2V8" />
            <path d="M2 3h20v5H2z" />
            <path d="M10 12h4" />
          </svg>
        ),
      };
    }
    if (ext === 'pdf') {
      return {
        tint: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        icon: (
          <svg {...svgProps}>
            <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            <path d="M13 3v5a2 2 0 002 2h5" />
            <path d="M9 13h6M9 17h4" />
          </svg>
        ),
      };
    }
    if (['doc', 'docx', 'txt', 'rtf', 'odt', 'md', 'pages'].includes(ext)) {
      return {
        tint: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        icon: (
          <svg {...svgProps}>
            <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            <path d="M13 3v5a2 2 0 002 2h5" />
            <path d="M9 13h6M9 17h6" />
          </svg>
        ),
      };
    }
    if (['xls', 'xlsx', 'csv', 'ods', 'numbers'].includes(ext)) {
      return {
        tint: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
        icon: (
          <svg {...svgProps}>
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M3 10h18M3 15h18M9 4v16M15 4v16" />
          </svg>
        ),
      };
    }
    if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'heic'].includes(ext)) {
      return {
        tint: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
        icon: (
          <svg {...svgProps}>
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <circle cx="9" cy="10" r="1.5" />
            <path d="M21 15l-5-5-9 9" />
          </svg>
        ),
      };
    }
    if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'].includes(ext)) {
      return {
        tint: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
        icon: (
          <svg {...svgProps}>
            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        ),
      };
    }
    return {
      tint: 'bg-gray-100 text-gray-600 dark:bg-secondary-700 dark:text-gray-300',
      icon: (
        <svg {...svgProps}>
          <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          <path d="M13 3v5a2 2 0 002 2h5" />
        </svg>
      ),
    };
  };

  // Compact AI-processing status pill for a lecture (shared by grid + list views)
  const renderLectureStatus = (lecture: LectureResponse, overlay = false) => {
    const hasAI = lecture.summary || (lecture.topics && lecture.topics.length > 0);
    const uploadedRecently = new Date(lecture.uploaded_at).getTime() > Date.now() - 30 * 60 * 1000;
    const base = overlay
      ? 'inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-semibold shadow-md backdrop-blur-sm'
      : 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold';
    if (hasAI) {
      return (
        <span className={`${base} ${overlay ? 'text-emerald-700' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          AI ready
        </span>
      );
    }
    if (uploadedRecently) {
      return (
        <span className={`${base} ${overlay ? 'text-amber-700' : 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300'}`}>
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
          Processing
        </span>
      );
    }
    return (
      <span className={`${base} ${overlay ? 'text-gray-600' : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'}`}>
        <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
        Pending
      </span>
    );
  };

  // Recommended video card, shared by the all-videos view and the per-lecture detail view
  const renderRecommendedVideoCard = (video: RecommendedVideoResponse) => (
    <div
      key={video.video_id}
      className={`group relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-medium dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-800 ${video.hidden ? 'opacity-60' : ''}`}
    >
      <a href={video.youtube_url} target="_blank" rel="noopener noreferrer" className="block relative">
        <img
          src={`https://img.youtube.com/vi/${video.youtube_video_id}/hqdefault.jpg`}
          alt={video.title}
          className="w-full h-44 object-cover group-hover:opacity-90 transition-opacity"
        />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="bg-red-600 rounded-full p-3 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
          </div>
        </div>
      </a>
      {renderVisibilityToggle(
        'recommended_video',
        video.video_id,
        !!video.hidden,
        `absolute top-3 right-12 flex h-8 w-8 items-center justify-center rounded-lg bg-white/90 text-gray-500 shadow-md backdrop-blur-sm transition-all hover:bg-gray-100 hover:text-gray-700 ${video.hidden ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} dark:bg-secondary-800/90 dark:text-gray-400 dark:hover:bg-secondary-700 dark:hover:text-gray-200`
      )}
      <button
        onClick={() => setVideoToDelete(video.video_id)}
        title="Delete video"
        className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-lg bg-white/90 text-gray-500 shadow-md backdrop-blur-sm transition-all hover:bg-red-50 hover:text-red-600 opacity-0 group-hover:opacity-100 dark:bg-secondary-800/90 dark:text-gray-400 dark:hover:bg-red-900/30 dark:hover:text-red-400"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
      <div className="p-5">
        <h3 className="text-base font-bold leading-snug text-gray-900 dark:text-white line-clamp-2">{video.title}</h3>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {video.hidden && <HiddenPill />}
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></svg>
            {new Date(video.added_at).toLocaleDateString()}
          </span>
          {/* Processing status pill */}
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
            video.processing_status === 'processed'
              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'
              : video.processing_status === 'processing'
              ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300'
              : video.processing_status === 'error'
              ? 'bg-red-50 text-red-700 dark:bg-red-900/25 dark:text-red-300'
              : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
          }`}>
            <span className={`h-1.5 w-1.5 rounded-full ${
              video.processing_status === 'processed'
                ? 'bg-emerald-500'
                : video.processing_status === 'processing'
                ? 'animate-pulse bg-amber-500'
                : video.processing_status === 'error'
                ? 'bg-red-500'
                : 'bg-gray-400'
            }`} />
            {video.processing_status === 'processed' ? 'Transcribed' :
             video.processing_status === 'processing' ? 'Processing' :
             video.processing_status === 'error' ? 'Error' : 'Not processed'}
          </span>
        </div>
        {/* Process / Retry button */}
        {(video.processing_status === 'not_processed' || video.processing_status === 'error') && (
          <button
            onClick={() => handleProcessVideo(video.video_id)}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {video.processing_status === 'error' ? 'Retry Processing' : 'Process for Quiz'}
          </button>
        )}
        {video.processing_status === 'processing' && (
          <div className="mt-4 flex items-center justify-center gap-2 w-full py-2 px-4 text-primary-600 dark:text-primary-400 text-sm font-bold">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 dark:border-primary-400"></div>
            Downloading & transcribing...
          </div>
        )}
      </div>
    </div>
  );

  // Course files not placed inside any folder (root-level "Files" entry)
  const rootFiles = courseFiles.filter((f) => !f.folder_id);

  // Lectures sorted per the content-manager control
  const sortedLectures = course
    ? [...course.lectures].sort((a, b) => {
        if (lecturesSort === 'newest') return new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime();
        if (lecturesSort === 'title') return a.title.localeCompare(b.title);
        return a.order - b.order;
      })
    : [];

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center rounded-3xl border border-gray-200 bg-white/60 py-16 dark:border-secondary-700 dark:bg-secondary-800/40">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-sm font-medium text-gray-500 dark:text-gray-400">Loading course…</p>
        </div>
      </Layout>
    );
  }

  if (error || !course) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
          <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-red-50 text-red-500 dark:bg-red-900/30 dark:text-red-400">
            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
            </svg>
          </div>
          <p className="mt-4 text-lg font-bold text-gray-900 dark:text-white">{error || 'Course not found'}</p>
          <Button onClick={() => navigate('/dashboard/teacher')} className="mt-6">
            Back to Dashboard
          </Button>
        </div>
      </Layout>
    );
  }

  const courseVisual = getCourseVisual(course.title);

  return (
    <Layout>
      <div className="space-y-8">
        {/* === Header === */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <button
              onClick={() => navigate('/dashboard/teacher')}
              className="inline-flex items-center gap-1.5 rounded-full border-2 border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-800 dark:border-secondary-700 dark:text-gray-400 dark:hover:bg-secondary-800 dark:hover:text-gray-200"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Courses
            </button>
            <div className="mt-3 flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                <SubjectGlyph glyph={courseVisual.glyph} className="h-6 w-6" strokeWidth={1.8} />
              </span>
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
                  {course.title}
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {course.semester && course.year ? `${course.semester} ${course.year} · ` : ''}{course.lecture_count} {course.lecture_count === 1 ? 'lecture' : 'lectures'} · {course.instructor_name || course.instructor}
                </p>
              </div>
            </div>
          </div>

          {/* Dynamic action button based on the active tab and selection */}
          <div className="flex gap-2">
            {activeTab === 'content' && contentView === 'lecture' && (
              <button
                onClick={() => setIsUploadModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload Lecture
              </button>
            )}
            {activeTab === 'content' && contentView === 'slides' && (
              <button
                onClick={() => setIsSlideUploadModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload Presentation
              </button>
            )}
            {activeTab === 'content' && contentView === 'recommended' && (
              <button
                onClick={() => setIsAddVideoModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Video
              </button>
            )}
            {activeTab === 'content' && (contentView === 'folder' || contentView === 'files') && (
              <button
                onClick={() => materialsFileInputRef.current?.click()}
                disabled={!!uploadingFileName}
                className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:cursor-not-allowed disabled:opacity-60"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload File
              </button>
            )}
            {activeTab === 'assessments' && assessmentsTab === 'quizzes' && (
              <button
                onClick={() => setIsGenerateQuizModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Generate Quiz
              </button>
            )}
            {activeTab === 'assessments' && assessmentsTab === 'scaffolds' && (
              <button
                onClick={() => setIsGenerateScaffoldModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Generate questions
              </button>
            )}
            {activeTab === 'assessments' && assessmentsTab === 'assignments' && (
              <button
                onClick={openCreateAssignmentModal}
                className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Assignment
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="overflow-x-auto">
          <nav className="inline-flex gap-1 rounded-full bg-gray-100 p-1 dark:bg-secondary-800" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-bold transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-primary-700 shadow-sm dark:bg-secondary-700 dark:text-white'
                    : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {tab.icon}
                {tab.label}
                {tab.id === 'content' && (
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                    activeTab === 'content' ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300' : 'bg-gray-200 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                  }`}>
                    {course.lectures.length}
                  </span>
                )}
                {tab.id === 'classlist' && enrolledStudents && enrolledStudents.total_students > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                    activeTab === 'classlist' ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300' : 'bg-gray-200 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                  }`}>
                    {enrolledStudents.total_students}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="animate-fade-in" key={activeTab}>
          {/* === HOME TAB === */}
          {activeTab === 'home' && (
            <div className="space-y-5">
              {/* Home tab header: quiet label + edit affordance */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">Course home</h2>
                <button
                  onClick={openEditHomeModal}
                  className="inline-flex items-center gap-1.5 rounded-full border-2 border-gray-200 px-4 py-2 text-xs font-bold text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-800 dark:border-secondary-700 dark:text-gray-400 dark:hover:bg-secondary-800 dark:hover:text-gray-200"
                >
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit home
                </button>
              </div>

              {/* Welcome card */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">About this course</p>
                {course.welcome_message || course.description ? (
                  <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                    {course.welcome_message || course.description}
                  </p>
                ) : (
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    No welcome message yet.{' '}
                    <button onClick={openEditHomeModal} className="font-bold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300">
                      Add one
                    </button>
                  </p>
                )}
              </div>

              {/* Class meetings + Office hours */}
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                {/* Class meetings */}
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <rect x="3" y="4" width="18" height="16" rx="2" />
                        <path d="M3 9h18M8 2v4M16 2v4" />
                      </svg>
                    </span>
                    <h3 className="text-base font-extrabold text-gray-900 dark:text-white">Class meetings</h3>
                  </div>
                  {course.class_schedule ? (
                    <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-gray-700 dark:text-gray-300">{course.class_schedule}</p>
                  ) : (
                    <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                      No class schedule posted yet.{' '}
                      <button onClick={openEditHomeModal} className="font-bold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300">
                        Add
                      </button>
                    </p>
                  )}
                  {course.class_meeting_link && (
                    <div className="mt-4">
                      <a
                        href={course.class_meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Join class
                      </a>
                      <p className="mt-2 truncate text-xs font-medium text-gray-500 dark:text-gray-400" title={course.class_meeting_link}>
                        {course.class_meeting_link}
                      </p>
                    </div>
                  )}
                </div>

                {/* Office hours */}
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 3" />
                      </svg>
                    </span>
                    <h3 className="text-base font-extrabold text-gray-900 dark:text-white">Office hours</h3>
                  </div>
                  {course.office_hours ? (
                    <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-gray-700 dark:text-gray-300">{course.office_hours}</p>
                  ) : (
                    <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                      No office hours posted yet.{' '}
                      <button onClick={openEditHomeModal} className="font-bold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300">
                        Add
                      </button>
                    </p>
                  )}
                  {course.office_hours_link && (
                    <div className="mt-4">
                      <a
                        href={course.office_hours_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Join office hours
                      </a>
                      <p className="mt-2 truncate text-xs font-medium text-gray-500 dark:text-gray-400" title={course.office_hours_link}>
                        {course.office_hours_link}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact */}
              <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Contact</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">{course.instructor_name || course.instructor}</p>
                  </div>
                </div>
                {course.contact_email ? (
                  <a
                    href={`mailto:${course.contact_email}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {course.contact_email}
                  </a>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No contact email posted yet.{' '}
                    <button onClick={openEditHomeModal} className="font-bold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300">
                      Add
                    </button>
                  </p>
                )}
              </div>

              {/* Course at a glance */}
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Course at a glance</p>
                <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <rect x="3" y="4" width="18" height="16" rx="2" />
                      <path d="M3 9h18M8 2v4M16 2v4" />
                    </svg>
                    {course.semester && course.year ? `${course.semester} ${course.year}` : 'No term set'}
                  </span>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {course.lecture_count} {course.lecture_count === 1 ? 'lecture' : 'lectures'}
                  </span>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      <path d="M9 14l2 2 4-4" />
                    </svg>
                    {quizzesLoading
                      ? 'Checking quizzes...'
                      : (() => {
                          const published = quizzes.filter((q) => q.published).length;
                          return published > 0
                            ? `${published} ${published === 1 ? 'quiz' : 'quizzes'} available`
                            : 'No quizzes available yet';
                        })()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* === CONTENT TAB (D2L-style table of contents + detail pane) === */}
          {activeTab === 'content' && (
            <div className="space-y-5">
                  {/* Continue watching banner: last unfinished lecture in this course */}
                  {(() => {
                    if (!lastWatched) return null;
                    const hero = course.lectures.find((l) => l.lecture_id === lastWatched.lecture_id);
                    if (!hero) return null;
                    const apiUrl = getStaticBaseUrl();
                    const fmtClock = (s: number) => {
                      const m = Math.floor(s / 60);
                      const sec = Math.floor(s % 60);
                      return `${m}:${sec.toString().padStart(2, '0')}`;
                    };
                    return (
                      <div
                        onClick={() => navigate(`/teacher/courses/${course.course_id}/lectures/${hero.lecture_id}`)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/teacher/courses/${course.course_id}/lectures/${hero.lecture_id}`); } }}
                        className="group flex cursor-pointer items-center gap-4 overflow-hidden rounded-2xl border border-gray-200 bg-white p-3 pr-5 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-medium focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-500/20 dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-800"
                      >
                        {/* Thumbnail with progress */}
                        <div className="relative h-20 w-32 flex-shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-secondary-700 dark:to-secondary-800">
                          {hero.thumbnail_path ? (
                            <img src={`${apiUrl}${hero.thumbnail_path}`} alt={hero.title} className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <svg className="h-7 w-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                            </div>
                          )}
                          <div className="absolute inset-x-0 bottom-0 h-1 bg-black/20">
                            <div className="h-full bg-primary-500" style={{ width: `${Math.min(100, Math.max(2, lastWatched.percent))}%` }} />
                          </div>
                        </div>
                        {/* Details */}
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-gray-500 dark:text-gray-400">Continue watching</p>
                          <p className="truncate text-sm font-extrabold text-gray-900 dark:text-white">{hero.title}</p>
                          {lastWatched.duration_seconds > 0 && (
                            <p className="mt-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                              {fmtClock(lastWatched.position_seconds)} / {fmtClock(lastWatched.duration_seconds)}
                            </p>
                          )}
                        </div>
                        <span className="hidden flex-shrink-0 items-center gap-2 rounded-full bg-primary-600 px-4 py-2 text-xs font-bold text-gray-900 shadow-[0_3px_0_0_rgba(0,0,0,0.12)] transition-all group-hover:bg-primary-700 group-active:translate-y-0.5 group-active:shadow-none sm:inline-flex">
                          <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                          Resume
                        </span>
                      </div>
                    );
                  })()}

                  {/* Mobile: table of contents as a select */}
                  <div className="lg:hidden">
                    <label htmlFor="content-toc-select" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Table of Contents
                    </label>
                    <select
                      id="content-toc-select"
                      value={
                        contentView === 'lecture'
                          ? (selectedLectureId ? `lecture:${selectedLectureId}` : 'lecture:none')
                          : contentView === 'folder'
                          ? `folder:${selectedFolderId}`
                          : contentView
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === 'slides' || v === 'recommended' || v === 'files') setContentView(v);
                        else if (v.startsWith('folder:')) { setSelectedFolderId(v.slice('folder:'.length)); setContentView('folder'); }
                        else if (v.startsWith('lecture:') && v !== 'lecture:none') handleSelectLecture(v.slice('lecture:'.length));
                      }}
                      className="w-full rounded-full border-2 border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-800 dark:text-gray-200"
                    >
                      <optgroup label="Lectures">
                        {sortedLectures.length === 0 && <option value="lecture:none">No lectures yet</option>}
                        {sortedLectures.map((lecture, index) => (
                          <option key={lecture.lecture_id} value={`lecture:${lecture.lecture_id}`}>
                            {index + 1}. {lecture.title}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Course materials">
                        {contentFolders.map((folder) => (
                          <option key={folder.folder_id} value={`folder:${folder.folder_id}`}>
                            {folder.name} ({folder.file_count})
                          </option>
                        ))}
                        {rootFiles.length > 0 && <option value="files">Files ({rootFiles.length})</option>}
                        <option value="slides">Slides and presentations ({slides.length})</option>
                        <option value="recommended">Recommended videos ({recommendedVideos.length})</option>
                      </optgroup>
                    </select>
                  </div>

                  <div className="flex items-start gap-6">
                    {/* Left sidebar: Table of Contents */}
                    <aside className="sticky top-6 hidden w-64 shrink-0 lg:block">
                      <div className="max-h-[calc(100vh-7rem)] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-3 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                        <p className="px-2 pb-2 pt-1 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Table of Contents</p>

                        {/* Lectures module */}
                        <button
                          onClick={() => setTocLecturesOpen(!tocLecturesOpen)}
                          className="flex w-full items-center justify-between rounded-xl px-2 py-2 text-sm font-bold text-gray-900 transition-colors hover:bg-gray-50 dark:text-white dark:hover:bg-secondary-700/50"
                        >
                          <span className="flex items-center gap-2">
                            Lectures
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">{course.lectures.length}</span>
                          </span>
                          <svg className={`h-4 w-4 text-gray-400 transition-transform ${tocLecturesOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        {tocLecturesOpen && (
                          <div className="mt-0.5 space-y-0.5">
                            {sortedLectures.length === 0 && (
                              <p className="px-2 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">No lectures yet</p>
                            )}
                            {sortedLectures.map((lecture, index) => {
                              const isActive = contentView === 'lecture' && selectedLectureId === lecture.lecture_id;
                              const hasAI = lecture.summary || (lecture.topics && lecture.topics.length > 0);
                              const uploadedRecently = new Date(lecture.uploaded_at).getTime() > Date.now() - 30 * 60 * 1000;
                              const dot = hasAI ? 'bg-emerald-500' : uploadedRecently ? 'animate-pulse bg-amber-500' : 'bg-gray-400';
                              return (
                                <button
                                  key={lecture.lecture_id}
                                  onClick={() => handleSelectLecture(lecture.lecture_id)}
                                  title={lecture.title}
                                  className={`flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-sm transition-colors ${
                                    isActive
                                      ? 'bg-primary-50 font-bold text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                                      : 'font-semibold text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-secondary-700/50'
                                  }`}
                                >
                                  <span className={`w-5 shrink-0 text-center text-xs font-bold ${isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`}>{index + 1}</span>
                                  <span className="min-w-0 flex-1 truncate">{lecture.title}</span>
                                  {lecture.hidden && eyeOffGlyph('h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500')}
                                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
                                </button>
                              );
                            })}
                          </div>
                        )}

                        <div className="my-2 border-t border-gray-100 dark:border-secondary-700" />

                        {/* Course materials module */}
                        <button
                          onClick={() => setTocMaterialsOpen(!tocMaterialsOpen)}
                          className="flex w-full items-center justify-between rounded-xl px-2 py-2 text-sm font-bold text-gray-900 transition-colors hover:bg-gray-50 dark:text-white dark:hover:bg-secondary-700/50"
                        >
                          <span>Course materials</span>
                          <svg className={`h-4 w-4 text-gray-400 transition-transform ${tocMaterialsOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        {tocMaterialsOpen && (
                          <div className="mt-0.5 space-y-0.5">
                            {contentFolders.map((folder) => {
                              const isActive = contentView === 'folder' && selectedFolderId === folder.folder_id;
                              return (
                                <button
                                  key={folder.folder_id}
                                  onClick={() => { setSelectedFolderId(folder.folder_id); setContentView('folder'); }}
                                  title={folder.name}
                                  className={`flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-sm transition-colors ${
                                    isActive
                                      ? 'bg-primary-50 font-bold text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                                      : 'font-semibold text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-secondary-700/50'
                                  }`}
                                >
                                  <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                    <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                                  </svg>
                                  <span className="min-w-0 flex-1 truncate">{folder.name}</span>
                                  {folder.hidden && eyeOffGlyph('h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500')}
                                  <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-bold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">{folder.file_count}</span>
                                </button>
                              );
                            })}
                            {rootFiles.length > 0 && (
                              <button
                                onClick={() => { setSelectedFolderId(null); setContentView('files'); }}
                                className={`flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-sm transition-colors ${
                                  contentView === 'files'
                                    ? 'bg-primary-50 font-bold text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                                    : 'font-semibold text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-secondary-700/50'
                                }`}
                              >
                                <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                  <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                  <path d="M13 3v5a2 2 0 002 2h5" />
                                </svg>
                                <span className="min-w-0 flex-1 truncate">Files</span>
                                <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-bold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">{rootFiles.length}</span>
                              </button>
                            )}
                            <button
                              onClick={() => setContentView('slides')}
                              className={`flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-sm transition-colors ${
                                contentView === 'slides'
                                  ? 'bg-primary-50 font-bold text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                                  : 'font-semibold text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-secondary-700/50'
                              }`}
                            >
                              <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                <path d="M13 3v5a2 2 0 002 2h5" />
                              </svg>
                              <span className="min-w-0 flex-1 truncate">Slides and presentations ({slides.length})</span>
                            </button>
                            <button
                              onClick={() => setContentView('recommended')}
                              className={`flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-sm transition-colors ${
                                contentView === 'recommended'
                                  ? 'bg-primary-50 font-bold text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                                  : 'font-semibold text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-secondary-700/50'
                              }`}
                            >
                              <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="min-w-0 flex-1 truncate">Recommended videos ({recommendedVideos.length})</span>
                            </button>
                            <button
                              onClick={openCreateFolderModal}
                              className="flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-xs font-bold text-gray-500 transition-colors hover:bg-gray-50 hover:text-primary-600 dark:text-gray-400 dark:hover:bg-secondary-700/50 dark:hover:text-primary-400"
                            >
                              <svg className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <path d="M12 5v14M5 12h14" />
                              </svg>
                              New folder
                            </button>
                          </div>
                        )}
                      </div>
                    </aside>

                    {/* Right pane: current selection */}
                    <div className="min-w-0 flex-1">
          {/* Selected lecture detail view */}
          {contentView === 'lecture' && (() => {
            const selectedLecture = course.lectures.find((l) => l.lecture_id === selectedLectureId);
            if (!selectedLecture) {
              return (
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <Mascot name="folder" chip="#93c5fd" className="h-20 w-20 shadow-medium" blinkDelay={0.5} />
                  <h3 className="mt-5 text-lg font-bold text-gray-900 dark:text-white">No lectures yet</h3>
                  <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">Upload your first lecture to get started</p>
                  <div className="mt-6">
                    <Button onClick={() => setIsUploadModalOpen(true)}>Upload Lecture</Button>
                  </div>
                </div>
              );
            }
            const apiUrl = getStaticBaseUrl();
            const lectureNumber = sortedLectures.findIndex((l) => l.lecture_id === selectedLecture.lecture_id) + 1;
            const lectureVideos = recommendedVideos.filter(
              (v) => (v as RecommendedVideoResponse & { lecture_id?: string }).lecture_id === selectedLecture.lecture_id
            );
            const isResume = lastWatched?.lecture_id === selectedLecture.lecture_id;
            return (
              <div className="space-y-5">
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                  {/* 16:9 thumbnail */}
                  <div
                    onClick={() => navigate(`/teacher/courses/${courseId}/lectures/${selectedLecture.lecture_id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/teacher/courses/${courseId}/lectures/${selectedLecture.lecture_id}`); } }}
                    className="group relative mx-auto mt-4 aspect-video w-full max-w-2xl cursor-pointer overflow-hidden rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-500/20 dark:from-secondary-700 dark:to-secondary-800"
                  >
                    {/* Centered play button */}
                    <span className="absolute inset-0 z-10 flex items-center justify-center">
                      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-primary-700 shadow-large transition-transform duration-200 group-hover:scale-110">
                        <svg className="ml-1 h-7 w-7" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                      </span>
                    </span>
                    {selectedLecture.thumbnail_path ? (
                      <img src={`${apiUrl}${selectedLecture.thumbnail_path}`} alt={selectedLecture.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <svg className="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                      </div>
                    )}
                    {/* Play overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/30 group-hover:opacity-100">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-primary-700 shadow-lg">
                        <svg className="ml-0.5 h-7 w-7" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                      </span>
                    </div>
                    {/* Number badge */}
                    <span className="absolute left-3 top-3 rounded-full bg-primary-600 px-2.5 py-0.5 text-xs font-bold text-gray-900 shadow-md">Lecture {lectureNumber}</span>
                    {/* Status */}
                    <div className="absolute right-3 top-3">{renderLectureStatus(selectedLecture, true)}</div>
                    {/* Duration */}
                    <span className="absolute bottom-3 right-3 rounded-full bg-black/70 px-2 py-0.5 text-xs font-bold text-white">{formatDuration(selectedLecture.duration)}</span>
                  </div>
                  {/* Body */}
                  <div className="p-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <h3 className="text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">{selectedLecture.title}</h3>
                        <div className="mt-2 flex flex-wrap items-center gap-3">
                          {renderLectureStatus(selectedLecture)}
                          {selectedLecture.hidden && <HiddenPill label="Hidden from students" />}
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                            {new Date(selectedLecture.uploaded_at).toLocaleDateString()}
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3" /></svg>
                            {formatDuration(selectedLecture.duration)}
                          </span>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          onClick={() => navigate(`/teacher/courses/${courseId}/lectures/${selectedLecture.lecture_id}`)}
                          className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                        >
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                          {isResume ? 'Resume' : 'Watch'}
                        </button>
                        {renderVisibilityToggle(
                          'lecture',
                          selectedLecture.lecture_id,
                          !!selectedLecture.hidden,
                          'flex h-10 w-10 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-secondary-700 dark:hover:text-gray-200'
                        )}
                        <button
                          onClick={() => setLectureToDelete(selectedLecture.lecture_id)}
                          className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                          title="Delete lecture"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </div>
                    {(selectedLecture.summary || selectedLecture.description) && (
                      <p className="mt-3 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                        {selectedLecture.summary || selectedLecture.description}
                      </p>
                    )}
                    {selectedLecture.topics && selectedLecture.topics.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {selectedLecture.topics.map((topic, idx) => (
                          <span key={idx} className="rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">{topic}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Recommended videos linked to this lecture */}
                {lectureVideos.length > 0 && (
                  <div>
                    <div className="mb-3 flex items-center gap-2">
                      <h4 className="text-base font-extrabold text-gray-900 dark:text-white">Recommended videos for this lecture</h4>
                      <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-bold text-gray-600 dark:bg-secondary-700 dark:text-gray-300">{lectureVideos.length}</span>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                      {lectureVideos.map((video) => renderRecommendedVideoCard(video))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Slides and presentations view */}
          {contentView === 'slides' && (
            <>
              {slidesLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                </div>
              ) : slides.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <Mascot name="book" chip="#fbbf24" className="h-20 w-20 shadow-medium" blinkDelay={0.9} />
                  <h3 className="mt-5 text-lg font-bold text-gray-900 dark:text-white">No presentations yet</h3>
                  <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">Upload slides and presentations for your students</p>
                  <div className="mt-6">
                    <Button onClick={() => setIsSlideUploadModalOpen(true)}>Upload Presentation</Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {slides.map((slide) => {
                    const apiUrl = getStaticBaseUrl();
                    return (
                      <div
                        key={slide.slide_id}
                        className={`group relative flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-medium dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-800 ${slide.hidden ? 'opacity-60' : ''}`}
                      >
                        {/* Header: icon tile + file-type chip */}
                        <div className="flex items-start justify-between">
                          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                              <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              <path d="M13 3v5a2 2 0 002 2h5" />
                              <path d="M9 13h6M9 17h4" />
                            </svg>
                          </span>
                          <div className="flex items-center gap-1.5">
                            {slide.hidden && <HiddenPill />}
                            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold uppercase text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                              {slide.file_name.split('.').pop()}
                            </span>
                            {renderVisibilityToggle(
                              'slide',
                              slide.slide_id,
                              !!slide.hidden,
                              `flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-700 ${slide.hidden ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} dark:hover:bg-secondary-700 dark:hover:text-gray-200`
                            )}
                            <button
                              onClick={() => setSlideToDelete(slide.slide_id)}
                              title="Delete presentation"
                              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        {/* Title */}
                        <h3 className="mt-4 line-clamp-2 text-base font-bold leading-snug text-gray-900 dark:text-white">
                          {slide.title}
                        </h3>
                        <p className="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">{slide.file_name}</p>

                        {/* Meta row */}
                        <div className="mt-3 flex items-center gap-4 text-xs font-medium text-gray-500 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1.5">
                            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1" /><path d="M12 3v10m0 0l-4-4m4 4l4-4" /></svg>
                            {formatFileSize(slide.file_size)}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></svg>
                            {new Date(slide.uploaded_at).toLocaleDateString()}
                          </span>
                        </div>

                        {/* Download CTA */}
                        <a
                          href={`${apiUrl}${slide.file_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download
                        </a>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Recommended videos view (all) */}
          {contentView === 'recommended' && (
            <>
              {recommendedLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                </div>
              ) : recommendedVideos.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <Mascot name="star" chip="#a5b4fc" className="h-20 w-20 shadow-medium" blinkDelay={1.3} />
                  <h3 className="mt-5 text-lg font-bold text-gray-900 dark:text-white">No recommended videos yet</h3>
                  <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">Add YouTube videos for your students to watch</p>
                  <div className="mt-6">
                    <Button onClick={() => setIsAddVideoModalOpen(true)}>Add Video</Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {recommendedVideos.map((video) => renderRecommendedVideoCard(video))}
                </div>
              )}
            </>
          )}

          {/* Content folder / root files view */}
          {(contentView === 'folder' || contentView === 'files') && (() => {
            const activeFolder = contentView === 'folder'
              ? contentFolders.find((f) => f.folder_id === selectedFolderId)
              : undefined;
            if (contentView === 'folder' && !activeFolder) {
              return (
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">This folder is no longer available.</p>
                </div>
              );
            }
            const filesInView = courseFiles.filter((f) =>
              contentView === 'folder' ? f.folder_id === activeFolder!.folder_id : !f.folder_id
            );
            const apiUrl = getStaticBaseUrl();
            return (
              <div className="space-y-5">
                {/* Header: folder name (or Files), count, rename + delete for folders */}
                <div className="group rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                        {activeFolder ? (
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                          </svg>
                        ) : (
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            <path d="M13 3v5a2 2 0 002 2h5" />
                          </svg>
                        )}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2.5">
                          <h3 className="truncate text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">
                            {activeFolder ? activeFolder.name : 'Files'}
                          </h3>
                          {activeFolder?.hidden && <HiddenPill label="Hidden from students" />}
                        </div>
                        <p className="mt-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                          {filesInView.length} {filesInView.length === 1 ? 'file' : 'files'}
                        </p>
                      </div>
                    </div>
                    {activeFolder && (
                      <div className="flex shrink-0 items-center gap-1.5">
                        {renderVisibilityToggle(
                          'folder',
                          activeFolder.folder_id,
                          !!activeFolder.hidden,
                          'flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-secondary-700 dark:hover:text-gray-200',
                          'Hide from students (also hides every file inside this folder)'
                        )}
                        <button
                          onClick={() => openRenameFolderModal(activeFolder)}
                          title="Rename folder"
                          className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-secondary-700 dark:hover:text-gray-200"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setFolderToDelete(activeFolder)}
                          title="Delete folder"
                          className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Upload block */}
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Upload to {activeFolder ? activeFolder.name : 'Files'}
                  </p>
                  {uploadingFileName ? (
                    <div className="mt-3">
                      <p className="truncate text-sm font-semibold text-gray-700 dark:text-gray-200">Uploading {uploadingFileName}...</p>
                      <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-secondary-700">
                        <div
                          className="h-2 rounded-full bg-primary-600 transition-all duration-200"
                          style={{ width: `${fileUploadProgress}%` }}
                        />
                      </div>
                      <p className="mt-1.5 text-xs font-bold text-primary-600 dark:text-primary-400">{fileUploadProgress}%</p>
                    </div>
                  ) : (
                    <div className="mt-3 space-y-3">
                      <input
                        type="text"
                        value={fileTitleInput}
                        onChange={(e) => setFileTitleInput(e.target.value)}
                        placeholder="Title (optional, uses the file name if empty)"
                        className="block w-full rounded-2xl border-2 border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white dark:placeholder-secondary-400"
                      />
                      <input
                        ref={materialsFileInputRef}
                        type="file"
                        onChange={handleCourseFileSelected}
                        className="block w-full cursor-pointer rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-500 transition-colors hover:border-primary-400 hover:bg-primary-50/40 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700/50 dark:text-gray-400 file:mr-4 file:cursor-pointer file:rounded-full file:border-0 file:bg-primary-600 file:px-4 file:py-2 file:text-sm file:font-bold file:text-gray-900 hover:file:bg-primary-700"
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Any file type works: zip, pdf, docx, images, and more.</p>
                        <button
                          onClick={() => { setLinkForm({ url: '', title: '' }); setLinkError(''); setIsAddLinkModalOpen(true); }}
                          className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border-2 border-gray-200 px-4 py-1.5 text-xs font-bold text-gray-600 transition-colors hover:border-primary-300 hover:text-primary-700 dark:border-secondary-600 dark:text-gray-300"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          Add link
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Inline embedded view for a selected link */}
                {selectedEmbedLink && selectedEmbedLink.url ? (
                  <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                    <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-3.5 dark:border-secondary-700">
                      <button
                        onClick={() => setSelectedEmbedLink(null)}
                        className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border-2 border-gray-200 px-3.5 py-1.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-secondary-600 dark:text-gray-300 dark:hover:bg-secondary-700 dark:hover:text-white"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                      </button>
                      <h3 className="min-w-0 flex-1 truncate text-base font-extrabold tracking-tight text-gray-900 dark:text-white">
                        {selectedEmbedLink.title}
                      </h3>
                      <a
                        href={selectedEmbedLink.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-primary-600 px-4 py-2 text-xs font-bold text-gray-900 shadow-[0_3px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                      >
                        Open in new tab
                      </a>
                    </div>
                    <iframe
                      src={analyzeLink(selectedEmbedLink.url).embedUrl || selectedEmbedLink.url}
                      title={selectedEmbedLink.title}
                      sandbox="allow-scripts allow-same-origin allow-forms allow-presentation allow-popups"
                      className="h-[75vh] w-full bg-white"
                    />
                  </div>
                ) : filesInView.length === 0 ? (
                  <p className="px-2 py-6 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                    No files here yet. Upload the first one above.
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                    <div className="divide-y divide-gray-100 dark:divide-secondary-700">
                      {filesInView.map((file) => {
                        const glyph = getFileGlyph(file.file_name);
                        const isLink = file.kind === 'link';
                        const linkInfo = isLink && file.url ? analyzeLink(file.url) : null;
                        return (
                          <div key={file.file_id} className={`group flex items-center gap-4 px-5 py-4 ${file.hidden ? 'opacity-60' : ''}`}>
                            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isLink ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' : glyph.tint}`}>
                              {isLink ? (
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                              ) : glyph.icon}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex min-w-0 items-center gap-2">
                                {isLink && linkInfo?.embedUrl ? (
                                  <button
                                    onClick={() => setSelectedEmbedLink(file)}
                                    className="truncate text-left text-sm font-bold text-gray-900 transition-colors hover:text-primary-700 focus:outline-none dark:text-white dark:hover:text-primary-300"
                                    title="Open embedded view"
                                  >
                                    {file.title}
                                  </button>
                                ) : (
                                  <p className="truncate text-sm font-bold text-gray-900 dark:text-white">{file.title}</p>
                                )}
                                {file.hidden && <HiddenPill />}
                              </div>
                              <div className="mt-0.5 flex min-w-0 items-center gap-2">
                                <p className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">
                                  {isLink
                                    ? `${linkInfo?.provider || 'Link'} · ${file.url}`
                                    : `${file.file_name} · ${formatFileSize(file.file_size)} · ${new Date(file.uploaded_at).toLocaleDateString()}`}
                                </p>
                                {file.text_status === 'ready' && (
                                  <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300">Ready for quizzes</span>
                                )}
                                {(file.text_status === 'pending' || !file.text_status) && (
                                  <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/25 dark:text-amber-300">Processing</span>
                                )}
                                {(file.text_status === 'unsupported' || file.text_status === 'failed') && (
                                  <span title={file.text_error || undefined} className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">Not quiz eligible</span>
                                )}
                                {isLink && linkInfo?.embedUrl && (
                                  <span className="shrink-0 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700 dark:bg-sky-900/25 dark:text-sky-300">Embeddable</span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => setPreviewFile(file)}
                              title="Preview file"
                              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-all hover:bg-primary-50 hover:text-primary-700 opacity-0 group-hover:opacity-100 dark:hover:bg-primary-900/20 dark:hover:text-primary-300"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>
                            {file.text_status !== 'unsupported' && file.text_status !== 'failed' && (
                              <button
                                onClick={() => {
                                  setQuizForm(prev => ({
                                    ...prev,
                                    selectedFiles: [file.file_id],
                                    title: prev.title || `Quiz: ${file.title || file.file_name}`,
                                  }));
                                  setIsGenerateQuizModalOpen(true);
                                }}
                                title="Generate quiz from this document"
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-all hover:bg-primary-50 hover:text-primary-700 opacity-0 group-hover:opacity-100 dark:hover:bg-primary-900/20 dark:hover:text-primary-300"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                  <path d="M12 11v6m-3-3h6" />
                                </svg>
                              </button>
                            )}
                            <a
                              href={isLink ? (file.url || '#') : `${apiUrl}${file.file_path}`}
                              {...(isLink ? {} : { download: file.file_name })}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-primary-600 px-4 py-2 text-xs font-bold text-gray-900 shadow-[0_3px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {isLink
                                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />}
                              </svg>
                              {isLink ? 'Open' : 'Download'}
                            </a>
                            {renderVisibilityToggle(
                              'file',
                              file.file_id,
                              !!file.hidden,
                              `flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-700 ${file.hidden ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} dark:hover:bg-secondary-700 dark:hover:text-gray-200`
                            )}
                            <button
                              onClick={() => setFileToDelete(file.file_id)}
                              title="Delete file"
                              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
                    </div>
                  </div>
            </div>
          )}

          {/* === ASSESSMENTS TAB === */}
          {activeTab === 'assessments' && (
            <div className="space-y-6">
              {/* Sub-pills: Quizzes | Scaffolds | Assignments */}
              <div className="inline-flex rounded-full bg-gray-100 p-1 dark:bg-secondary-800">
                {([
                  { id: 'quizzes' as AssessmentsTab, label: 'Quizzes' },
                  { id: 'scaffolds' as AssessmentsTab, label: 'Quiz generator' },
                  { id: 'assignments' as AssessmentsTab, label: 'Assignments' },
                ]).map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => setAssessmentsTab(sub.id)}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-all ${
                      assessmentsTab === sub.id
                        ? 'bg-white text-primary-700 shadow-sm dark:bg-secondary-700 dark:text-white'
                        : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    {sub.label}
                  </button>
                ))}
              </div>

          {/* Quizzes sub-tab */}
          {assessmentsTab === 'quizzes' && (
            <>
              {quizzesLoading || quizDetailLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                </div>
              ) : viewingQuiz ? (
                /* Quiz workspace: header card + internal pill nav (Questions | Submissions | Settings) */
                <div className="space-y-4">
                  {/* Clean header card: back, title, combined status, quiet schedule, ONE primary action + overflow */}
                  <div className="sticky top-0 z-10 rounded-2xl border border-gray-200 bg-white p-4 shadow-soft dark:border-secondary-700 dark:bg-secondary-800 sm:p-5">
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        onClick={() => { setViewingQuiz(null); setEditingQuestions([]); setExpandedQuestions({}); setQuestionPointsDrafts({}); resetQuizReviewState(); }}
                        className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border-2 border-gray-200 px-3.5 py-1.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-secondary-600 dark:text-gray-300 dark:hover:bg-secondary-700 dark:hover:text-white"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        All quizzes
                      </button>
                      <div className="flex min-w-0 flex-1 items-center gap-2.5">
                        <h2 className="truncate text-lg font-extrabold text-gray-900 dark:text-white sm:text-xl">{viewingQuiz.title}</h2>
                        {(() => {
                          const status = getQuizStatus(viewingQuiz);
                          return (
                            <span className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${status.pill}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                              {status.label}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2">
                        {/* ONE contextual primary action: Publish for drafts, Release all when grades are waiting */}
                        {!viewingQuiz.published ? (
                          <Button
                            onClick={() => openScheduleModal('publish')}
                            loading={quizPublishing}
                            size="sm"
                          >
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Publish
                            </div>
                          </Button>
                        ) : quizHasUnreleasedAttempts ? (
                          <button
                            onClick={handleReleaseAllQuizGrades}
                            disabled={releasingQuizGradesAll}
                            className="inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-4 py-2 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {releasingQuizGradesAll ? 'Releasing...' : 'Release all grades'}
                          </button>
                        ) : null}
                        {/* Overflow menu: Edit schedule, Unpublish, Delete */}
                        <div className="relative" ref={quizMenuRef}>
                          <button
                            onClick={() => setQuizMenuOpen((v) => !v)}
                            title="More actions"
                            aria-label="More actions"
                            aria-expanded={quizMenuOpen}
                            className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-gray-200 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-secondary-600 dark:text-gray-400 dark:hover:bg-secondary-700 dark:hover:text-white"
                          >
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 5.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 5.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
                            </svg>
                          </button>
                          {quizMenuOpen && (
                            <div className="absolute right-0 top-11 z-20 w-44 overflow-hidden rounded-2xl border border-gray-200 bg-white py-1.5 shadow-medium dark:border-secondary-600 dark:bg-secondary-800">
                              <button
                                onClick={() => { setQuizMenuOpen(false); openScheduleModal('edit'); }}
                                className="block w-full px-4 py-2 text-left text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-secondary-700 dark:hover:text-white"
                              >
                                Edit schedule
                              </button>
                              {viewingQuiz.published && (
                                <button
                                  onClick={() => { setQuizMenuOpen(false); handleUnpublishQuiz(); }}
                                  className="block w-full px-4 py-2 text-left text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-secondary-700 dark:hover:text-white"
                                >
                                  Unpublish
                                </button>
                              )}
                              <button
                                onClick={() => { setQuizMenuOpen(false); setQuizToDelete(viewingQuiz.quiz_id); }}
                                className="block w-full px-4 py-2 text-left text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                              >
                                Delete quiz
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Quiet schedule meta */}
                    <p className="mt-2.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                      {viewingQuiz.available_from || viewingQuiz.due_at ? (
                        <>
                          {viewingQuiz.available_from && `Opens ${formatScheduleDateTime(viewingQuiz.available_from)}`}
                          {viewingQuiz.available_from && viewingQuiz.due_at && ' · '}
                          {viewingQuiz.due_at && `Due ${formatScheduleDateTime(viewingQuiz.due_at)}`}
                        </>
                      ) : (
                        'No schedule set'
                      )}
                    </p>
                    {/* Internal workspace pills */}
                    <div className="mt-3 inline-flex rounded-full bg-gray-100 p-1 dark:bg-secondary-700">
                      {([
                        { id: 'questions' as const, label: 'Questions' },
                        { id: 'submissions' as const, label: `Submissions (${quizAttempts.length})` },
                        { id: 'settings' as const, label: 'Settings' },
                      ]).map((pill) => (
                        <button
                          key={pill.id}
                          onClick={() => { setViewingAttempt(null); setQuizSubView(pill.id); }}
                          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-bold transition-all ${
                            quizSubView === pill.id
                              ? 'bg-white text-primary-700 shadow-sm dark:bg-secondary-600 dark:text-white'
                              : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                          }`}
                        >
                          {pill.label}
                          {pill.id === 'submissions' && quizHasUnreleasedAttempts && (
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" title="Grades waiting to be released" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Quiet summary strip: total questions + per-Bloom-level chips */}
                  {quizSubView === 'questions' && (() => {
                    const bloomCounts = editingQuestions.reduce<Record<number, number>>((acc, q) => {
                      const lvl = getQuestionBloom(q);
                      if (lvl) acc[lvl] = (acc[lvl] || 0) + 1;
                      return acc;
                    }, {});
                    const levels = Object.keys(bloomCounts).map(Number).sort((a, b) => a - b);
                    const totalPoints = editingQuestions.reduce((sum, q) => sum + (q.points ?? 1), 0);
                    return (
                      <div className="flex flex-wrap items-center gap-2 px-1">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          {editingQuestions.length} question{editingQuestions.length !== 1 ? 's' : ''}
                        </span>
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          · Total: {formatPoints(totalPoints)} points
                        </span>
                        {levels.map((level) => {
                          const c = BLOOM_COLORS[level];
                          return (
                            <span
                              key={level}
                              title={`Level ${level}: ${BLOOM_LABELS[level]}`}
                              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${c.bg} ${c.text} ${c.darkBg} ${c.darkText}`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
                              {bloomCounts[level]} {BLOOM_LABELS[level]}
                            </span>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Questions List: numbered collapsible cards (first expanded by default) */}
                  {quizSubView === 'questions' && (
                  <div className="space-y-3">
                    {editingQuestions.map((q, idx) => {
                      const isExpanded = expandedQuestions[q.question_id] ?? idx === 0;
                      const bloomLevel = getQuestionBloom(q);
                      const bloomColors = bloomLevel ? BLOOM_COLORS[bloomLevel] : null;
                      return (
                        <div
                          key={q.question_id}
                          className="group rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800"
                        >
                          {/* Collapsed header row */}
                          <div className="flex items-center gap-2 p-4">
                            <button
                              onClick={() => setExpandedQuestions((prev) => ({ ...prev, [q.question_id]: !isExpanded }))}
                              className="flex min-w-0 flex-1 items-center gap-3 text-left"
                              title={isExpanded ? 'Collapse question' : 'Expand question'}
                            >
                              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-extrabold text-gray-600 dark:bg-secondary-700 dark:text-gray-300">
                                {idx + 1}
                              </span>
                              {bloomColors && bloomLevel && (
                                <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${bloomColors.bg} ${bloomColors.text} ${bloomColors.darkBg} ${bloomColors.darkText}`}>
                                  L{bloomLevel} · {BLOOM_LABELS[bloomLevel]}
                                </span>
                              )}
                              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900 dark:text-white">
                                {q.question_text}
                              </span>
                              <span className="flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                                {formatPoints(q.points ?? 1)} pt{(q.points ?? 1) === 1 ? '' : 's'}
                              </span>
                              <span className="hidden flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400 sm:inline">
                                {q.question_type === 'open_ended' ? 'Open ended' : 'Multiple choice'}
                              </span>
                              <svg
                                className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none" stroke="currentColor" viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {/* Hover-reveal remove */}
                            <button
                              onClick={() => handleRemoveQuestion(q.question_id)}
                              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-300 opacity-0 transition-all hover:bg-red-50 hover:text-red-500 focus-visible:opacity-100 group-hover:opacity-100 dark:hover:bg-red-900/20"
                              title="Remove this question"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>

                          {/* Expanded body */}
                          {isExpanded && (
                            <div className="space-y-3 border-t border-gray-100 px-4 pb-4 pt-3 dark:border-secondary-700 sm:px-5">
                              <p className="text-sm font-medium leading-relaxed text-gray-900 dark:text-white">
                                {q.question_text}
                              </p>

                              {/* Point allocation */}
                              <div className="flex items-center gap-2">
                                <label
                                  htmlFor={`question-points-${q.question_id}`}
                                  className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                >
                                  Points
                                </label>
                                <input
                                  id={`question-points-${q.question_id}`}
                                  type="number"
                                  min={0}
                                  step={0.5}
                                  value={questionPointsDrafts[q.question_id] ?? String(q.points ?? 1)}
                                  onChange={(e) => handleQuestionPointsChange(q.question_id, e.target.value)}
                                  onBlur={() => setQuestionPointsDrafts((prev) => {
                                    const next = { ...prev };
                                    delete next[q.question_id];
                                    return next;
                                  })}
                                  className="w-20 rounded-xl border-2 border-gray-200 bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white"
                                />
                              </div>

                              {q.question_type === 'open_ended' ? (
                                /* Open-ended: show rubric preview */
                                <div className="space-y-2">
                                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                                    Students will answer with free text and self-assess using the rubric after submission.
                                  </p>
                                  {q.rubric?.dimensions && (
                                    <div className="space-y-2">
                                      {q.rubric.dimensions.map((dim, di) => (
                                        <div key={di} className="text-sm border-2 border-gray-100 dark:border-secondary-600 rounded-2xl overflow-hidden">
                                          <div className="bg-gray-50 dark:bg-secondary-700 px-3 py-1.5 font-medium text-gray-700 dark:text-gray-300 text-xs">
                                            {dim.name}
                                          </div>
                                          <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-secondary-600">
                                            <div className="p-2">
                                              <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-0.5">Beginning</p>
                                              <p className="text-xs text-gray-600 dark:text-gray-500">{dim.beginning}</p>
                                            </div>
                                            <div className="p-2">
                                              <p className="text-xs font-medium text-yellow-600 dark:text-yellow-400 mb-0.5">Developing</p>
                                              <p className="text-xs text-gray-600 dark:text-gray-500">{dim.developing}</p>
                                            </div>
                                            <div className="p-2">
                                              <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-0.5">Proficient</p>
                                              <p className="text-xs text-gray-600 dark:text-gray-500">{dim.proficient}</p>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {q.rubric?.exemplar_response && (
                                    <div className="p-2.5 bg-green-50 dark:bg-green-900/20 rounded-2xl border-2 border-green-100 dark:border-green-800">
                                      <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-1">Exemplar Response:</p>
                                      <p className="text-xs text-green-800 dark:text-green-300 whitespace-pre-wrap">{q.rubric.exemplar_response}</p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                /* MCQ: show options with correct-answer marking */
                                <div className="space-y-2">
                                  {q.options?.map((opt) => (
                                    <div
                                      key={opt.label}
                                      className={`flex items-center gap-3 p-2.5 rounded-2xl text-sm border-2 ${
                                        opt.label === q.correct_answer
                                          ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                                          : 'bg-gray-50 border-gray-100 dark:bg-secondary-700 dark:border-secondary-600'
                                      }`}
                                    >
                                      <span className={`font-semibold w-6 ${
                                        opt.label === q.correct_answer ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
                                      }`}>{opt.label}.</span>
                                      <span className={opt.label === q.correct_answer ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}>
                                        {opt.text}
                                      </span>
                                      {opt.label === q.correct_answer && (
                                        <svg className="w-4 h-4 text-green-500 ml-auto flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Explanation */}
                              {q.explanation && (
                                <div className="text-sm text-gray-500 dark:text-gray-400 italic bg-gray-50 dark:bg-secondary-700/50 p-3 rounded-2xl">
                                  <span className="font-medium not-italic text-gray-600 dark:text-gray-300">Explanation: </span>
                                  {q.explanation}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  )}

                  {quizSubView === 'questions' && editingQuestions.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      All questions have been removed. This quiz cannot be published without questions.
                    </div>
                  )}

                  {/* Sticky save bar: appears in the Questions section whenever the draft is dirty */}
                  {quizSubView === 'questions' && quizHasUnsavedChanges && (
                    <div className="sticky bottom-4 z-10 flex justify-end">
                      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-orange-200 bg-white px-4 py-3 shadow-medium dark:border-orange-900/50 dark:bg-secondary-800">
                        <p className="text-sm font-medium text-orange-500">
                          {quizQuestionsRemovedCount > 0
                            ? `${quizQuestionsRemovedCount} question${quizQuestionsRemovedCount !== 1 ? 's' : ''} removed${quizPointsChanged ? ' and point values changed' : ''}.`
                            : 'Point values changed.'}
                        </p>
                        <Button onClick={handleSaveQuiz} loading={quizSaving} size="sm">
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Submissions review sub-view */}
                  {quizSubView === 'submissions' && (
                    <div className="space-y-4">
                      {quizAttemptsLoading || attemptDetailLoading ? (
                        <div className="flex justify-center py-12">
                          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                        </div>
                      ) : viewingAttempt ? (
                        /* Attempt detail: per-question review + feedback + release */
                        <div className="space-y-4">
                          <div className="flex flex-wrap items-center gap-3">
                            <button
                              onClick={() => setViewingAttempt(null)}
                              className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border-2 border-gray-200 px-3.5 py-1.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-secondary-600 dark:text-gray-300 dark:hover:bg-secondary-700 dark:hover:text-white"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                              </svg>
                              All submissions
                            </button>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-base font-extrabold text-gray-900 dark:text-white">
                                {viewingAttempt.full_name}
                                <span className="ml-2 text-sm font-medium text-gray-500 dark:text-gray-400">@{viewingAttempt.student_username}</span>
                              </p>
                              <p className="mt-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                                Submitted {formatScheduleDateTime(viewingAttempt.completed_at)} · <span className={`font-bold ${gradeBandClass(viewingAttempt.percentage)}`}>{formatPoints(viewingAttempt.earned_points)} / {formatPoints(viewingAttempt.total_points)} points ({Math.round(viewingAttempt.percentage)}%)</span>
                              </p>
                            </div>
                            <span className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                              viewingAttempt.released
                                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'
                                : 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300'
                            }`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${viewingAttempt.released ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                              {viewingAttempt.released ? 'Released' : 'Pending'}
                            </span>
                          </div>

                          {/* Per-question responses */}
                          <div className="space-y-3">
                            {viewingAttempt.results.map((r, idx) => {
                              const maxPts = r.points ?? 1;
                              const awardedPts = r.awarded ?? 0;
                              const needsScoring = r.question_type === 'open_ended' && !r.adjusted;
                              const isEditingScore = scoreEditQid === r.question_id;
                              const startScoreEdit = () => {
                                setScoreEditQid(r.question_id);
                                setScoreDraft(formatPoints(awardedPts));
                              };
                              return (
                              <div key={r.question_id} className="group rounded-2xl border border-gray-200 bg-white p-4 shadow-soft dark:border-secondary-700 dark:bg-secondary-800 sm:p-5">
                                <div className="flex items-start gap-3">
                                  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-extrabold text-gray-600 dark:bg-secondary-700 dark:text-gray-300">
                                    {idx + 1}
                                  </span>
                                  <div className="min-w-0 flex-1 space-y-2.5">
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                      <p className="text-sm font-semibold leading-relaxed text-gray-900 dark:text-white">{r.question_text}</p>
                                      <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5">
                                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                                          {r.question_type === 'open_ended' ? 'Open ended' : 'Multiple choice'}
                                        </span>
                                        {isEditingScore ? (
                                          /* Inline score editor: number input + mini Save/Cancel */
                                          <span className="flex items-center gap-1.5">
                                            <input
                                              type="number"
                                              min={0}
                                              max={maxPts}
                                              step={0.5}
                                              value={scoreDraft}
                                              onChange={(e) => setScoreDraft(e.target.value)}
                                              disabled={scoreSaving}
                                              autoFocus
                                              className="w-16 rounded-xl border-2 border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-900 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:cursor-not-allowed disabled:opacity-60 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white"
                                            />
                                            <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">/ {formatPoints(maxPts)} pts</span>
                                            <button
                                              onClick={handleSaveQuestionScore}
                                              disabled={scoreSaving}
                                              className="rounded-full bg-primary-600 px-2.5 py-1 text-[11px] font-bold text-gray-900 shadow-[0_2px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:cursor-not-allowed disabled:opacity-60"
                                            >
                                              {scoreSaving ? 'Saving...' : 'Save'}
                                            </button>
                                            <button
                                              onClick={() => setScoreEditQid(null)}
                                              disabled={scoreSaving}
                                              className="rounded-full border-2 border-gray-200 px-2.5 py-0.5 text-[11px] font-bold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-secondary-600 dark:text-gray-300 dark:hover:bg-secondary-700 dark:hover:text-white"
                                            >
                                              Cancel
                                            </button>
                                          </span>
                                        ) : (
                                          <>
                                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                                              {formatPoints(awardedPts)} / {formatPoints(maxPts)} pts
                                            </span>
                                            {r.adjusted && (
                                              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                                                Adjusted
                                              </span>
                                            )}
                                            {needsScoring && (
                                              <button
                                                onClick={startScoreEdit}
                                                title="Score this response"
                                                className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700 transition-colors hover:bg-amber-100 dark:bg-amber-900/25 dark:text-amber-300 dark:hover:bg-amber-900/40"
                                              >
                                                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                                                Needs scoring
                                              </button>
                                            )}
                                            <button
                                              onClick={startScoreEdit}
                                              title="Adjust score"
                                              className={`flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-secondary-700 dark:hover:text-gray-300 ${
                                                r.question_type === 'open_ended' ? '' : 'opacity-0 focus-visible:opacity-100 group-hover:opacity-100'
                                              }`}
                                            >
                                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                                <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                              </svg>
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    {r.question_type === 'open_ended' ? (
                                      <>
                                        <div className="rounded-2xl border-2 border-gray-100 bg-gray-50 p-3 dark:border-secondary-600 dark:bg-secondary-700/50">
                                          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Student response</p>
                                          <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                                            {r.response_text?.trim() ? r.response_text : 'No response'}
                                          </p>
                                        </div>
                                        {r.rubric?.dimensions && r.rubric.dimensions.length > 0 && (
                                          <div className="space-y-2">
                                            {r.rubric.dimensions.map((dim, di) => (
                                              <div key={di} className="overflow-hidden rounded-2xl border-2 border-gray-100 text-sm dark:border-secondary-600">
                                                <div className="bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700 dark:bg-secondary-700 dark:text-gray-300">
                                                  {dim.name}
                                                </div>
                                                <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-secondary-600">
                                                  <div className="p-2">
                                                    <p className="mb-0.5 text-xs font-medium text-red-600 dark:text-red-400">Beginning</p>
                                                    <p className="text-xs text-gray-600 dark:text-gray-500">{dim.beginning}</p>
                                                  </div>
                                                  <div className="p-2">
                                                    <p className="mb-0.5 text-xs font-medium text-yellow-600 dark:text-yellow-400">Developing</p>
                                                    <p className="text-xs text-gray-600 dark:text-gray-500">{dim.developing}</p>
                                                  </div>
                                                  <div className="p-2">
                                                    <p className="mb-0.5 text-xs font-medium text-green-600 dark:text-green-400">Proficient</p>
                                                    <p className="text-xs text-gray-600 dark:text-gray-500">{dim.proficient}</p>
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        {r.rubric?.exemplar_response && (
                                          <div className="rounded-2xl border-2 border-green-100 bg-green-50 p-2.5 dark:border-green-800 dark:bg-green-900/20">
                                            <p className="mb-1 text-xs font-medium text-green-700 dark:text-green-400">Exemplar Response:</p>
                                            <p className="whitespace-pre-wrap text-xs text-green-800 dark:text-green-300">{r.rubric.exemplar_response}</p>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      /* MCQ: student answer vs correct */
                                      <div className="space-y-1.5">
                                        <div className={`flex items-center gap-3 rounded-2xl border-2 p-2.5 text-sm ${
                                          r.is_correct
                                            ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                                            : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                                        }`}>
                                          <span className={`text-xs font-bold uppercase tracking-wide ${r.is_correct ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            Student answer
                                          </span>
                                          <span className={r.is_correct ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                                            {r.selected || 'No answer'}
                                          </span>
                                          {r.is_correct ? (
                                            <svg className="ml-auto h-4 w-4 flex-shrink-0 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                          ) : (
                                            <svg className="ml-auto h-4 w-4 flex-shrink-0 text-red-500" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" viewBox="0 0 24 24">
                                              <path d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                          )}
                                        </div>
                                        {!r.is_correct && r.correct && (
                                          <div className="flex items-center gap-3 rounded-2xl border-2 border-green-200 bg-green-50 p-2.5 text-sm dark:border-green-800 dark:bg-green-900/20">
                                            <span className="text-xs font-bold uppercase tracking-wide text-green-600 dark:text-green-400">Correct answer</span>
                                            <span className="text-green-700 dark:text-green-300">{r.correct}</span>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {r.explanation && (
                                      <div className="rounded-2xl bg-gray-50 p-3 text-sm italic text-gray-500 dark:bg-secondary-700/50 dark:text-gray-400">
                                        <span className="font-medium not-italic text-gray-600 dark:text-gray-300">Explanation: </span>
                                        {r.explanation}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              );
                            })}
                          </div>

                          {/* Feedback + release controls */}
                          <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-soft dark:border-secondary-700 dark:bg-secondary-800 sm:p-5">
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">
                              Feedback for {viewingAttempt.full_name}
                            </label>
                            <textarea
                              value={attemptFeedbackDraft}
                              onChange={(e) => setAttemptFeedbackDraft(e.target.value)}
                              rows={3}
                              placeholder="Write feedback the student will see with their grade"
                              className="block w-full rounded-2xl border-2 border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 transition-colors duration-200 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white dark:placeholder-secondary-400"
                            />
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                onClick={handleSaveAttemptFeedback}
                                disabled={attemptFeedbackSaving}
                                className="inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-4 py-2 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {attemptFeedbackSaving ? 'Saving...' : 'Save feedback'}
                              </button>
                              <button
                                onClick={handleToggleAttemptRelease}
                                disabled={attemptReleaseSaving}
                                className="inline-flex items-center gap-1.5 rounded-full border-2 border-gray-200 px-4 py-2 text-sm font-bold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-secondary-600 dark:text-gray-300 dark:hover:bg-secondary-700 dark:hover:text-white"
                              >
                                {attemptReleaseSaving
                                  ? 'Updating...'
                                  : viewingAttempt.released
                                    ? 'Unrelease'
                                    : 'Release to student'}
                              </button>
                            </div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                              Need to adjust the score? Open the{' '}
                              <button
                                onClick={() => { setActiveTab('grades'); openGradebookStudent(viewingAttempt.student_username); }}
                                className="font-bold text-primary-700 hover:underline dark:text-primary-300"
                              >
                                Gradebook drill-in
                              </button>{' '}
                              for this student.
                            </p>
                          </div>
                        </div>
                      ) : quizAttempts.length === 0 ? (
                        <div className="rounded-2xl border border-gray-200 bg-white px-6 py-12 text-center shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                          <h3 className="text-base font-bold text-gray-900 dark:text-white">No submissions yet</h3>
                          <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                            Student attempts will show up here once they take this quiz
                          </p>
                        </div>
                      ) : (
                        /* Attempts table */
                        <>
                          <div className="flex flex-wrap items-center justify-between gap-3 px-1">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              {quizAttempts.length} submission{quizAttempts.length !== 1 ? 's' : ''}
                            </span>
                            <button
                              onClick={handleReleaseAllQuizGrades}
                              disabled={releasingQuizGradesAll}
                              className="inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-4 py-2 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {releasingQuizGradesAll ? 'Releasing...' : 'Release all grades'}
                            </button>
                          </div>
                          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200 dark:divide-secondary-700">
                                <thead className="bg-gray-50 dark:bg-secondary-700/50">
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Student</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Submitted</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Score</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Status</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                      <span className="sr-only">Actions</span>
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-secondary-700">
                                  {quizAttempts.map((attempt) => (
                                    <tr key={attempt.attempt_id} className="transition-colors hover:bg-primary-50/50 dark:hover:bg-secondary-700/50">
                                      <td className="whitespace-nowrap px-6 py-3.5">
                                        <p className="text-sm font-bold text-gray-900 dark:text-white">{attempt.full_name}</p>
                                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">@{attempt.student_username}</p>
                                      </td>
                                      <td className="whitespace-nowrap px-4 py-3.5 text-sm font-medium text-gray-500 dark:text-gray-400">
                                        {new Date(attempt.completed_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                      </td>
                                      <td className="whitespace-nowrap px-4 py-3.5">
                                        <span className={`text-sm font-bold ${gradeBandClass(attempt.percentage)}`}>
                                          {attempt.score}/{attempt.total} · {Math.round(attempt.percentage)}%
                                        </span>
                                        {attempt.open_ended_count > 0 && (
                                          <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                                            {attempt.open_ended_count} open ended
                                          </p>
                                        )}
                                      </td>
                                      <td className="whitespace-nowrap px-4 py-3.5">
                                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                                          attempt.released
                                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'
                                            : 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300'
                                        }`}>
                                          <span className={`h-1.5 w-1.5 rounded-full ${attempt.released ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                                          {attempt.released ? 'Released' : 'Pending'}
                                        </span>
                                      </td>
                                      <td className="whitespace-nowrap px-4 py-3.5 text-right">
                                        <button
                                          onClick={() => handleOpenAttempt(attempt.attempt_id)}
                                          className="inline-flex items-center rounded-full border-2 border-gray-200 px-3.5 py-1.5 text-xs font-bold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-secondary-600 dark:text-gray-300 dark:hover:bg-secondary-700 dark:hover:text-white"
                                        >
                                          Review
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Settings sub-view: schedule, publishing, and the danger row */}
                  {quizSubView === 'settings' && (
                    <div className="space-y-4">
                      {/* Schedule */}
                      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-soft dark:border-secondary-700 dark:bg-secondary-800 sm:p-5">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Schedule</h3>
                            <p className="mt-1 text-sm font-medium text-gray-500 dark:text-gray-400">
                              {viewingQuiz.available_from || viewingQuiz.due_at ? (
                                <>
                                  {viewingQuiz.available_from && `Opens ${formatScheduleDateTime(viewingQuiz.available_from)}`}
                                  {viewingQuiz.available_from && viewingQuiz.due_at && ' · '}
                                  {viewingQuiz.due_at && `Due ${formatScheduleDateTime(viewingQuiz.due_at)}`}
                                </>
                              ) : (
                                'No schedule set. The quiz opens immediately once published and has no deadline.'
                              )}
                            </p>
                          </div>
                          <button
                            onClick={() => openScheduleModal('edit')}
                            className="inline-flex flex-shrink-0 items-center rounded-full border-2 border-gray-200 px-3.5 py-1.5 text-sm font-bold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-secondary-600 dark:text-gray-300 dark:hover:bg-secondary-700 dark:hover:text-white"
                          >
                            Edit schedule
                          </button>
                        </div>
                      </div>

                      {/* Publishing */}
                      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-soft dark:border-secondary-700 dark:bg-secondary-800 sm:p-5">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Publishing</h3>
                            <p className="mt-1 text-sm font-medium text-gray-500 dark:text-gray-400">
                              {viewingQuiz.published
                                ? 'This quiz is published. Students can see it according to the schedule above.'
                                : 'This quiz is a draft. Students cannot see it until you publish.'}
                            </p>
                          </div>
                          {viewingQuiz.published ? (
                            <Button onClick={handleUnpublishQuiz} loading={quizPublishing} variant="secondary" size="sm">
                              Unpublish
                            </Button>
                          ) : (
                            <Button onClick={() => openScheduleModal('publish')} loading={quizPublishing} size="sm">
                              Publish
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Danger row */}
                      <div className="rounded-2xl border border-red-200 bg-white p-4 shadow-soft dark:border-red-900/50 dark:bg-secondary-800 sm:p-5">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="text-sm font-bold text-red-600 dark:text-red-400">Delete this quiz</h3>
                            <p className="mt-1 text-sm font-medium text-gray-500 dark:text-gray-400">
                              Removes the quiz and every student attempt. This cannot be undone.
                            </p>
                          </div>
                          <button
                            onClick={() => setQuizToDelete(viewingQuiz.quiz_id)}
                            className="inline-flex flex-shrink-0 items-center rounded-full border-2 border-red-200 px-3.5 py-1.5 text-sm font-bold text-red-600 transition-colors hover:bg-red-50 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-900/20"
                          >
                            Delete quiz
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : quizzes.length === 0 ? (
                <div className="space-y-4">
                  <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                    <Mascot name="bulb" chip="#fb923c" className="h-20 w-20 shadow-medium" blinkDelay={0.7} />
                    <h3 className="mt-5 text-lg font-bold text-gray-900 dark:text-white">No quizzes yet</h3>
                    <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">Generate quizzes from your lectures and slides</p>
                    <div className="mt-6">
                      <Button onClick={() => setIsGenerateQuizModalOpen(true)}>Generate Quiz</Button>
                    </div>
                    <button
                      onClick={() => setAssessmentsTab('scaffolds')}
                      className="mt-3 text-xs font-bold text-primary-700 hover:underline dark:text-primary-300"
                    >
                      or build one in the quiz generator
                    </button>
                  </div>
                </div>
              ) : (
                /* Quiz List View: filter pills + glanceable card grid */
                <div className="space-y-4">
                  {/* Header row: background generation status + generator shortcut */}
                  <div className="flex flex-wrap items-center justify-end gap-3">
                    <button
                      onClick={() => setAssessmentsTab('scaffolds')}
                      className="text-xs font-bold text-primary-700 hover:underline dark:text-primary-300"
                    >
                      New from generator
                    </button>
                  </div>

                  {/* Side index + list */}
                  <div className="flex flex-col gap-4 lg:flex-row">
                    <aside className="w-full flex-shrink-0 lg:w-48">
                      <div className="flex flex-row gap-1 rounded-2xl border border-gray-200 bg-white p-2 shadow-soft dark:border-secondary-700 dark:bg-secondary-800 lg:flex-col">
                        {([
                          { id: 'all' as QuizListFilter, label: 'All quizzes', count: quizzes.length },
                          { id: 'needs_review' as QuizListFilter, label: 'Needs review', count: quizzesNeedingReview },
                          { id: 'drafts' as QuizListFilter, label: 'Drafts', count: quizzes.filter((q) => !q.published).length },
                          { id: 'open' as QuizListFilter, label: 'Open', count: quizzes.filter((q) => getQuizStatus(q).label === 'Open').length },
                        ]).map((f) => (
                          <button
                            key={f.id}
                            onClick={() => setQuizListFilter(f.id)}
                            className={`flex flex-1 items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-xs font-bold transition-colors lg:flex-none ${
                              quizListFilter === f.id
                                ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'
                                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-secondary-700 dark:hover:text-gray-200'
                            }`}
                          >
                            <span className="truncate">{f.label}</span>
                            <span
                              className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-extrabold ${
                                f.id === 'needs_review' && f.count > 0
                                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                  : quizListFilter === f.id
                                    ? 'bg-white text-primary-700 dark:bg-secondary-800 dark:text-primary-300'
                                    : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                              }`}
                            >
                              {f.count}
                            </span>
                          </button>
                        ))}
                      </div>
                    </aside>

                    <div className="min-w-0 flex-1">
                  {filteredQuizzes.length === 0 ? (
                    <div className="rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {quizListFilter === 'needs_review'
                          ? 'Nothing waiting on review. Nice work.'
                          : quizListFilter === 'drafts'
                            ? 'No drafts right now.'
                            : 'No open quizzes right now.'}
                      </p>
                    </div>
                  ) : (
                  <div className="divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:divide-secondary-700/60 dark:border-secondary-700 dark:bg-secondary-800">
                  {filteredQuizzes.map((quiz) => {
                    const status = getQuizStatus(quiz);
                    const now = Date.now();
                    const opensInFuture = !!quiz.available_from && new Date(quiz.available_from).getTime() > now;
                    const dueTime = quiz.due_at ? new Date(quiz.due_at).getTime() : null;
                    const shortDate = (iso: string) =>
                      new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                    const dueClass = dueTime === null
                      ? ''
                      : dueTime < now
                        ? 'text-red-600 dark:text-red-400'
                        : dueTime - now <= 48 * 3_600_000
                          ? 'text-amber-600 dark:text-amber-400'
                          : '';
                    return (
                      <div
                        key={quiz.quiz_id}
                        onClick={() => handleViewQuiz(quiz.quiz_id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewQuiz(quiz.quiz_id); } }}
                        className="group flex cursor-pointer items-center gap-4 px-4 py-3.5 transition-colors hover:bg-primary-50/60 focus:outline-none focus-visible:bg-primary-50/60 dark:hover:bg-secondary-700/40 sm:px-5"
                      >
                        {/* status */}
                        <span className={`hidden w-28 flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold sm:inline-flex ${status.pill}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                          {status.label}
                        </span>

                        {/* title + meta */}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold text-gray-900 dark:text-white">{quiz.title}</p>
                          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                            <span className={`inline-flex items-center gap-1 sm:hidden ${status.pill} rounded-full px-2 py-0.5`}>
                              {status.label}
                            </span>
                            <span>{quiz.num_questions} questions</span>
                            {opensInFuture && <span>Opens {shortDate(quiz.available_from!)}</span>}
                            {dueTime !== null && <span className={dueClass}>Due {shortDate(quiz.due_at!)}</span>}
                          </p>
                        </div>

                        {/* workload signals */}
                        <div className="flex flex-shrink-0 items-center gap-3">
                          <span className="hidden text-xs font-medium text-gray-500 dark:text-gray-400 md:inline">
                            {quiz.attempts_count ?? 0} submitted
                          </span>
                          {(quiz.pending_review ?? 0) > 0 ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700 dark:bg-amber-900/25 dark:text-amber-300">
                              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                              {quiz.pending_review} to review
                            </span>
                          ) : (quiz.attempts_count ?? 0) > 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400" title="All grades released">
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </span>
                          ) : null}
                          <button
                            onClick={(e) => { e.stopPropagation(); setQuizToDelete(quiz.quiz_id); }}
                            title="Delete quiz"
                            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 focus-visible:opacity-100 group-hover:opacity-100 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                          <svg className="h-4 w-4 flex-shrink-0 text-gray-300 transition-colors group-hover:text-primary-600 dark:text-secondary-600" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                  )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Scaffolds sub-tab */}
          {assessmentsTab === 'scaffolds' && (
            <>
              {/* How we generate quizzes helper */}
              {!viewingScaffold && (
                <div className="mb-4 flex justify-end">
                  <div
                    className="relative"
                    onMouseEnter={() => setHowQuizzesOpen(true)}
                    onMouseLeave={() => setHowQuizzesOpen(false)}
                  >
                    <button
                      onClick={() => setHowQuizzesOpen((v) => !v)}
                      className="inline-flex items-center gap-1.5 rounded-full border-2 border-gray-200 px-3.5 py-1.5 text-xs font-bold text-gray-500 transition-colors hover:border-primary-300 hover:text-primary-700 dark:border-secondary-700 dark:text-gray-400 dark:hover:text-primary-300"
                    >
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></svg>
                      How we generate quizzes
                    </button>
                    {howQuizzesOpen && (
                      <div className="absolute right-0 top-full z-20 mt-2 w-[26rem] max-w-[90vw]">
                        <BloomLadderDemo />
                      </div>
                    )}
                  </div>
                </div>
              )}
              {scaffoldsLoading && scaffolds.length === 0 || scaffoldDetailLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                </div>
              ) : viewingScaffold ? (
                /* Scaffold Detail View */
                <div className="space-y-6 animate-fade-in">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => { setViewingScaffold(null); setExpandedExemplar(null); }}
                        className="flex h-10 w-10 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-secondary-800 dark:hover:text-gray-200"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <div>
                        <h2 className="text-xl font-extrabold text-gray-900 dark:text-white">{viewingScaffold.title}</h2>
                        <div className="flex items-center gap-3 mt-1">
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                            viewingScaffold.published
                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'
                              : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                          }`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${viewingScaffold.published ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                            {viewingScaffold.published ? 'Published' : 'Draft'}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {viewingScaffold.questions.length} question{viewingScaffold.questions.length !== 1 ? 's' : ''}
                          </span>
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
                            {viewingScaffold.topic}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {viewingScaffold.type_distribution?.mcq || 0} MCQ · {viewingScaffold.type_distribution?.open_ended || 0} Open-ended
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {viewingScaffold.published ? (
                        <Button onClick={handleUnpublishScaffold} loading={scaffoldPublishing} variant="secondary">
                          Unpublish
                        </Button>
                      ) : (
                        <Button onClick={handlePublishScaffold} loading={scaffoldPublishing}>
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Publish
                          </div>
                        </Button>
                      )}
                      <button
                        onClick={() => setScaffoldToDelete(viewingScaffold.scaffold_id)}
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors"
                        title="Delete scaffold"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Metrics Summary */}
                  {viewingScaffold.metrics && (
                    <div className="bg-white dark:bg-secondary-800 rounded-2xl border border-gray-200 dark:border-secondary-700 shadow-soft p-5">
                      <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">Question Set Quality Metrics</h3>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {[
                          { label: 'Completeness', value: viewingScaffold.metrics.scaffold_completeness },
                          { label: 'Diversity', value: viewingScaffold.metrics.scaffold_diversity },
                          { label: 'Coherence', value: viewingScaffold.metrics.scaffold_coherence },
                          { label: 'Type Transition', value: viewingScaffold.metrics.type_transition_score },
                          { label: 'Monotonicity', value: viewingScaffold.metrics.cognitive_monotonicity },
                        ].map((m) => (
                          <div key={m.label} className="text-center">
                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                              {(m.value * 100).toFixed(0)}%
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{m.label}</div>
                            <div className="mt-1.5 w-full bg-gray-200 dark:bg-secondary-700 rounded-full h-1.5">
                              <div
                                className="bg-primary-600 h-1.5 rounded-full transition-all duration-500"
                                style={{ width: `${m.value * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Bloom Level Progression */}
                  <div className="flex items-center gap-1.5 px-1">
                    {viewingScaffold.questions.map((q, idx) => {
                      const colors = BLOOM_COLORS[q.bloom_level] || BLOOM_COLORS[1];
                      return (
                        <React.Fragment key={q.question_id}>
                          <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text} ${colors.darkBg} ${colors.darkText}`}>
                            L{q.bloom_level}
                          </div>
                          {idx < viewingScaffold.questions.length - 1 && (
                            <svg className="w-4 h-4 text-gray-300 dark:text-secondary-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Questions List */}
                  <div className="space-y-4">
                    {viewingScaffold.questions.map((q, idx) => {
                      const colors = BLOOM_COLORS[q.bloom_level] || BLOOM_COLORS[1];
                      return (
                        <div
                          key={q.question_id}
                          className="bg-white dark:bg-secondary-800 rounded-2xl border border-gray-200 dark:border-secondary-700 shadow-soft p-5 group relative"
                        >
                          {/* Question Header */}
                          <div className="flex items-center gap-2 mb-3">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${colors.bg} ${colors.text} ${colors.darkBg} ${colors.darkText}`}>
                              L{q.bloom_level} · {q.bloom_level_label}
                            </span>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                              q.question_type === 'mcq'
                                ? 'bg-gray-100 text-gray-600 dark:bg-secondary-700 dark:text-gray-400'
                                : 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                            }`}>
                              {q.question_type === 'mcq' ? 'MCQ' : 'Open-ended'}
                            </span>
                            {q.bloom_confidence > 0 && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                                {(q.bloom_confidence * 100).toFixed(0)}% confidence
                              </span>
                            )}
                          </div>

                          {/* Question Text */}
                          <p className="font-medium text-gray-900 dark:text-white">
                            <span className="text-primary-600 mr-2">Q{idx + 1}.</span>
                            {q.question_text}
                          </p>

                          {/* MCQ Options */}
                          {q.question_type === 'mcq' && q.options && (
                            <div className="mt-3 space-y-2 ml-6">
                              {q.options.map((opt) => (
                                <div
                                  key={opt.label}
                                  className={`flex items-center gap-3 p-2.5 rounded-xl text-sm ${
                                    opt.label === q.correct_answer
                                      ? 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800'
                                      : 'bg-gray-50 border border-gray-100 dark:bg-secondary-700 dark:border-secondary-600'
                                  }`}
                                >
                                  <span className={`font-semibold w-6 ${
                                    opt.label === q.correct_answer ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
                                  }`}>{opt.label}.</span>
                                  <span className={opt.label === q.correct_answer ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}>
                                    {opt.text}
                                  </span>
                                  {opt.label === q.correct_answer && (
                                    <svg className="w-4 h-4 text-green-500 ml-auto flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Open-ended Rubric */}
                          {q.question_type === 'open_ended' && q.rubric && (
                            <div className="mt-4 ml-6 space-y-3">
                              {/* Key Concepts */}
                              {q.rubric.key_concepts && q.rubric.key_concepts.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">Key concepts:</span>
                                  {q.rubric.key_concepts.map((concept, i) => (
                                    <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border border-primary-200 dark:border-primary-800">
                                      {concept}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {/* Rubric Dimensions */}
                              <div className="border border-gray-200 dark:border-secondary-600 rounded-xl overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="bg-gray-50 dark:bg-secondary-700">
                                      <th className="text-left px-3 py-2 font-medium text-gray-700 dark:text-gray-300">Dimension</th>
                                      <th className="text-left px-3 py-2 font-medium text-red-600 dark:text-red-400">Beginning</th>
                                      <th className="text-left px-3 py-2 font-medium text-yellow-600 dark:text-yellow-400">Developing</th>
                                      <th className="text-left px-3 py-2 font-medium text-green-600 dark:text-green-400">Proficient</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100 dark:divide-secondary-700">
                                    {q.rubric.dimensions.map((dim, i) => (
                                      <tr key={i}>
                                        <td className="px-3 py-2 font-medium text-gray-900 dark:text-white align-top">{dim.name}</td>
                                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 align-top">{dim.beginning}</td>
                                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 align-top">{dim.developing}</td>
                                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 align-top">{dim.proficient}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              {/* Exemplar Response (Collapsible) */}
                              {q.rubric.exemplar_response && (
                                <div>
                                  <button
                                    onClick={() => setExpandedExemplar(expandedExemplar === q.question_id ? null : q.question_id)}
                                    className="flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                                  >
                                    <svg className={`w-4 h-4 transition-transform ${expandedExemplar === q.question_id ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                    Exemplar Response
                                  </button>
                                  {expandedExemplar === q.question_id && (
                                    <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-xl text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                      {q.rubric.exemplar_response}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Explanation */}
                          {q.explanation && (
                            <div className="mt-3 ml-6 text-sm text-gray-500 dark:text-gray-400 italic bg-gray-50 dark:bg-secondary-700/50 p-3 rounded-xl">
                              <span className="font-medium not-italic text-gray-600 dark:text-gray-300">Explanation: </span>
                              {q.explanation}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : scaffolds.length === 0 ? (
                /* Empty State */
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <Mascot name="apple" chip="#f472b6" className="h-20 w-20 shadow-medium" blinkDelay={1.7} />
                  <h3 className="mt-5 text-lg font-bold text-gray-900 dark:text-white">No generated question sets yet</h3>
                  <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">Generate cognitive scaffolds from your lectures using Bloom's Taxonomy</p>
                  <div className="mt-6">
                    <Button onClick={() => setIsGenerateScaffoldModalOpen(true)}>Generate questions</Button>
                  </div>
                </div>
              ) : (
                /* Scaffold Batch Folder View */
                <div className="space-y-4">
                  {/* Selection action bar */}
                  {selectedScaffolds.size > 0 && (
                    <div className="sticky top-0 z-10 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-2xl p-4 flex items-center justify-between shadow-soft">
                      <span className="text-sm font-bold text-primary-700 dark:text-primary-300">
                        {selectedScaffolds.size} scaffold{selectedScaffolds.size !== 1 ? 's' : ''} selected
                      </span>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setSelectedScaffolds(new Set())}
                          className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          Clear selection
                        </button>
                        <Button onClick={handleOpenPublishAsQuiz}>
                          Publish Selected as Quiz
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Side index + batch folders */}
                  <div className="flex flex-col gap-4 lg:flex-row">
                    <aside className="w-full flex-shrink-0 lg:w-48">
                      <div className="flex flex-row gap-1 rounded-2xl border border-gray-200 bg-white p-2 shadow-soft dark:border-secondary-700 dark:bg-secondary-800 lg:flex-col">
                        {([
                          { id: 'all' as ScaffoldListFilter, label: 'All sets', count: scaffolds.length },
                          { id: 'published' as ScaffoldListFilter, label: 'Published', count: scaffolds.filter((s) => s.published).length },
                          { id: 'drafts' as ScaffoldListFilter, label: 'Drafts', count: scaffolds.filter((s) => !s.published).length },
                        ]).map((f) => (
                          <button
                            key={f.id}
                            onClick={() => setScaffoldListFilter(f.id)}
                            className={`flex flex-1 items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-xs font-bold transition-colors lg:flex-none ${
                              scaffoldListFilter === f.id
                                ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'
                                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-secondary-700 dark:hover:text-gray-200'
                            }`}
                          >
                            <span className="truncate">{f.label}</span>
                            <span
                              className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-extrabold ${
                                scaffoldListFilter === f.id
                                  ? 'bg-white text-primary-700 dark:bg-secondary-800 dark:text-primary-300'
                                  : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                              }`}
                            >
                              {f.count}
                            </span>
                          </button>
                        ))}
                      </div>
                    </aside>

                    <div className="min-w-0 flex-1 space-y-4">
                      {visibleScaffoldBatches.length === 0 ? (
                        <div className="rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {scaffoldListFilter === 'published'
                              ? 'No published question sets yet.'
                              : 'No drafts right now.'}
                          </p>
                        </div>
                      ) : (
                        visibleScaffoldBatches.map((batch) => {
                          const isExpanded = expandedBatches.has(batch.batchKey);
                          const allSelected = batch.scaffolds.every(s => selectedScaffolds.has(s.scaffold_id));
                          const someSelected = batch.scaffolds.some(s => selectedScaffolds.has(s.scaffold_id));

                          return (
                            <div key={batch.batchKey} className="bg-white dark:bg-secondary-800 rounded-2xl border border-gray-200 dark:border-secondary-700 shadow-soft overflow-hidden">
                              {/* Folder header */}
                              <div
                                onClick={() => toggleBatchExpand(batch.batchKey)}
                                className="flex items-center gap-4 p-5 cursor-pointer hover:bg-gray-50 dark:hover:bg-secondary-700/50 transition-colors"
                              >
                                <div className="flex-shrink-0">
                                  <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                                <div className="flex-shrink-0 w-10 h-10 bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300 rounded-xl flex items-center justify-center">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                                    <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">
                                      {batch.lectureLabel}
                                    </h3>
                                    <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="4" width="18" height="17" rx="2.5" /><path d="M3 9h18M8 2v4M16 2v4" />
                                      </svg>
                                      {batch.dateLabel}
                                    </span>
                                  </div>
                                  {/* Topic pills, capped so long batches stay tidy */}
                                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                                    {batch.topics.slice(0, 4).map((topic) => (
                                      <span
                                        key={topic}
                                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border border-primary-200 dark:border-primary-800 capitalize"
                                      >
                                        {topic}
                                      </span>
                                    ))}
                                    {batch.topics.length > 4 && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold text-gray-500 bg-gray-100 dark:bg-secondary-700 dark:text-gray-400">
                                        +{batch.topics.length - 4} more
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex flex-shrink-0 flex-col items-end gap-1">
                                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    {batch.scaffolds.length} question set{batch.scaffolds.length !== 1 ? 's' : ''}
                                  </span>
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    {batch.scaffolds.reduce((sum, sc) => sum + (sc.num_questions || 0), 0)} questions
                                  </span>
                                </div>
                              </div>

                              {/* Expanded folder content */}
                              {isExpanded && (
                                <div className="border-t border-gray-100 dark:border-secondary-700 px-5 pb-5">
                                  {/* Select all / Publish button */}
                                  <div className="flex items-center justify-between py-3">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={allSelected}
                                        ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                                        onChange={() => selectAllInBatch(batch.scaffolds)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                      />
                                      <span className="text-sm text-gray-600 dark:text-gray-500">
                                        {allSelected ? 'Deselect All' : 'Select All'}
                                      </span>
                                    </label>
                                    {someSelected && (
                                      <Button size="sm" onClick={handleOpenPublishAsQuiz}>
                                        Publish selected as quiz
                                      </Button>
                                    )}
                                  </div>

                                  {/* Question set rows */}
                                  <div className="divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-200 dark:divide-secondary-700/60 dark:border-secondary-700">
                                    {batch.scaffolds.map((scaffold) => (
                                      <div
                                        key={scaffold.scaffold_id}
                                        onClick={() => handleViewScaffold(scaffold.scaffold_id)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleViewScaffold(scaffold.scaffold_id); } }}
                                        className="group flex cursor-pointer items-center gap-4 px-4 py-3.5 transition-colors hover:bg-primary-50/60 focus:outline-none focus-visible:bg-primary-50/60 dark:hover:bg-secondary-700/40 sm:px-5"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={selectedScaffolds.has(scaffold.scaffold_id)}
                                          onChange={() => toggleScaffoldSelection(scaffold.scaffold_id)}
                                          onClick={(e) => e.stopPropagation()}
                                          className="flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        />

                                        {/* title + meta */}
                                        <div className="min-w-0 flex-1">
                                          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                                            <p className="truncate text-sm font-bold capitalize text-gray-900 dark:text-white">{scaffold.title}</p>
                                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold sm:hidden ${
                                              scaffold.published
                                                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'
                                                : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                                            }`}>
                                              {scaffold.published ? 'Published' : 'Draft'}
                                            </span>
                                          </div>
                                          <p className="mt-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                                            {scaffold.num_questions} questions · {scaffold.type_distribution?.mcq || 0} multiple choice · {scaffold.type_distribution?.open_ended || 0} open ended
                                          </p>
                                          {/* Bloom ladder: named level chips */}
                                          <div className="mt-1.5 flex flex-wrap items-center gap-1">
                                            {[1,2,3,4,5,6].map((level) => {
                                              const levelLabel = BLOOM_LABELS[level];
                                              const count = scaffold.bloom_distribution?.[levelLabel] || 0;
                                              const colors = BLOOM_COLORS[level];
                                              if (count <= 0) return null;
                                              return (
                                                <span
                                                  key={level}
                                                  title={`${count} ${levelLabel} question${count !== 1 ? 's' : ''}`}
                                                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${colors.bg} ${colors.text} ${colors.darkBg} ${colors.darkText}`}
                                                >
                                                  <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
                                                  {levelLabel}{count > 1 ? ` ×${count}` : ''}
                                                </span>
                                              );
                                            })}
                                          </div>
                                        </div>

                                        {/* status + actions */}
                                        <div className="flex flex-shrink-0 items-center gap-3">
                                          <span className={`hidden items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold sm:inline-flex ${
                                            scaffold.published
                                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'
                                              : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                                          }`}>
                                            <span className={`h-1.5 w-1.5 rounded-full ${scaffold.published ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                                            {scaffold.published ? 'Published' : 'Draft'}
                                          </span>
                                          {!scaffold.published && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedScaffolds(new Set([scaffold.scaffold_id]));
                                                setPublishAsQuizTitle(`Quiz: ${scaffold.topic}`);
                                                setIsPublishAsQuizModalOpen(true);
                                              }}
                                              className="hidden flex-shrink-0 items-center gap-1 rounded-full border-2 border-gray-200 px-3 py-1 text-xs font-bold text-gray-600 opacity-0 transition-all hover:border-primary-300 hover:text-primary-700 focus-visible:opacity-100 group-hover:opacity-100 sm:inline-flex dark:border-secondary-600 dark:text-gray-300"
                                            >
                                              Publish as quiz
                                            </button>
                                          )}
                                          <button
                                            onClick={(e) => { e.stopPropagation(); setScaffoldToDelete(scaffold.scaffold_id); }}
                                            title="Delete question set"
                                            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 focus-visible:opacity-100 group-hover:opacity-100 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                                          >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                          </button>
                                          <svg className="h-4 w-4 flex-shrink-0 text-gray-300 transition-colors group-hover:text-primary-600 dark:text-secondary-600" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {/* Assignments sub-tab */}
          {assessmentsTab === 'assignments' && (
            <>
              {assignmentsLoading && !viewingAssignment ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                </div>
              ) : viewingAssignment ? (
                /* Assignment Detail View */
                <div className="space-y-4">
                  {/* Header card */}
                  <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-soft dark:border-secondary-700 dark:bg-secondary-800 sm:p-5">
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        onClick={() => { setViewingAssignment(null); setAssignmentSubmissions([]); setSubGradeDrafts({}); }}
                        className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border-2 border-gray-200 px-3.5 py-1.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-secondary-600 dark:text-gray-300 dark:hover:bg-secondary-700 dark:hover:text-white"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        All assignments
                      </button>
                      <div className="flex min-w-0 flex-1 items-center gap-2.5">
                        <h2 className="truncate text-lg font-extrabold text-gray-900 dark:text-white sm:text-xl">{viewingAssignment.title}</h2>
                        <span className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                          viewingAssignment.published
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'
                            : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                        }`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${viewingAssignment.published ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                          {viewingAssignment.published ? 'Published' : 'Draft'}
                        </span>
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2">
                        <Button onClick={openEditAssignmentModal} variant="secondary" size="sm">
                          Edit
                        </Button>
                        {viewingAssignment.published ? (
                          <Button onClick={handleToggleAssignmentPublish} loading={assignmentPublishing} variant="secondary" size="sm">
                            Unpublish
                          </Button>
                        ) : (
                          <Button onClick={handleToggleAssignmentPublish} loading={assignmentPublishing} size="sm">
                            Publish
                          </Button>
                        )}
                        <button
                          onClick={() => setAssignmentToDelete(viewingAssignment.assignment_id)}
                          className="inline-flex items-center rounded-full border-2 border-red-200 px-3.5 py-1.5 text-sm font-bold text-red-600 transition-colors hover:bg-red-50 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-900/20"
                          title="Delete assignment"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    {/* Quiet schedule meta */}
                    <p className="mt-2.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                      {viewingAssignment.available_from || viewingAssignment.due_at ? (
                        <>
                          {viewingAssignment.available_from && `Opens ${formatScheduleDateTime(viewingAssignment.available_from)}`}
                          {viewingAssignment.available_from && viewingAssignment.due_at && ' · '}
                          {viewingAssignment.due_at && `Due ${formatScheduleDateTime(viewingAssignment.due_at)}`}
                        </>
                      ) : (
                        'No schedule set'
                      )}
                    </p>
                  </div>

                  {/* Instructions */}
                  {viewingAssignment.instructions && (
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Instructions</h3>
                      <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                        {viewingAssignment.instructions}
                      </p>
                    </div>
                  )}

                  {/* Attachments */}
                  <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Attachments ({viewingAssignment.attachments.length})
                      </h3>
                      <button
                        onClick={() => assignmentFileInputRef.current?.click()}
                        disabled={!!assignmentUploadingName}
                        className="inline-flex items-center gap-1.5 rounded-full border-2 border-gray-200 px-3.5 py-1.5 text-xs font-bold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-secondary-600 dark:text-gray-300 dark:hover:bg-secondary-700 dark:hover:text-white"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Add file
                      </button>
                      <input
                        ref={assignmentFileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleAssignmentAttachmentSelected}
                      />
                    </div>
                    {assignmentUploadingName && (
                      <div className="mt-3 rounded-2xl border border-primary-200 bg-primary-50 p-3 dark:border-primary-800 dark:bg-primary-900/20">
                        <p className="text-xs font-semibold text-primary-700 dark:text-primary-300">
                          Uploading {assignmentUploadingName}... {assignmentUploadProgress}%
                        </p>
                        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-primary-200 dark:bg-primary-800">
                          <div className="h-1.5 rounded-full bg-primary-600 transition-all" style={{ width: `${assignmentUploadProgress}%` }} />
                        </div>
                      </div>
                    )}
                    {viewingAssignment.attachments.length === 0 && !assignmentUploadingName ? (
                      <p className="mt-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                        No attachments yet. Add files students will need for this assignment. Any file type works.
                      </p>
                    ) : (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {viewingAssignment.attachments.map((att) => (
                          <a
                            key={att.file_id}
                            href={`${getStaticBaseUrl()}${att.file_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={att.file_name}
                            className="inline-flex items-center gap-2 rounded-full border-2 border-gray-200 px-3.5 py-1.5 text-xs font-bold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-secondary-600 dark:text-gray-300 dark:hover:bg-secondary-700 dark:hover:text-white"
                            title={`Download ${att.file_name} (${formatFileSize(att.file_size)})`}
                          >
                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                              <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                            </svg>
                            <span className="max-w-[14rem] truncate">{att.title || att.file_name}</span>
                            <span className="font-medium text-gray-400 dark:text-gray-500">{formatFileSize(att.file_size)}</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Submissions */}
                  <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-secondary-700">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Submissions ({assignmentSubmissions.length})
                      </h3>
                      {assignmentSubmissions.length > 0 && (
                        <button
                          onClick={handleReleaseAllAssignmentGrades}
                          disabled={releasingAssignmentGradesAll}
                          className="inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-4 py-2 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {releasingAssignmentGradesAll ? 'Releasing...' : 'Release all graded'}
                        </button>
                      )}
                    </div>
                    {assignmentSubsLoading ? (
                      <div className="flex justify-center py-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                      </div>
                    ) : assignmentSubmissions.length === 0 ? (
                      <p className="px-5 py-8 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                        No submissions yet. Student work will show up here once they submit.
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100 dark:divide-secondary-700">
                          <thead className="bg-gray-50 dark:bg-secondary-700/50">
                            <tr>
                              <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Student</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Submitted</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Grade</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Feedback</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-secondary-700">
                            {assignmentSubmissions.map((sub) => {
                              const draft = subGradeDrafts[sub.submission_id] || { grade: '', feedback: '' };
                              const saving = subSavingId === sub.submission_id;
                              const releasing = subReleasingId === sub.submission_id;
                              return (
                                <tr key={sub.submission_id} className="align-top transition-colors hover:bg-primary-50/40 dark:hover:bg-secondary-700/40">
                                  <td className="px-5 py-4">
                                    <p className="whitespace-nowrap text-sm font-bold text-gray-900 dark:text-white">{sub.full_name}</p>
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">@{sub.student_username}</p>
                                    {sub.files.length > 0 && (
                                      <div className="mt-1.5 flex flex-col gap-1">
                                        {sub.files.map((f) => (
                                          <a
                                            key={f.file_id}
                                            href={`${getStaticBaseUrl()}${f.file_path}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            download={f.file_name}
                                            className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:underline dark:text-primary-300"
                                          >
                                            <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                              <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                                            </svg>
                                            <span className="max-w-[12rem] truncate">{f.file_name}</span>
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                    {sub.comment && (
                                      <p className="mt-1.5 max-w-[16rem] text-xs font-medium italic text-gray-500 dark:text-gray-400">
                                        "{sub.comment}"
                                      </p>
                                    )}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-4">
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                      {new Date(sub.submitted_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                    </p>
                                    {sub.submitted_late && (
                                      <span className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:bg-red-900/25 dark:text-red-300">
                                        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                                        Late
                                      </span>
                                    )}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-4">
                                    <div className="flex items-center gap-1.5">
                                      <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        step={0.1}
                                        value={draft.grade}
                                        onChange={(e) =>
                                          setSubGradeDrafts((prev) => ({
                                            ...prev,
                                            [sub.submission_id]: { ...draft, grade: e.target.value },
                                          }))
                                        }
                                        aria-label={`Grade for ${sub.full_name}, 0 to 100`}
                                        className="w-20 rounded-lg border-2 border-gray-200 bg-white px-2 py-1 text-sm font-semibold text-gray-900 focus:border-primary-400 focus:outline-none dark:border-secondary-600 dark:bg-secondary-900 dark:text-white"
                                      />
                                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">%</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-4">
                                    <textarea
                                      rows={2}
                                      value={draft.feedback}
                                      onChange={(e) =>
                                        setSubGradeDrafts((prev) => ({
                                          ...prev,
                                          [sub.submission_id]: { ...draft, feedback: e.target.value },
                                        }))
                                      }
                                      placeholder="Feedback"
                                      aria-label={`Feedback for ${sub.full_name}`}
                                      className="w-48 rounded-xl border-2 border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-900 placeholder-gray-400 focus:border-primary-400 focus:outline-none dark:border-secondary-600 dark:bg-secondary-900 dark:text-white dark:placeholder-secondary-400 sm:w-56"
                                    />
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-4">
                                    <div className="flex flex-col items-start gap-2">
                                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                                        sub.released
                                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'
                                          : 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300'
                                      }`}>
                                        <span className={`h-1.5 w-1.5 rounded-full ${sub.released ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                                        {sub.released ? 'Released' : 'Pending'}
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => handleSaveSubmissionGrade(sub)}
                                          disabled={saving}
                                          className="inline-flex items-center rounded-full bg-primary-600 px-3.5 py-1.5 text-xs font-bold text-gray-900 shadow-[0_3px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                          {saving ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                          onClick={() => handleToggleSubmissionRelease(sub)}
                                          disabled={releasing}
                                          className="text-xs font-bold text-gray-500 transition-colors hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200"
                                        >
                                          {releasing ? 'Updating...' : sub.released ? 'Unrelease' : 'Release'}
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              ) : assignments.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <Mascot name="star" chip="#38bdf8" className="h-20 w-20 shadow-medium" blinkDelay={1.4} />
                  <h3 className="mt-5 text-lg font-bold text-gray-900 dark:text-white">No assignments yet</h3>
                  <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                    Create an assignment for students to submit their work
                  </p>
                  <div className="mt-6">
                    <Button onClick={openCreateAssignmentModal}>New Assignment</Button>
                  </div>
                </div>
              ) : (
                /* Assignment List View: side index + list rows */
                <div className="flex flex-col gap-4 lg:flex-row">
                  <aside className="w-full flex-shrink-0 lg:w-48">
                    <div className="flex flex-row gap-1 rounded-2xl border border-gray-200 bg-white p-2 shadow-soft dark:border-secondary-700 dark:bg-secondary-800 lg:flex-col">
                      {([
                        { id: 'all' as AssignmentListFilter, label: 'All assignments', count: assignments.length },
                        { id: 'needs_grading' as AssignmentListFilter, label: 'Needs grading', count: assignmentsNeedingGrading },
                        { id: 'drafts' as AssignmentListFilter, label: 'Drafts', count: assignments.filter((a) => !a.published).length },
                        { id: 'open' as AssignmentListFilter, label: 'Open', count: assignments.filter((a) => getQuizStatus(a).label === 'Open').length },
                      ]).map((f) => (
                        <button
                          key={f.id}
                          onClick={() => setAssignmentListFilter(f.id)}
                          className={`flex flex-1 items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-xs font-bold transition-colors lg:flex-none ${
                            assignmentListFilter === f.id
                              ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'
                              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-secondary-700 dark:hover:text-gray-200'
                          }`}
                        >
                          <span className="truncate">{f.label}</span>
                          <span
                            className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-extrabold ${
                              f.id === 'needs_grading' && f.count > 0
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                : assignmentListFilter === f.id
                                  ? 'bg-white text-primary-700 dark:bg-secondary-800 dark:text-primary-300'
                                  : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                            }`}
                          >
                            {f.count}
                          </span>
                        </button>
                      ))}
                    </div>
                  </aside>

                  <div className="min-w-0 flex-1">
                    {filteredAssignments.length === 0 ? (
                      <div className="rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          {assignmentListFilter === 'needs_grading'
                            ? 'Nothing waiting on grading. Nice work.'
                            : assignmentListFilter === 'drafts'
                              ? 'No drafts right now.'
                              : 'No open assignments right now.'}
                        </p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:divide-secondary-700/60 dark:border-secondary-700 dark:bg-secondary-800">
                        {filteredAssignments.map((assignment) => {
                          const status = getQuizStatus(assignment);
                          const now = Date.now();
                          const opensInFuture = !!assignment.available_from && new Date(assignment.available_from).getTime() > now;
                          const dueTime = assignment.due_at ? new Date(assignment.due_at).getTime() : null;
                          const shortDate = (iso: string) =>
                            new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                          const dueClass = dueTime === null
                            ? ''
                            : dueTime < now
                              ? 'text-red-600 dark:text-red-400'
                              : dueTime - now <= 48 * 3_600_000
                                ? 'text-amber-600 dark:text-amber-400'
                                : '';
                          return (
                            <div
                              key={assignment.assignment_id}
                              onClick={() => handleOpenAssignment(assignment)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenAssignment(assignment); } }}
                              className="group flex cursor-pointer items-center gap-4 px-4 py-3.5 transition-colors hover:bg-primary-50/60 focus:outline-none focus-visible:bg-primary-50/60 dark:hover:bg-secondary-700/40 sm:px-5"
                            >
                              {/* status */}
                              <span className={`hidden w-28 flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold sm:inline-flex ${status.pill}`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                                {status.label}
                              </span>

                              {/* title + meta */}
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-bold text-gray-900 dark:text-white">{assignment.title}</p>
                                <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                                  <span className={`inline-flex items-center gap-1 sm:hidden ${status.pill} rounded-full px-2 py-0.5`}>
                                    {status.label}
                                  </span>
                                  {assignment.attachments.length > 0 && (
                                    <span>{assignment.attachments.length} attachment{assignment.attachments.length !== 1 ? 's' : ''}</span>
                                  )}
                                  {opensInFuture && <span>Opens {shortDate(assignment.available_from!)}</span>}
                                  {dueTime !== null && <span className={dueClass}>Due {shortDate(assignment.due_at!)}</span>}
                                </p>
                              </div>

                              {/* workload signals */}
                              <div className="flex flex-shrink-0 items-center gap-3">
                                <span className="hidden text-xs font-medium text-gray-500 dark:text-gray-400 md:inline">
                                  {assignment.submission_count} submitted
                                </span>
                                {(assignment.pending_review ?? 0) > 0 ? (
                                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700 dark:bg-amber-900/25 dark:text-amber-300">
                                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                                    {assignment.pending_review} to grade
                                  </span>
                                ) : assignment.submission_count > 0 ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400" title="All submissions graded">
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </span>
                                ) : null}
                                <button
                                  onClick={(e) => { e.stopPropagation(); setAssignmentToDelete(assignment.assignment_id); }}
                                  title="Delete assignment"
                                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 focus-visible:opacity-100 group-hover:opacity-100 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                                <svg className="h-4 w-4 flex-shrink-0 text-gray-300 transition-colors group-hover:text-primary-600 dark:text-secondary-600" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
            </div>
          )}

          {/* === GRADES TAB (instructor gradebook) === */}
          {activeTab === 'grades' && (
            <>
              {gradebookLoading || !gradebook ? (
                /* Skeleton while the gradebook loads */
                <div className="space-y-5 animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <div className="h-6 w-40 rounded-full bg-gray-200 dark:bg-secondary-700" />
                      <div className="h-4 w-72 rounded-full bg-gray-100 dark:bg-secondary-700/60" />
                    </div>
                    <div className="h-9 w-32 rounded-full bg-gray-200 dark:bg-secondary-700" />
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                    <div className="space-y-3">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="h-10 rounded-xl bg-gray-100 dark:bg-secondary-700/50" />
                      ))}
                    </div>
                  </div>
                </div>
              ) : gradebook.rows.length === 0 ? (
                /* No students enrolled yet */
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <Mascot name="apple" chip="#f472b6" className="h-20 w-20 shadow-medium" blinkDelay={1.1} />
                  <h3 className="mt-5 text-lg font-bold text-gray-900 dark:text-white">No students enrolled yet</h3>
                  <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                    Grades will appear here once students enroll and take quizzes
                  </p>
                </div>
              ) : gradebook.quizzes.length === 0 ? (
                /* Students enrolled but nothing published to grade */
                <div className="rounded-2xl border border-gray-200 bg-white px-6 py-12 text-center shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">Publish a quiz to start collecting grades</h3>
                  <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                    Best attempts from your {gradebook.rows.length} enrolled student{gradebook.rows.length !== 1 ? 's' : ''} will show up here
                  </p>
                  <button
                    onClick={() => setActiveTab('assessments')}
                    className="mt-3 text-sm font-bold text-primary-700 hover:underline dark:text-primary-300"
                  >
                    Go to Assessments
                  </button>
                </div>
              ) : (() => {
                const selectedRow = gradebookStudent
                  ? gradebook.rows.find((r) => r.username === gradebookStudent)
                  : undefined;

                if (selectedRow) {
                  /* Per-student drill-in: one row per published quiz, with inline grade adjustment */
                  return (
                    <div className="space-y-5">
                      {/* Back to the full gradebook */}
                      <button
                        onClick={() => {
                          setGradebookStudent(null);
                          setGradeEditQuizId(null);
                          setGradeEditDraft('');
                        }}
                        className="inline-flex items-center gap-1.5 rounded-full border-2 border-gray-200 px-4 py-2 text-xs font-bold text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-800 dark:border-secondary-700 dark:text-gray-400 dark:hover:bg-secondary-800 dark:hover:text-gray-200"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        All students
                      </button>

                      {/* Student header: name + quiet username + overall average pill */}
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <h2 className="text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">{selectedRow.full_name}</h2>
                          <p className="mt-0.5 text-sm font-medium text-gray-500 dark:text-gray-400">@{selectedRow.username}</p>
                        </div>
                        {selectedRow.average != null ? (
                          <span className={`inline-flex items-center rounded-full px-3.5 py-1.5 text-sm font-bold ${gradeBandPillClass(selectedRow.average)}`}>
                            {Math.round(selectedRow.average)}% average
                          </span>
                        ) : (
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">No attempts yet</span>
                        )}
                      </div>

                      {/* One row per published quiz */}
                      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                        <ul className="divide-y divide-gray-100 dark:divide-secondary-700">
                          {gradebook.quizzes.map((quiz) => {
                            const score = selectedRow.scores[quiz.quiz_id];
                            const adjusted = selectedRow.overridden?.[quiz.quiz_id] ?? false;
                            const isEditing = gradeEditQuizId === quiz.quiz_id;
                            const parsedDraft = parseFloat(gradeEditDraft);
                            const draftValid = gradeEditDraft.trim() !== '' && !isNaN(parsedDraft) && parsedDraft >= 0 && parsedDraft <= 100;
                            return (
                              <li key={quiz.quiz_id} className="group flex items-center justify-between gap-4 px-6 py-4">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-bold text-gray-900 dark:text-white" title={quiz.title}>{quiz.title}</p>
                                  {quiz.due_at && (
                                    <p className="mt-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                                      due {new Date(quiz.due_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </p>
                                  )}
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                  {isEditing ? (
                                    <>
                                      {adjusted && (
                                        <button
                                          onClick={() => handleSaveGradeOverride(selectedRow.username, quiz.quiz_id, null)}
                                          disabled={gradeEditSaving}
                                          className="text-xs font-bold text-red-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-400"
                                        >
                                          Revert to earned
                                        </button>
                                      )}
                                      <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        step={0.1}
                                        autoFocus
                                        value={gradeEditDraft}
                                        onChange={(e) => setGradeEditDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && draftValid && !gradeEditSaving) {
                                            handleSaveGradeOverride(selectedRow.username, quiz.quiz_id, parsedDraft);
                                          } else if (e.key === 'Escape') {
                                            setGradeEditQuizId(null);
                                            setGradeEditDraft('');
                                          }
                                        }}
                                        aria-label={`Adjusted grade for ${quiz.title}, 0 to 100`}
                                        className="w-20 rounded-lg border-2 border-gray-200 bg-white px-2 py-1 text-sm font-semibold text-gray-900 focus:border-primary-400 focus:outline-none dark:border-secondary-600 dark:bg-secondary-900 dark:text-white"
                                      />
                                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">%</span>
                                      <button
                                        onClick={() => handleSaveGradeOverride(selectedRow.username, quiz.quiz_id, parsedDraft)}
                                        disabled={gradeEditSaving || !draftValid}
                                        className="rounded-full bg-primary-600 px-3.5 py-1.5 text-xs font-bold text-gray-900 shadow-[0_3px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:cursor-not-allowed disabled:opacity-60"
                                      >
                                        {gradeEditSaving ? 'Saving...' : 'Save'}
                                      </button>
                                      <button
                                        onClick={() => {
                                          setGradeEditQuizId(null);
                                          setGradeEditDraft('');
                                        }}
                                        disabled={gradeEditSaving}
                                        className="text-xs font-bold text-gray-500 transition-colors hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200"
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      {adjusted && (
                                        <span
                                          title="Adjusted by instructor"
                                          className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:bg-secondary-700 dark:text-gray-400"
                                        >
                                          Adjusted
                                        </span>
                                      )}
                                      {score != null ? (
                                        <>
                                          <span className={`text-sm font-bold ${gradeBandClass(score)}`}>{Math.round(score * 10) / 10}%</span>
                                          {rowReleasedMap(selectedRow)?.[quiz.quiz_id] === false && renderNotReleasedPill()}
                                        </>
                                      ) : (
                                        <span className="text-sm font-medium text-gray-300 dark:text-gray-600">-</span>
                                      )}
                                      {/* Hover-reveal pencil: opens the inline adjustment editor */}
                                      <button
                                        onClick={() => {
                                          setGradeEditQuizId(quiz.quiz_id);
                                          setGradeEditDraft(score != null ? String(Math.round(score * 10) / 10) : '');
                                        }}
                                        title="Adjust grade"
                                        aria-label={`Adjust grade for ${quiz.title}`}
                                        className="rounded-full p-1.5 text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-700 focus-visible:opacity-100 group-hover:opacity-100 dark:hover:bg-secondary-700 dark:hover:text-gray-200"
                                      >
                                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                          <path d="M17 3a2.85 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                                        </svg>
                                      </button>
                                    </>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  );
                }

                return (
                <div className="space-y-5">
                  {/* Header: title + quiet summary + export */}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">Gradebook</h2>
                      <p className="mt-0.5 text-sm font-medium text-gray-500 dark:text-gray-400">
                        {[
                          `${gradebook.rows.length} student${gradebook.rows.length !== 1 ? 's' : ''}`,
                          `${gradebook.quizzes.length} published quiz${gradebook.quizzes.length !== 1 ? 'zes' : ''}`,
                          gradebook.class_average != null ? `class average ${Math.round(gradebook.class_average)}%` : null,
                        ].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <button
                      onClick={handleExportGradebookCsv}
                      className="inline-flex items-center gap-1.5 rounded-full border-2 border-gray-200 px-4 py-2 text-xs font-bold text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-800 dark:border-secondary-700 dark:text-gray-400 dark:hover:bg-secondary-800 dark:hover:text-gray-200"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                      </svg>
                      Export CSV
                    </button>
                  </div>

                  {/* Gradebook table */}
                  <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-secondary-700">
                        <thead className="bg-gray-50 dark:bg-secondary-700/50">
                          <tr>
                            <th className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                              Student
                            </th>
                            {gradebook.quizzes.map((quiz) => (
                              <th key={quiz.quiz_id} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                <span className="block max-w-[9rem] truncate" title={quiz.title}>{quiz.title}</span>
                                {quiz.due_at && (
                                  <span className="mt-0.5 block text-[11px] font-medium normal-case tracking-normal text-gray-500 dark:text-gray-400">
                                    due {new Date(quiz.due_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                  </span>
                                )}
                              </th>
                            ))}
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Average
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-secondary-700">
                          {gradebook.rows.map((row) => (
                            <tr
                              key={row.username}
                              onClick={() => openGradebookStudent(row.username)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openGradebookStudent(row.username);
                                }
                              }}
                              tabIndex={0}
                              role="button"
                              aria-label={`View grades for ${row.full_name}`}
                              className="group cursor-pointer transition-colors hover:bg-primary-50 focus-visible:bg-primary-50 focus-visible:outline-none dark:hover:bg-secondary-700 dark:focus-visible:bg-secondary-700"
                            >
                              <td className="sticky left-0 z-10 whitespace-nowrap bg-white px-6 py-3.5 transition-colors group-hover:bg-primary-50 group-focus-visible:bg-primary-50 dark:bg-secondary-800 dark:group-hover:bg-secondary-700 dark:group-focus-visible:bg-secondary-700">
                                <p className="text-sm font-bold text-gray-900 dark:text-white">{row.full_name}</p>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">@{row.username}</p>
                              </td>
                              {gradebook.quizzes.map((quiz) => {
                                const score = row.scores[quiz.quiz_id];
                                return (
                                  <td key={quiz.quiz_id} className="whitespace-nowrap px-4 py-3.5">
                                    {score != null ? (
                                      <span className="inline-flex items-center gap-1.5">
                                        <span className={`text-sm font-semibold ${gradeBandClass(score)}`}>
                                          {Math.round(score)}%
                                          {row.overridden?.[quiz.quiz_id] && (
                                            <span title="Adjusted by instructor" className="ml-0.5 align-super text-[11px] font-bold text-gray-500 dark:text-gray-400">*</span>
                                          )}
                                        </span>
                                        {rowReleasedMap(row)?.[quiz.quiz_id] === false && renderNotReleasedPill()}
                                      </span>
                                    ) : (
                                      <span className="text-sm font-medium text-gray-300 dark:text-gray-600">-</span>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="whitespace-nowrap px-4 py-3.5">
                                {row.average != null ? (
                                  <span className={`text-sm font-bold ${gradeBandClass(row.average)}`}>{Math.round(row.average)}%</span>
                                ) : (
                                  <span className="text-sm font-medium text-gray-300 dark:text-gray-600">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-gray-200 bg-gray-50 dark:border-secondary-600 dark:bg-secondary-700/50">
                            <td className="sticky left-0 z-10 whitespace-nowrap bg-gray-50 px-6 py-3.5 text-sm font-bold text-gray-700 dark:bg-secondary-700 dark:text-gray-200">
                              Class average
                            </td>
                            {gradebook.quizzes.map((quiz) => {
                              const avg = gradebook.quiz_averages[quiz.quiz_id];
                              return (
                                <td key={quiz.quiz_id} className="whitespace-nowrap px-4 py-3.5">
                                  {avg != null ? (
                                    <span className={`text-sm font-bold ${gradeBandClass(avg)}`}>{Math.round(avg)}%</span>
                                  ) : (
                                    <span className="text-sm font-medium text-gray-300 dark:text-gray-600">-</span>
                                  )}
                                </td>
                              );
                            })}
                            <td className="whitespace-nowrap px-4 py-3.5">
                              {gradebook.class_average != null ? (
                                <span className={`text-sm font-bold ${gradeBandClass(gradebook.class_average)}`}>{Math.round(gradebook.class_average)}%</span>
                              ) : (
                                <span className="text-sm font-medium text-gray-300 dark:text-gray-600">-</span>
                              )}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
                );
              })()}
            </>
          )}

          {/* === INSIGHTS TAB (reflection analytics) === */}
          {activeTab === 'insights' && (
            <div className="space-y-6">
              {/* Sub-pills: Course structure | Learning trends | Learning paths | Learning outcomes */}
              <div className="inline-flex flex-wrap rounded-full bg-gray-100 p-1 dark:bg-secondary-800">
                {([
                  { id: 'structure' as InsightsTab, label: 'Course structure' },
                  { id: 'trends' as InsightsTab, label: 'Learning trends' },
                  { id: 'paths' as InsightsTab, label: 'Learning paths' },
                  { id: 'outcomes' as InsightsTab, label: 'Learning outcomes' },
                ]).map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => setInsightsTab(sub.id)}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-all ${
                      insightsTab === sub.id
                        ? 'bg-white text-primary-700 shadow-sm dark:bg-secondary-700 dark:text-white'
                        : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    {sub.label}
                  </button>
                ))}
              </div>

          {/* Learning trends sub-view: concept heatmap + per-lecture charts + AI analysis */}
          {insightsTab === 'trends' && (
            <>
              {/* 1. Concept heatmap */}
              <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Concept heatmap</h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      How many students flagged each topic, lecture by lecture
                    </p>
                  </div>
                  {/* Polarity toggle */}
                  <div className="inline-flex flex-shrink-0 rounded-full bg-gray-100 p-1 dark:bg-secondary-700">
                    <button
                      onClick={() => setHeatmapPolarity('challenging')}
                      className={`rounded-full px-4 py-1.5 text-xs font-bold transition-all ${
                        heatmapPolarity === 'challenging'
                          ? 'bg-amber-100 text-amber-800 shadow-sm dark:bg-amber-900/40 dark:text-amber-300'
                          : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                      }`}
                    >
                      Challenging
                    </button>
                    <button
                      onClick={() => setHeatmapPolarity('interesting')}
                      className={`rounded-full px-4 py-1.5 text-xs font-bold transition-all ${
                        heatmapPolarity === 'interesting'
                          ? 'bg-sky-100 text-sky-800 shadow-sm dark:bg-sky-900/40 dark:text-sky-300'
                          : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                      }`}
                    >
                      Interesting
                    </button>
                  </div>
                </div>

                {heatmapLoading && !heatmapData ? (
                  <div className="flex justify-center py-10">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
                  </div>
                ) : !heatmapData || heatmapData.topics.length === 0 || heatmapLectures.length === 0 ? (
                  <div className="mt-5 rounded-2xl border border-dashed border-gray-300 bg-white/60 px-6 py-10 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      The heatmap fills in as students submit reflections
                    </p>
                  </div>
                ) : (
                  <div className="mt-5 overflow-x-auto">
                    <table className="border-separate" style={{ borderSpacing: '3px' }}>
                      <thead>
                        <tr>
                          <th className="sticky left-0 z-10 bg-white dark:bg-secondary-800" />
                          {heatmapLectures.map((lec) => (
                            <th
                              key={lec.lecture_id}
                              title={lec.title}
                              className="px-1 pb-1 text-center text-xs font-bold text-gray-500 dark:text-gray-400"
                            >
                              L{lec.order + 1}
                            </th>
                          ))}
                          <th className="pb-1 pl-2 text-left text-xs font-bold text-gray-500 dark:text-gray-400">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {heatmapData.topics.map((row) => {
                          const rowTotal = heatmapPolarity === 'challenging' ? row.total_challenging : row.total_interesting;
                          return (
                            <tr key={row.topic}>
                              <td
                                title={row.topic}
                                className="sticky left-0 z-10 max-w-[180px] truncate bg-white pr-3 text-sm font-bold text-gray-700 dark:bg-secondary-800 dark:text-gray-200"
                              >
                                {row.topic}
                              </td>
                              {heatmapLectures.map((lec) => {
                                const cell = row.cells.find((c) => c.order === lec.order);
                                const value = cell ? cell[heatmapPolarity] : 0;
                                const alpha = 0.15 + 0.85 * (value / heatmapMaxCell);
                                return (
                                  <td key={lec.lecture_id}>
                                    <div
                                      title={`${value} student${value === 1 ? '' : 's'} · ${row.topic} · ${lec.title}`}
                                      className={`flex size-8 items-center justify-center rounded-md text-xs font-bold text-gray-800 dark:text-gray-100 ${
                                        value === 0 ? 'bg-gray-50 dark:bg-secondary-700/40' : ''
                                      }`}
                                      style={value > 0 ? { backgroundColor: `rgba(${heatmapRgb}, ${alpha})` } : undefined}
                                    >
                                      {value > 0 ? value : ''}
                                    </div>
                                  </td>
                                );
                              })}
                              <td className="pl-2">
                                <span
                                  className={`inline-flex min-w-[2rem] items-center justify-center rounded-full px-2 py-0.5 text-xs font-bold ${
                                    heatmapPolarity === 'challenging'
                                      ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300'
                                      : 'bg-sky-50 text-sky-700 dark:bg-sky-900/25 dark:text-sky-300'
                                  }`}
                                >
                                  {rowTotal}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* 2. Per-lecture top topics */}
              <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Per-lecture top topics</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  What stood out to students in a single lecture
                </p>

                {trendsLectures.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {trendsLectures.map((lec) => (
                      <button
                        key={lec.id}
                        title={lec.title}
                        onClick={() => setTrendsLectureId(lec.id)}
                        className={`rounded-full px-3 py-1 text-xs font-bold transition-all ${
                          trendsSelectedId === lec.id
                            ? 'bg-primary-600 text-gray-900 shadow-sm'
                            : 'bg-gray-100 text-gray-500 hover:text-gray-800 dark:bg-secondary-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                      >
                        L{lec.order + 1}
                      </button>
                    ))}
                  </div>
                )}

                {trendsLectures.length === 0 ? (
                  <div className="mt-5 rounded-2xl border border-dashed border-gray-300 bg-white/60 px-6 py-10 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Charts appear once students submit reflections
                    </p>
                  </div>
                ) : !trendsSelectedAi ? (
                  <p className="mt-5 text-sm text-gray-500 dark:text-gray-400">
                    No AI analysis for this lecture yet. Run "Analyze reflections" below to fill in these charts.
                  </p>
                ) : (
                  <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <div>
                      <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                        Top challenging
                      </h3>
                      {trendsTopBars(trendsSelectedAi.top_challenging).length === 0 ? (
                        <p className="py-6 text-center text-sm italic text-gray-500 dark:text-gray-400">No data yet</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={Math.max(140, trendsTopBars(trendsSelectedAi.top_challenging).length * 36)}>
                          <BarChart
                            data={trendsTopBars(trendsSelectedAi.top_challenging)}
                            layout="vertical"
                            margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                          >
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} stroke="#9ca3af" />
                            <YAxis type="category" dataKey="topic" width={130} tick={{ fontSize: 11, fill: '#9ca3af' }} stroke="#9ca3af" />
                            <Tooltip
                              formatter={(value: any) => [`${value} students`, 'Count']}
                              labelFormatter={(_label: any, payload: any) => payload?.[0]?.payload?.fullTopic || _label}
                            />
                            <Bar dataKey="count" fill="#f59e0b" radius={[0, 6, 6, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                    <div>
                      <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-sky-700 dark:text-sky-400">
                        Top interesting
                      </h3>
                      {trendsTopBars(trendsSelectedAi.top_interesting).length === 0 ? (
                        <p className="py-6 text-center text-sm italic text-gray-500 dark:text-gray-400">No data yet</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={Math.max(140, trendsTopBars(trendsSelectedAi.top_interesting).length * 36)}>
                          <BarChart
                            data={trendsTopBars(trendsSelectedAi.top_interesting)}
                            layout="vertical"
                            margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                          >
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} stroke="#9ca3af" />
                            <YAxis type="category" dataKey="topic" width={130} tick={{ fontSize: 11, fill: '#9ca3af' }} stroke="#9ca3af" />
                            <Tooltip
                              formatter={(value: any) => [`${value} students`, 'Count']}
                              labelFormatter={(_label: any, payload: any) => payload?.[0]?.payload?.fullTopic || _label}
                            />
                            <Bar dataKey="count" fill="#93c5fd" stroke="#649cdd" radius={[0, 6, 6, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* 3. AI reflection analysis (compact: text tucked under disclosures) */}
              <div className="mb-10 space-y-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">AI reflection analysis</h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Summaries, teaching notes, and per-student insights generated from student reflections
                    </p>
                  </div>
                  <button
                    onClick={handleAnalyzeReflections}
                    disabled={aiRunning}
                    className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {aiRunning ? 'Analyzing...' : 'Analyze reflections'}
                  </button>
                </div>

                {/* Job status pills */}
                {aiJobStatus === 'running' && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/25 dark:text-amber-300">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
                    {aiAnalysis?.job?.progress || 'Analyzing reflections'}
                  </span>
                )}
                {aiJobStatus === 'failed' && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/25 dark:text-red-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                    {aiAnalysis?.job?.error || 'Analysis failed'}
                  </span>
                )}

                {!aiJobStatus && !aiHasResults ? (
                  <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-gray-300 bg-white/60 px-6 py-10 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No analysis yet. Run it after students submit reflections.
                    </p>
                    <button
                      onClick={handleAnalyzeReflections}
                      disabled={aiRunning}
                      className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {aiRunning ? 'Analyzing...' : 'Analyze reflections'}
                    </button>
                  </div>
                ) : (
                  <>
                    {/* AI summaries and teaching notes (collapsed by default) */}
                    {sortedAiLectures.length > 0 && (
                      <div className="rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                        <button
                          onClick={() => setAiSummariesOpen((o) => !o)}
                          className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                        >
                          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                            <span className="font-bold text-gray-900 dark:text-white">AI summaries and teaching notes</span>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                              {sortedAiLectures.length} lecture{sortedAiLectures.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <svg
                            className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${aiSummariesOpen ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {aiSummariesOpen && (
                        <div className="space-y-4 px-5 pb-5">
                        {sortedAiLectures.map((lec) => {
                          const isExpanded = aiExpandedEffective.has(lec.lecture_id);
                          const interesting = Object.entries(lec.top_interesting).sort((a, b) => b[1] - a[1]);
                          const challenging = Object.entries(lec.top_challenging).sort((a, b) => b[1] - a[1]);
                          return (
                            <div
                              key={lec.lecture_id}
                              className="rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800"
                            >
                              <button
                                onClick={() => toggleAiLecture(lec.lecture_id)}
                                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                              >
                                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                                  <span className="font-bold text-gray-900 dark:text-white">{lec.lecture_title}</span>
                                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                    {lec.total_responses} reflections
                                  </span>
                                </div>
                                <svg
                                  className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                  fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              {isExpanded && (
                                <div className="space-y-5 px-5 pb-5">
                                  <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">{lec.summary}</p>
                                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                                    <div>
                                      <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                                        Top interesting
                                      </h4>
                                      {interesting.length === 0 ? (
                                        <p className="text-sm text-gray-500 dark:text-gray-400">No data</p>
                                      ) : (
                                        <div className="flex flex-wrap gap-1.5">
                                          {interesting.map(([topic, count]) => (
                                            <span
                                              key={topic}
                                              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300"
                                            >
                                              {topic}
                                              <span className="font-bold text-emerald-500 dark:text-emerald-400">{count}</span>
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                                        Top challenging
                                      </h4>
                                      {challenging.length === 0 ? (
                                        <p className="text-sm text-gray-500 dark:text-gray-400">No data</p>
                                      ) : (
                                        <div className="flex flex-wrap gap-1.5">
                                          {challenging.map(([topic, count]) => (
                                            <span
                                              key={topic}
                                              className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/25 dark:text-amber-300"
                                            >
                                              {topic}
                                              <span className="font-bold text-amber-500 dark:text-amber-400">{count}</span>
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                      Teaching notes
                                    </h4>
                                    <div className="whitespace-pre-line rounded-xl bg-gray-50 p-4 text-sm leading-relaxed text-gray-600 dark:bg-secondary-700/50 dark:text-gray-300">
                                      {lec.teaching_notes}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        </div>
                        )}
                      </div>
                    )}

                    {/* Per-student AI narratives (collapsed by default) */}
                    {aiAnalysis && aiAnalysis.students.length > 0 && (
                      <div className="rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                        <button
                          onClick={() => setAiNarrativesOpen((o) => !o)}
                          className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                        >
                          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                            <span className="font-bold text-gray-900 dark:text-white">Per-student AI narratives</span>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                              {aiAnalysis.students.length} student{aiAnalysis.students.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <svg
                            className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${aiNarrativesOpen ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {aiNarrativesOpen && (
                        <div className="grid grid-cols-1 gap-4 px-5 pb-5 sm:grid-cols-2">
                          {aiAnalysis.students.map((stu) => (
                            <div
                              key={stu.student_username}
                              className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800"
                            >
                              <p className="font-bold text-gray-900 dark:text-white">{stu.student_username}</p>
                              <div className="mt-3">
                                <h4 className="text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">Struggles</h4>
                                <p className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-300">{stu.challenges_analysis}</p>
                                {stu.challenging_list.length > 0 && (
                                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                    {stu.challenging_list.slice(0, 6).map((topic) => (
                                      <span
                                        key={topic}
                                        className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/25 dark:text-amber-300"
                                      >
                                        {topic}
                                      </span>
                                    ))}
                                    {stu.challenging_list.length > 6 && (
                                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                        +{stu.challenging_list.length - 6} more
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="mt-4">
                                <h4 className="text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">Engagement</h4>
                                <p className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-300">{stu.interesting_analysis}</p>
                                {stu.interesting_list.length > 0 && (
                                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                    {stu.interesting_list.slice(0, 6).map((topic) => (
                                      <span
                                        key={topic}
                                        className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300"
                                      >
                                        {topic}
                                      </span>
                                    ))}
                                    {stu.interesting_list.length > 6 && (
                                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                        +{stu.interesting_list.length - 6} more
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* 4. Legacy charts (older reflection analytics, collapsed by default) */}
              <div className="rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                <button
                  onClick={() => setLegacyChartsOpen((o) => !o)}
                  className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                >
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <span className="font-bold text-gray-900 dark:text-white">Legacy charts</span>
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Response distribution and per-lecture breakdown
                    </span>
                  </div>
                  <svg
                    className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${legacyChartsOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {legacyChartsOpen && (
                <div className="px-5 pb-5">
              {courseReflectionsLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                </div>
              ) : !courseReflections || courseReflections.total_responses === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <Mascot name="clock" chip="#fde047" className="h-20 w-20 shadow-medium" blinkDelay={2.1} />
                  <h3 className="mt-5 text-lg font-bold text-gray-900 dark:text-white">No reflections yet</h3>
                  <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                    Students can submit reflections from each lecture's player page
                  </p>
                </div>
              ) : (
                <div className="space-y-8 animate-fade-in">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                      <div className="flex items-center gap-4">
                        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </span>
                        <div>
                          <p className="text-2xl font-extrabold text-gray-900 dark:text-white">{courseReflections.total_responses}</p>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Responses</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                      <div className="flex items-center gap-4">
                        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </span>
                        <div>
                          <p className="text-2xl font-extrabold text-gray-900 dark:text-white">
                            {courseReflections.lectures_with_responses}/{courseReflections.total_lectures}
                          </p>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Lectures with Reflections</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                      <div className="flex items-center gap-4">
                        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </span>
                        <div>
                          <p className="text-2xl font-extrabold text-gray-900 dark:text-white">
                            {courseReflections.lectures_with_responses > 0
                              ? (courseReflections.total_responses / courseReflections.lectures_with_responses).toFixed(1)
                              : '0'}
                          </p>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg per Lecture</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Response Distribution - Pie Chart + Overview */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="bg-white dark:bg-secondary-800 rounded-2xl p-6 border border-gray-200 dark:border-secondary-700 shadow-soft">
                      <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-4">
                        Responses by Lecture
                      </h3>
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie
                            data={courseReflections.lecture_analytics
                              .filter(l => l.total_responses > 0)
                              .map(l => ({ name: l.lecture_title, value: l.total_responses }))}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {courseReflections.lecture_analytics
                              .filter(l => l.total_responses > 0)
                              .map((_, index) => {
                                const colors = ['#93c5fd', '#fde68a', '#649cdd', '#f59e0b', '#bfdbfe', '#9ca3af'];
                                return <Cell key={`pie-${index}`} fill={colors[index % colors.length]} />;
                              })}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Most Interesting Topics - Bar Chart */}
                    <div className="bg-white dark:bg-secondary-800 rounded-2xl p-6 border border-gray-200 dark:border-secondary-700 shadow-soft">
                      <h3 className="text-sm font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide mb-4 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                        Top Interesting Topics
                      </h3>
                      {Object.keys(courseReflections.interesting_counts).length === 0 ? (
                        <p className="text-sm text-gray-500 italic py-8 text-center">No data yet</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={Math.min(300, Math.max(180, Object.keys(courseReflections.interesting_counts).length * 32))}>
                          <BarChart
                            data={Object.entries(courseReflections.interesting_counts)
                              .slice(0, 8)
                              .map(([topic, count]) => ({ topic: topic.length > 20 ? topic.slice(0, 18) + '...' : topic, count, fullTopic: topic }))}
                            layout="vertical"
                            margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" opacity={0.25} horizontal={false} />
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} stroke="#9ca3af" />
                            <YAxis type="category" dataKey="topic" width={130} tick={{ fontSize: 11, fill: '#9ca3af' }} stroke="#9ca3af" />
                            <Tooltip
                              formatter={(value: any) => [`${value} students`, 'Count']}
                              labelFormatter={(_label: any, payload: any) => payload?.[0]?.payload?.fullTopic || _label}
                            />
                            <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                              {Object.entries(courseReflections.interesting_counts)
                                .slice(0, 8)
                                .map((_, index) => (
                                  <Cell key={`int-bar-${index}`} fill={index % 2 === 0 ? '#93c5fd' : '#649cdd'} />
                                ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>

                    {/* Most Challenging Topics - Bar Chart */}
                    <div className="bg-white dark:bg-secondary-800 rounded-2xl p-6 border border-gray-200 dark:border-secondary-700 shadow-soft">
                      <h3 className="text-sm font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-4 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        Top Challenging Topics
                      </h3>
                      {Object.keys(courseReflections.challenging_counts).length === 0 ? (
                        <p className="text-sm text-gray-500 italic py-8 text-center">No data yet</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={Math.min(300, Math.max(180, Object.keys(courseReflections.challenging_counts).length * 32))}>
                          <BarChart
                            data={Object.entries(courseReflections.challenging_counts)
                              .slice(0, 8)
                              .map(([topic, count]) => ({ topic: topic.length > 20 ? topic.slice(0, 18) + '...' : topic, count, fullTopic: topic }))}
                            layout="vertical"
                            margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" opacity={0.25} horizontal={false} />
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} stroke="#9ca3af" />
                            <YAxis type="category" dataKey="topic" width={130} tick={{ fontSize: 11, fill: '#9ca3af' }} stroke="#9ca3af" />
                            <Tooltip
                              formatter={(value: any) => [`${value} students`, 'Count']}
                              labelFormatter={(_label: any, payload: any) => payload?.[0]?.payload?.fullTopic || _label}
                            />
                            <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                              {Object.entries(courseReflections.challenging_counts)
                                .slice(0, 8)
                                .map((_, index) => (
                                  <Cell key={`ch-bar-${index}`} fill={index % 2 === 0 ? '#f59e0b' : '#fde68a'} />
                                ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>

                  {/* Per-Lecture Breakdown */}
                  <div>
                    <h3 className="text-lg font-extrabold text-gray-900 dark:text-white mb-4">Per-Lecture Breakdown</h3>
                    <div className="space-y-3">
                      {courseReflections.lecture_analytics.map((lec) => {
                        const lecVisual = getCourseVisual(lec.lecture_title);
                        return (
                        <div
                          key={lec.lecture_id}
                          className="rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800 overflow-hidden"
                        >
                          <button
                            onClick={() => setExpandedLecture(expandedLecture === lec.lecture_id ? null : lec.lecture_id)}
                            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-secondary-700/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                                <SubjectGlyph glyph={lecVisual.glyph} className="h-5 w-5" strokeWidth={1.9} />
                              </span>
                              <div className="text-left">
                                <p className="font-bold text-gray-900 dark:text-white text-sm">{lec.lecture_title}</p>
                                <p className="mt-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                                  {lec.total_responses} response{lec.total_responses !== 1 ? 's' : ''}
                                  {lec.total_responses > 0 && ` · ${Object.keys(lec.interesting_counts).length + Object.keys(lec.challenging_counts).length} topics tagged`}
                                </p>
                              </div>
                            </div>
                            <svg
                              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                                expandedLecture === lec.lecture_id ? 'rotate-180' : ''
                              }`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>

                          {expandedLecture === lec.lecture_id && lec.total_responses > 0 && (
                            <div className="px-4 pb-4 border-t border-gray-100 dark:border-secondary-700 pt-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <h4 className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mb-3">
                                    Interesting Topics
                                  </h4>
                                  {Object.keys(lec.interesting_counts).length === 0 ? (
                                    <p className="text-xs text-gray-500 italic">None selected</p>
                                  ) : (
                                    <div className="space-y-2">
                                      {Object.entries(lec.interesting_counts)
                                        .sort(([, a], [, b]) => b - a)
                                        .map(([topic, count]) => {
                                          const pct = Math.round((count / lec.total_responses) * 100);
                                          return (
                                            <div key={`int-${lec.lecture_id}-${topic}`}>
                                              <div className="flex justify-between text-xs mb-0.5">
                                                <span className="text-gray-700 dark:text-gray-300 truncate mr-2">{topic}</span>
                                                <span className="text-gray-400 flex-shrink-0">{count} ({pct}%)</span>
                                              </div>
                                              <div className="w-full bg-gray-100 dark:bg-secondary-700 rounded-full h-1.5">
                                                <div
                                                  className="bg-[#93c5fd] h-1.5 rounded-full transition-all duration-500"
                                                  style={{ width: `${pct}%` }}
                                                />
                                              </div>
                                            </div>
                                          );
                                        })}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <h4 className="text-xs font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-3">
                                    Challenging Topics
                                  </h4>
                                  {Object.keys(lec.challenging_counts).length === 0 ? (
                                    <p className="text-xs text-gray-500 italic">None selected</p>
                                  ) : (
                                    <div className="space-y-2">
                                      {Object.entries(lec.challenging_counts)
                                        .sort(([, a], [, b]) => b - a)
                                        .map(([topic, count]) => {
                                          const pct = Math.round((count / lec.total_responses) * 100);
                                          return (
                                            <div key={`ch-${lec.lecture_id}-${topic}`}>
                                              <div className="flex justify-between text-xs mb-0.5">
                                                <span className="text-gray-700 dark:text-gray-300 truncate mr-2">{topic}</span>
                                                <span className="text-gray-400 flex-shrink-0">{count} ({pct}%)</span>
                                              </div>
                                              <div className="w-full bg-gray-100 dark:bg-secondary-700 rounded-full h-1.5">
                                                <div
                                                  className="bg-[#f59e0b] h-1.5 rounded-full transition-all duration-500"
                                                  style={{ width: `${pct}%` }}
                                                />
                                              </div>
                                            </div>
                                          );
                                        })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {expandedLecture === lec.lecture_id && lec.total_responses === 0 && (
                            <div className="px-4 pb-4 border-t border-gray-100 dark:border-secondary-700 pt-4">
                              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">No reflections submitted for this lecture</p>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
                </div>
                )}
              </div>
            </>
          )}

          {/* Learning paths sub-view: per-student concept journeys */}
          {insightsTab === 'paths' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Learning paths</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Follow how each student's interesting and challenging topics evolve across lectures
                </p>
              </div>

              {/* Student picker */}
              {studentsLoading && pathStudentChoices.length === 0 ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                </div>
              ) : pathStudentChoices.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <Mascot name="apple" chip="#f472b6" className="h-20 w-20 shadow-medium" blinkDelay={1.4} />
                  <h3 className="mt-5 text-lg font-bold text-gray-900 dark:text-white">No students yet</h3>
                  <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                    Learning paths appear once students enroll and submit reflections
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
                    <div ref={pathBoxRef} className="relative w-full max-w-xs">
                      <input
                        type="text"
                        value={pathSearch}
                        onChange={(e) => {
                          setPathSearch(e.target.value);
                          setPathDropOpen(true);
                          setPathHighlight(0);
                        }}
                        onFocus={() => setPathDropOpen(true)}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setPathDropOpen(true);
                            setPathHighlight((h) => Math.min(h + 1, filteredPathChoices.length - 1));
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setPathHighlight((h) => Math.max(h - 1, 0));
                          } else if (e.key === 'Enter') {
                            e.preventDefault();
                            const pick = filteredPathChoices[pathHighlight] ?? filteredPathChoices[0];
                            if (pathDropOpen && pick) handleSelectPathStudent(pick);
                          } else if (e.key === 'Escape') {
                            setPathDropOpen(false);
                          }
                        }}
                        placeholder="Search students..."
                        aria-label="Search students"
                        className="w-full rounded-full border-2 border-gray-200 bg-white px-4 py-2 pr-9 text-sm font-semibold text-gray-900 placeholder-gray-400 outline-none transition-colors focus:border-primary-400 dark:border-secondary-600 dark:bg-secondary-800 dark:text-white dark:placeholder-gray-500"
                      />
                      {(pathSearch || pathStudent) && (
                        <button
                          onClick={handleClearPathStudent}
                          aria-label="Clear student"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200"
                        >
                          <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                            <path fillRule="evenodd" d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 01-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 01-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                      {pathDropOpen && (
                        <div className="absolute z-10 mt-2 max-h-64 w-full overflow-auto rounded-2xl border border-gray-200 bg-white py-1.5 shadow-medium dark:border-secondary-600 dark:bg-secondary-800">
                          {filteredPathChoices.length === 0 ? (
                            <p className="px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                              No students match "{pathSearch}"
                            </p>
                          ) : (
                            filteredPathChoices.map((choice, idx) => (
                              <button
                                key={choice.username}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleSelectPathStudent(choice)}
                                onMouseEnter={() => setPathHighlight(idx)}
                                className={`flex w-full items-baseline gap-2 px-4 py-2 text-left text-sm transition-colors ${
                                  idx === pathHighlight
                                    ? 'bg-primary-50 dark:bg-secondary-700'
                                    : 'hover:bg-gray-50 dark:hover:bg-secondary-700/60'
                                }`}
                              >
                                <span className="truncate font-bold text-gray-900 dark:text-white">{choice.fullName}</span>
                                <span className="flex-shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">
                                  @{choice.username}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {selectedPathChoice && (
                      <div className="min-w-0">
                        <p className="truncate font-bold text-gray-900 dark:text-white">{selectedPathChoice.fullName}</p>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">@{selectedPathChoice.username}</p>
                      </div>
                    )}
                  </div>

                  {/* Loading skeleton */}
                  {pathLoading && (
                    <div className="space-y-4 animate-pulse">
                      <div className="flex flex-wrap gap-2">
                        {[0, 1, 2, 3].map((i) => (
                          <div key={i} className="h-7 w-28 rounded-full bg-gray-100 dark:bg-secondary-700" />
                        ))}
                      </div>
                      <div className="h-40 rounded-2xl bg-gray-100 dark:bg-secondary-700" />
                      <div className="h-56 rounded-2xl bg-gray-100 dark:bg-secondary-700" />
                    </div>
                  )}

                  {!pathLoading && pathStudent && !pathData && (
                    <div className="rounded-2xl border border-dashed border-gray-300 bg-white/60 px-6 py-10 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Could not load a learning path for {selectedPathChoice?.fullName ?? pathStudent}
                      </p>
                    </div>
                  )}

                  {!pathLoading && pathData && pathData.timeline.length === 0 && (
                    <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                      <Mascot name="clock" chip="#fde047" className="h-20 w-20 shadow-medium" blinkDelay={2.3} />
                      <h3 className="mt-5 text-lg font-bold text-gray-900 dark:text-white">No reflections yet</h3>
                      <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                        {pathData.student_username} has not submitted any reflections in this course
                      </p>
                    </div>
                  )}

                  {!pathLoading && pathData && pathData.timeline.length > 0 && (
                    <div className="space-y-6 animate-fade-in">
                      {/* Summary stat cards */}
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        {PATH_STAT_CARDS.map(({ state, label, num }) => (
                          <div
                            key={state}
                            className="rounded-2xl border border-gray-200 bg-white p-4 shadow-soft dark:border-secondary-700 dark:bg-secondary-800"
                          >
                            <div className="flex items-center justify-between">
                              <p className={`text-2xl font-extrabold ${num}`}>{pathData.summary[state]}</p>
                              <span
                                className={`flex h-8 w-8 items-center justify-center rounded-xl ${CONCEPT_ICON_CHIP[state].chip}`}
                              >
                                {CONCEPT_ICON_CHIP[state].icon}
                              </span>
                            </div>
                            <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              {label}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3.5 py-1.5 text-xs font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                        Reflected in {pathData.summary.reflected_lectures} of {pathData.summary.total_lectures} lectures
                      </div>

                      {/* Concepts list: icon chip, topic, explanation, lecture strip, state badge */}
                      {pathData.concepts.length > 0 && (
                        <div className="rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                          <h3 className="border-b border-gray-100 px-5 py-3.5 text-xs font-bold uppercase tracking-wide text-gray-500 dark:border-secondary-700 dark:text-gray-400">
                            Concepts
                          </h3>
                          <div className="divide-y divide-gray-100 dark:divide-secondary-700">
                            {[...pathData.concepts]
                              .sort((a, b) => CONCEPT_SORT[a.state] - CONCEPT_SORT[b.state])
                              .map((concept) => (
                                <div key={concept.topic} className="flex items-start gap-3 px-5 py-4">
                                  <span
                                    className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl ${CONCEPT_ICON_CHIP[concept.state].chip}`}
                                  >
                                    {CONCEPT_ICON_CHIP[concept.state].icon}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-bold text-gray-900 dark:text-white">{concept.topic}</p>
                                    <p className="mt-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                                      {conceptExplanation(concept)}
                                    </p>
                                    <div className="mt-2 flex flex-wrap items-center gap-1">
                                      {Array.from({ length: pathTotalLectures }, (_, o) => {
                                        const inChal = concept.chal_lectures.includes(o);
                                        const inInt = concept.int_lectures.includes(o);
                                        return (
                                          <span
                                            key={o}
                                            title={`L${o + 1}: ${orderedLectureTitles[o] ?? `Lecture ${o + 1}`}`}
                                            className={`h-3 w-3 rounded-[3px] ${
                                              !inChal && !inInt ? 'bg-gray-200 dark:bg-secondary-700' : ''
                                            }`}
                                            style={
                                              inChal
                                                ? { backgroundColor: '#f59e0b' }
                                                : inInt
                                                  ? { backgroundColor: '#93c5fd' }
                                                  : undefined
                                            }
                                          />
                                        );
                                      })}
                                    </div>
                                  </div>
                                  <span
                                    className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${CONCEPT_STATES[concept.state].pill}`}
                                  >
                                    <span className={`h-1.5 w-1.5 rounded-full ${CONCEPT_STATES[concept.state].dot}`} />
                                    {CONCEPT_STATES[concept.state].label}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Lecture-by-lecture timeline */}
                      <div className="rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                        <h3 className="border-b border-gray-100 px-5 py-3.5 text-xs font-bold uppercase tracking-wide text-gray-500 dark:border-secondary-700 dark:text-gray-400">
                          Timeline
                        </h3>
                        <div className="divide-y divide-gray-100 dark:divide-secondary-700">
                          {pathData.timeline.map((entry) => (
                            <div key={entry.lecture_id} className="flex gap-4 px-5 py-4">
                              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-sm font-bold text-primary-800 dark:bg-primary-900/40 dark:text-primary-300">
                                L{entry.order + 1}
                              </span>
                              <div className="min-w-0 flex-1 space-y-2">
                                <p className="font-bold text-gray-900 dark:text-white">{entry.lecture_title}</p>
                                {entry.challenging.length === 0 && entry.interesting.length === 0 && (
                                  <p className="text-sm text-gray-500 dark:text-gray-400">No topics this lecture</p>
                                )}
                                {entry.challenging.length > 0 && (
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                      Challenging
                                    </span>
                                    {entry.challenging.map((topic) => (
                                      <span
                                        key={`ch-${topic}`}
                                        className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/25 dark:text-amber-300"
                                      >
                                        {topic}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {entry.interesting.length > 0 && (
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                      Interesting
                                    </span>
                                    {entry.interesting.map((topic) => (
                                      <span
                                        key={`int-${topic}`}
                                        className="rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-sky-700 dark:bg-sky-900/25 dark:text-sky-300"
                                      >
                                        {topic}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {entry.challenging_comment && (
                                  <p className="text-sm italic text-gray-500 dark:text-gray-400">
                                    "{entry.challenging_comment}"
                                  </p>
                                )}
                                {entry.interesting_comment && (
                                  <p className="text-sm italic text-gray-500 dark:text-gray-400">
                                    "{entry.interesting_comment}"
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Learning outcomes sub-view: outcomes map + per-student verdicts */}
          {insightsTab === 'outcomes' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Learning outcomes</h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Map reflections onto your learning outcomes and see where each student stands
                  </p>
                </div>
                <button
                  onClick={handleGenerateOutcomes}
                  disabled={outcomesRunning}
                  className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {outcomesRunning ? 'Generating...' : 'Generate outcomes'}
                </button>
              </div>

              {/* Job status pills */}
              {outcomesJobStatus === 'running' && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/25 dark:text-amber-300">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
                  {outcomesData?.job?.progress || 'Generating outcomes'}
                </span>
              )}
              {outcomesJobStatus === 'failed' && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/25 dark:text-red-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                  {outcomesData?.job?.error || 'Outcome analysis failed'}
                </span>
              )}

              {!outcomesData?.learning_map && !outcomesData?.outcomes && !outcomesRunning ? (
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <Mascot name="clock" chip="#fde047" className="h-20 w-20 shadow-medium" blinkDelay={1.8} />
                  <h3 className="mt-5 text-lg font-bold text-gray-900 dark:text-white">No outcomes yet</h3>
                  <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                    Add learning outcomes under Course structure, then generate outcomes once students have reflected
                  </p>
                </div>
              ) : (
                <>
                  {/* Learning map: one collapsible card per objective */}
                  {outcomesData?.learning_map && outcomesData.learning_map.tree.length > 0 && (
                    <div>
                      <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Learning map
                      </h3>
                      <div className="space-y-3">
                        {outcomesData.learning_map.tree.map((node) => {
                          const isExpanded = outcomesExpanded.has(node.objective_id);
                          return (
                            <div
                              key={node.objective_id}
                              className="rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800"
                            >
                              <button
                                onClick={() => toggleOutcomeObjective(node.objective_id)}
                                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                              >
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                                  <span className="font-bold text-gray-900 dark:text-white">{node.objective_text}</span>
                                  <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300">
                                    {node.interesting} interesting
                                  </span>
                                  <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/25 dark:text-amber-300">
                                    {node.challenging} challenging
                                  </span>
                                </div>
                                <svg
                                  className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                  fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              {isExpanded && (
                                <div className="space-y-4 px-5 pb-5">
                                  {node.lectures.length === 0 ? (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">No mapped lectures yet</p>
                                  ) : (
                                    node.lectures.map((lec) => (
                                      <div key={lec.lecture_id}>
                                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{lec.lecture_title}</p>
                                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                                          {lec.topics.map((t) => (
                                            <span
                                              key={`${t.type}-${t.topic}`}
                                              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                                                t.type === 'interesting'
                                                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'
                                                  : 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300'
                                              }`}
                                            >
                                              {t.topic}
                                              <span className="font-bold opacity-70">{t.count}</span>
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Unmapped topics */}
                  {outcomesData?.learning_map && outcomesData.learning_map.unmapped.length > 0 && (
                    <div>
                      <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Unmapped topics
                      </h3>
                      <div className="flex flex-wrap gap-1.5">
                        {outcomesData.learning_map.unmapped.map((t) => (
                          <span
                            key={`${t.type}-${t.topic}`}
                            className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400"
                          >
                            {t.topic}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Student outcome cards */}
                  {outcomesData?.outcomes && outcomesData.outcomes.students.length > 0 && (
                    <div>
                      <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Student outcomes
                      </h3>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {outcomesData.outcomes.students.map((stu) => {
                          const verdict = OUTCOME_VERDICTS[stu.verdict] ?? OUTCOME_VERDICTS['Some gaps'];
                          return (
                            <div
                              key={stu.student_username}
                              className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800"
                            >
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                                <p className="font-bold text-gray-900 dark:text-white">{stu.student_username}</p>
                                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${verdict.pill}`}>
                                  <span className={`h-1.5 w-1.5 rounded-full ${verdict.dot}`} />
                                  {stu.verdict}
                                </span>
                              </div>
                              <p className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                                coverage {Math.round(stu.coverage * 100)}% · {stu.unresolved} unresolved
                              </p>
                              {stu.narrative && (
                                <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-300">{stu.narrative}</p>
                              )}
                              <div className="mt-4 space-y-3">
                                {stu.objectives.map((obj) => {
                                  const status = OBJECTIVE_STATUS[obj.status] ?? OBJECTIVE_STATUS['no-evidence'];
                                  const topics = [
                                    ...obj.engaged.map((t) => ({ topic: t.topic, tone: 'emerald' })),
                                    ...obj.resolved.map((t) => ({ topic: t.topic, tone: 'sky' })),
                                    ...obj.struggled.map((t) => ({ topic: t.topic, tone: 'amber' })),
                                  ];
                                  return (
                                    <div key={obj.objective_id} className="border-t border-gray-100 pt-3 dark:border-secondary-700">
                                      <div className="flex items-start gap-2">
                                        <span className={`mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full ${status.dot}`} />
                                        <div>
                                          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                            {obj.objective_text}
                                            <span className={`ml-2 text-xs font-bold ${status.text}`}>{status.label}</span>
                                          </p>
                                          {topics.length > 0 && (
                                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                                              {topics.map((t, i) => (
                                                <span
                                                  key={`${t.tone}-${t.topic}-${i}`}
                                                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                    t.tone === 'emerald'
                                                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'
                                                      : t.tone === 'sky'
                                                      ? 'bg-sky-50 text-sky-700 dark:bg-sky-900/25 dark:text-sky-300'
                                                      : 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300'
                                                  }`}
                                                >
                                                  {t.topic}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Course structure sub-view: outcomes -> modules -> lectures editor */}
          {insightsTab === 'structure' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Course structure</h2>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                    Outcomes and modules power the learning map and student outcomes. When lectures are
                    assigned to modules, outcomes are attributed through this structure instead of AI mapping.
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-3">
                  {structureSaved && (
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Saved</span>
                  )}
                  <button
                    onClick={handleSaveStructure}
                    disabled={structureSaving || !structureDraft}
                    className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {structureSaving ? 'Saving...' : 'Save structure'}
                  </button>
                </div>
              </div>

              {structureLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                </div>
              ) : !structureDraft ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Could not load the course structure.</p>
              ) : (() => {
                const draft = structureDraft;
                const objIndexById = new Map(draft.learning_objectives.map((o, i) => [o.id, i]));
                const lectureById = new Map(draft.lectures.map((l) => [l.lecture_id, l]));
                const sortedLectures = [...draft.lectures].sort((a, b) => a.order - b.order);
                const paletteFor = (objectiveId: string) =>
                  OUTCOME_PALETTE[(objIndexById.get(objectiveId) ?? 0) % OUTCOME_PALETTE.length];
                /** True when a lecture's explicit outcomes differ from its module's set. */
                const isCustomized = (lectureId: string, moduleObjIds: string[]) => {
                  const explicit = draft.lecture_outcomes[lectureId];
                  if (!explicit || explicit.length === 0) return false;
                  return (
                    explicit.length !== moduleObjIds.length ||
                    explicit.some((oid) => !moduleObjIds.includes(oid))
                  );
                };
                return (
                  <>
                    {/* a. Learning outcomes */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                      <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Learning outcomes
                      </h3>
                      <div className="mt-4 space-y-3">
                        {draft.learning_objectives.length === 0 && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            No outcomes yet. Add the outcomes you want this course to build toward.
                          </p>
                        )}
                        {draft.learning_objectives.map((obj, idx) => {
                          const color = OUTCOME_PALETTE[idx % OUTCOME_PALETTE.length];
                          return (
                            <div key={obj.id} className="group flex items-center gap-2">
                              <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${color.dot}`} />
                              <span className="flex-shrink-0 rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] font-bold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                                {obj.id}
                              </span>
                              <input
                                type="text"
                                value={obj.text}
                                onChange={(e) => handleOutcomeTextChange(obj.id, e.target.value)}
                                placeholder={`Outcome ${idx + 1}`}
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white dark:placeholder-gray-500"
                              />
                              <button
                                onClick={() => handleRemoveOutcome(obj.id)}
                                aria-label="Remove outcome"
                                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-gray-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 focus:opacity-100 group-hover:opacity-100 dark:hover:bg-red-900/25 dark:hover:text-red-400"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <button
                        onClick={handleAddOutcome}
                        className="mt-4 text-sm font-bold text-gray-500 transition-colors hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        + Add outcome
                      </button>
                    </div>

                    {/* b. Modules */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                      <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Modules
                      </h3>
                      <div className="mt-4 space-y-4">
                        {draft.modules.length === 0 && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            No modules yet. Group lectures into modules and tag each module with the outcomes it builds.
                          </p>
                        )}
                        {draft.modules.map((mod, mIdx) => {
                          const moduleLectures = mod.lecture_ids
                            .map((lid) => lectureById.get(lid))
                            .filter((l): l is StructureLecture => Boolean(l));
                          return (
                            <div
                              key={mod.module_id}
                              className="group rounded-2xl border border-gray-200 p-4 dark:border-secondary-700"
                            >
                              {/* Title + reorder + delete */}
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={mod.title}
                                  onChange={(e) => handleModuleTitleChange(mod.module_id, e.target.value)}
                                  placeholder={`Module ${mIdx + 1}`}
                                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-900 placeholder-gray-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white dark:placeholder-gray-500"
                                />
                                <button
                                  onClick={() => handleMoveModule(mod.module_id, -1)}
                                  disabled={mIdx === 0}
                                  aria-label="Move module up"
                                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-secondary-700 dark:hover:text-gray-200"
                                >
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleMoveModule(mod.module_id, 1)}
                                  disabled={mIdx === draft.modules.length - 1}
                                  aria-label="Move module down"
                                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-secondary-700 dark:hover:text-gray-200"
                                >
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDeleteModule(mod.module_id)}
                                  aria-label="Delete module"
                                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 focus:opacity-100 group-hover:opacity-100 dark:hover:bg-red-900/25 dark:hover:text-red-400"
                                >
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>

                              {/* Outcome chips */}
                              <div className="mt-3">
                                <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                  Outcomes
                                </p>
                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                  {draft.learning_objectives.length === 0 && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      Add learning outcomes above to tag this module
                                    </span>
                                  )}
                                  {draft.learning_objectives.map((obj, oIdx) => {
                                    const selected = mod.objective_ids.includes(obj.id);
                                    const color = OUTCOME_PALETTE[oIdx % OUTCOME_PALETTE.length];
                                    return (
                                      <button
                                        key={obj.id}
                                        onClick={() => handleToggleModuleObjective(mod.module_id, obj.id)}
                                        title={obj.text}
                                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-xs font-bold transition-all ${
                                          selected
                                            ? color.chip
                                            : 'border border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:border-secondary-600 dark:text-gray-400 dark:hover:border-secondary-500 dark:hover:text-gray-200'
                                        }`}
                                      >
                                        <span className={`h-2 w-2 rounded-full ${color.dot}`} />
                                        {obj.id}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Read-only lecture chips (assignment happens in the Lectures table below) */}
                              <div className="mt-3">
                                <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                  Lectures
                                </p>
                                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                  {moduleLectures.map((lec) => (
                                    <span
                                      key={lec.lecture_id}
                                      className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-700 dark:bg-secondary-700 dark:text-gray-200"
                                    >
                                      <span className="text-gray-400 dark:text-gray-500">L{lec.order + 1}</span>
                                      <span className="max-w-[10rem] truncate">{lec.title}</span>
                                      <span className="rounded-full bg-white px-1.5 py-0.5 text-[11px] font-bold text-gray-500 dark:bg-secondary-800 dark:text-gray-400">
                                        {lec.reflection_count} reflections
                                      </span>
                                    </span>
                                  ))}
                                  {moduleLectures.length === 0 && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      No lectures yet. Assign them in the Lectures table below.
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <button
                        onClick={handleAddModule}
                        className="mt-4 inline-flex items-center gap-1.5 rounded-full border-2 border-primary-300 px-4 py-1.5 text-sm font-bold text-primary-800 transition-colors hover:bg-primary-50 dark:border-primary-700 dark:text-primary-300 dark:hover:bg-primary-900/20"
                      >
                        + Add module
                      </button>
                    </div>

                    {/* c. Lectures: per-lecture module assignment + outcome overrides */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                      <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Lectures
                      </h3>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Assign each lecture to a module and fine-tune its outcomes. Lectures inherit
                        their module's outcomes until you customize them here.
                      </p>
                      {sortedLectures.length === 0 ? (
                        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                          No lectures in this course yet.
                        </p>
                      ) : (
                        <div className="mt-3 divide-y divide-gray-100 dark:divide-secondary-700">
                          {sortedLectures.map((lec) => {
                            const lectureModule = draft.modules.find((m) =>
                              m.lecture_ids.includes(lec.lecture_id)
                            );
                            const explicit = draft.lecture_outcomes[lec.lecture_id];
                            const hasExplicit = Boolean(explicit && explicit.length > 0);
                            const effectiveOutcomes = hasExplicit
                              ? explicit!
                              : (lectureModule?.objective_ids ?? []);
                            return (
                              <div key={lec.lecture_id} className="py-3 first:pt-0 last:pb-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="flex h-7 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 font-mono text-xs font-bold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                                    L{lec.order + 1}
                                  </span>
                                  <span className="min-w-0 max-w-[18rem] truncate text-sm font-bold text-gray-900 dark:text-white">
                                    {lec.title}
                                  </span>
                                  <span className="flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                                    {lec.reflection_count} reflections
                                  </span>
                                  <select
                                    value={lectureModule?.module_id ?? ''}
                                    onChange={(e) => handleSetLectureModule(lec.lecture_id, e.target.value)}
                                    aria-label={`Module for ${lec.title}`}
                                    className="ml-auto rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-bold text-gray-600 focus:border-primary-400 focus:outline-none dark:border-secondary-600 dark:bg-secondary-700 dark:text-gray-300"
                                  >
                                    <option value="">Unassigned</option>
                                    {draft.modules.map((m, mIdx) => (
                                      <option key={m.module_id} value={m.module_id}>
                                        {m.title || `Module ${mIdx + 1}`}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-11">
                                  {draft.learning_objectives.length === 0 ? (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      Add learning outcomes above to tag this lecture
                                    </span>
                                  ) : (
                                    <>
                                      {draft.learning_objectives.map((obj, oIdx) => {
                                        const selected = effectiveOutcomes.includes(obj.id);
                                        const color = OUTCOME_PALETTE[oIdx % OUTCOME_PALETTE.length];
                                        return (
                                          <button
                                            key={obj.id}
                                            onClick={() => handleToggleLectureOutcome(lec.lecture_id, obj.id)}
                                            title={obj.text}
                                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-xs font-bold transition-all ${
                                              selected
                                                ? hasExplicit
                                                  ? color.chip
                                                  : `${color.chip} opacity-70`
                                                : 'border border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:border-secondary-600 dark:text-gray-400 dark:hover:border-secondary-500 dark:hover:text-gray-200'
                                            }`}
                                          >
                                            <span className={`h-2 w-2 rounded-full ${color.dot}`} />
                                            {obj.id}
                                          </button>
                                        );
                                      })}
                                      {hasExplicit ? (
                                        <>
                                          <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400">
                                            customized
                                          </span>
                                          <button
                                            onClick={() => handleResetLectureOutcomes(lec.lecture_id)}
                                            className="text-[11px] font-bold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                                          >
                                            Reset to module
                                          </button>
                                        </>
                                      ) : (
                                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                          {lectureModule && effectiveOutcomes.length > 0
                                            ? 'inherited from module'
                                            : 'no outcomes'}
                                        </span>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* d. Structure tree preview */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                      <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Structure preview
                      </h3>
                      {draft.learning_objectives.length === 0 ? (
                        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                          Add learning outcomes to see the structure tree.
                        </p>
                      ) : (
                        <div className="mt-4 space-y-5">
                          {draft.learning_objectives.map((obj, oIdx) => {
                            const color = paletteFor(obj.id);
                            const carriers = draft.modules.filter((m) => m.objective_ids.includes(obj.id));
                            return (
                              <div key={obj.id}>
                                <div className="flex items-start gap-2">
                                  <span className={`mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${color.dot}`} />
                                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                                    {obj.text || `Outcome ${oIdx + 1}`}
                                  </p>
                                </div>
                                {carriers.length === 0 ? (
                                  <p className="ml-5 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    Not covered by any module yet
                                  </p>
                                ) : (
                                  <div className="ml-5 mt-2 space-y-3 border-l-2 border-gray-100 pl-4 dark:border-secondary-700">
                                    {carriers.map((mod) => {
                                      const lecs = mod.lecture_ids
                                        .map((lid) => lectureById.get(lid))
                                        .filter((l): l is StructureLecture => Boolean(l));
                                      return (
                                        <div key={mod.module_id}>
                                          <p className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                            {mod.title || 'Untitled module'}
                                          </p>
                                          {lecs.length === 0 ? (
                                            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                              No lectures assigned
                                            </p>
                                          ) : (
                                            <div className="mt-1 space-y-1">
                                              {lecs.map((lec) => (
                                                <div
                                                  key={lec.lecture_id}
                                                  className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                                                >
                                                  <span className="font-mono text-xs font-bold text-gray-500 dark:text-gray-400">
                                                    L{lec.order + 1}
                                                  </span>
                                                  <span className="truncate">{lec.title}</span>
                                                  <span className="flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                                                    {lec.reflection_count} reflections
                                                  </span>
                                                  {isCustomized(lec.lecture_id, mod.objective_ids) && (
                                                    <span className="flex-shrink-0 rounded-full border border-gray-200 px-1.5 py-0.5 text-[11px] font-bold text-gray-500 dark:border-secondary-600 dark:text-gray-400">
                                                      custom
                                                    </span>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
            </div>
          )}

          {/* === CLASSLIST TAB === */}
          {activeTab === 'classlist' && (
            <>
              {studentsLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                </div>
              ) : !enrolledStudents || enrolledStudents.total_students === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <Mascot name="apple" chip="#f472b6" className="h-20 w-20 shadow-medium" blinkDelay={1.1} />
                  <h3 className="mt-5 text-lg font-bold text-gray-900 dark:text-white">No students enrolled</h3>
                  <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                    Students can enroll in this course from the course catalog
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mt-5"
                    onClick={() => setIsRosterImportModalOpen(true)}
                  >
                    Import roster
                  </Button>
                </div>
              ) : (
                <div className="space-y-6 animate-fade-in">
                  {/* Summary */}
                  <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </span>
                        <div>
                          <p className="text-2xl font-extrabold text-gray-900 dark:text-white">{enrolledStudents.total_students}</p>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Student{enrolledStudents.total_students !== 1 ? 's' : ''} Enrolled
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setIsRosterImportModalOpen(true)}
                      >
                        Import roster
                      </Button>
                    </div>
                  </div>

                  {/* Student Table */}
                  <div className="rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-secondary-700">
                      <thead className="bg-gray-50 dark:bg-secondary-700/50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Student</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Enrolled</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-secondary-700">
                        {enrolledStudents.students.map((student, idx) => (
                          <tr key={student.username} className={idx % 2 === 0 ? 'bg-white dark:bg-secondary-800' : 'bg-gray-50/50 dark:bg-secondary-700/20'}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-200 text-sm font-bold text-gray-900 dark:bg-primary-900/40 dark:text-primary-200">
                                  {student.full_name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-sm font-bold text-gray-900 dark:text-white">{student.full_name}</p>
                                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">@{student.username}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <p className="text-sm text-gray-600 dark:text-gray-300">{student.email}</p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {new Date(student.enrolled_at).toLocaleDateString()}
                              </p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Import Roster Modal */}
      <Modal
        isOpen={isRosterImportModalOpen}
        onClose={closeRosterImportModal}
        title="Import roster"
        size="lg"
      >
        {!rosterResult ? (
          <div className="space-y-5">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Upload a OneRoster users.csv straight from your SIS, or any CSV with email, givenName, and familyName columns. Students get accounts and are enrolled in this course; existing students are just enrolled.
              </p>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Rows with a teacher role are skipped. Re-importing the same file is safe.
              </p>
            </div>

            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center transition-colors hover:border-primary-400 hover:bg-primary-50/40 dark:border-secondary-600 dark:bg-secondary-700/50 dark:hover:border-primary-500">
              <input
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                onChange={(e) => setRosterFile(e.target.files?.[0] || null)}
              />
              <svg className="h-8 w-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0-12l-4 4m4-4l4 4" />
              </svg>
              {rosterFile ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                  <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                  {rosterFile.name}
                </span>
              ) : (
                <>
                  <span className="text-sm font-bold text-gray-700 dark:text-gray-300">Choose a CSV file</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">users.csv from your SIS export works as is</span>
                </>
              )}
            </label>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="secondary" onClick={closeRosterImportModal} fullWidth>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleRosterImport}
                disabled={!rosterFile}
                loading={rosterImporting}
                fullWidth
              >
                {rosterImporting ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary strip */}
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {rosterResult.created} created
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 dark:bg-sky-900/25 dark:text-sky-300">
                <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                {rosterResult.enrolled} enrolled
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600 dark:bg-secondary-700 dark:text-gray-300">
                <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                {rosterResult.skipped} skipped
              </span>
              {rosterResult.errors > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/25 dark:text-red-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                  {rosterResult.errors} errors
                </span>
              )}
            </div>

            {rosterResult.rows.some((r) => r.temp_password) && (
              <p className="rounded-2xl bg-amber-50 px-4 py-2.5 text-xs font-medium text-amber-800 dark:bg-amber-900/25 dark:text-amber-300">
                Some accounts could not be emailed; share these temporary passwords securely, they are shown only once.
              </p>
            )}
            {rosterResult.created > 0 &&
              rosterResult.rows.filter((r) => r.action === 'created').every((r) => r.emailed) && (
                <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                  Welcome emails with temporary passwords were sent automatically.
                </p>
              )}

            {/* Results table */}
            <div className="max-h-80 overflow-y-auto rounded-2xl border border-gray-200 dark:border-secondary-700">
              <table className="min-w-full divide-y divide-gray-100 text-sm dark:divide-secondary-700">
                <tbody className="divide-y divide-gray-100 dark:divide-secondary-700">
                  {rosterResult.rows.map((row) => (
                    <tr key={row.row} className="align-top">
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-400 dark:text-gray-500">{row.row}</td>
                      <td className="px-3 py-2.5">
                        <p className="text-sm font-bold text-gray-900 dark:text-white">{row.username || row.email || 'Unknown'}</p>
                        {(row.full_name || row.email) && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {[row.full_name, row.email].filter(Boolean).join(' | ')}
                          </p>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${ROSTER_ACTIONS[row.action].pill}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${ROSTER_ACTIONS[row.action].dot}`} />
                          {ROSTER_ACTIONS[row.action].label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        {row.message && <p className="text-xs text-gray-500 dark:text-gray-400">{row.message}</p>}
                        {row.temp_password && (
                          <span className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 font-mono text-xs text-gray-800 dark:bg-secondary-700 dark:text-gray-200">
                            {row.temp_password}
                            <button
                              type="button"
                              onClick={() => copyTempPassword(row.row, row.temp_password!)}
                              title="Copy password"
                              className="text-gray-400 transition-colors hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              {copiedRosterRow === row.row ? (
                                <svg className="h-3.5 w-3.5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                              ) : (
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                  <path d="M8 7V5a2 2 0 012-2h9a2 2 0 012 2v9a2 2 0 01-2 2h-2M5 9h9a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2v-9a2 2 0 012-2z" />
                                </svg>
                              )}
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => {
                  setRosterResult(null);
                  setRosterFile(null);
                  setCopiedRosterRow(null);
                }}
                className="text-sm font-semibold text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Import another file
              </button>
              <Button type="button" onClick={closeRosterImportModal}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Upload Lecture Modal */}
      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        title="Upload Lecture"
        size="md"
      >
        <form onSubmit={handleUploadLecture} className="space-y-5">
          {lectureErrors.general && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 dark:bg-red-900/20 dark:text-red-400 px-4 py-3 rounded-2xl text-sm">
              {lectureErrors.general}
            </div>
          )}

          <Input
            label="Lecture Title"
            placeholder="e.g., Introduction to Variables"
            value={lectureForm.title}
            onChange={(e) => setLectureForm({ ...lectureForm, title: e.target.value })}
            error={lectureErrors.title}
            fullWidth
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Description</label>
            <textarea
              placeholder="Describe what's covered in this lecture..."
              value={lectureForm.description}
              onChange={(e) => setLectureForm({ ...lectureForm, description: e.target.value })}
              className="block w-full px-4 py-2.5 border-2 border-gray-200 rounded-2xl bg-white text-gray-900 placeholder-gray-400 dark:bg-secondary-700 dark:text-white dark:border-secondary-600 dark:placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors duration-200"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Video File <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoFileChange}
              className="block w-full cursor-pointer rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-500 transition-colors hover:border-primary-400 hover:bg-primary-50/40 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700/50 dark:text-gray-400 file:mr-4 file:cursor-pointer file:rounded-full file:border-0 file:bg-primary-600 file:px-4 file:py-2 file:text-sm file:font-bold file:text-gray-900 hover:file:bg-primary-700"
              required
            />
            {lectureErrors.video && <p className="mt-1.5 text-sm text-red-600">{lectureErrors.video}</p>}
            {lectureForm.videoFile && (
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                {lectureForm.videoFile.name} ({(lectureForm.videoFile.size / (1024 * 1024)).toFixed(2)} MB)
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsUploadModalOpen(false)}
              fullWidth
            >
              Cancel
            </Button>
            <Button type="submit" fullWidth>
              Upload Lecture
            </Button>
          </div>
        </form>
      </Modal>

      {/* Upload Slide Modal */}
      <Modal
        isOpen={isSlideUploadModalOpen}
        onClose={() => setIsSlideUploadModalOpen(false)}
        title="Upload Presentation"
        size="md"
      >
        <form onSubmit={handleUploadSlide} className="space-y-5">
          {slideErrors.general && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 dark:bg-red-900/20 dark:text-red-400 px-4 py-3 rounded-2xl text-sm">
              {slideErrors.general}
            </div>
          )}

          <Input
            label="Presentation Title"
            placeholder="e.g., Chapter 1 - Introduction to Python"
            value={slideForm.title}
            onChange={(e) => setSlideForm({ ...slideForm, title: e.target.value })}
            error={slideErrors.title}
            fullWidth
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Presentation File <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept=".ppt,.pptx,.pdf,.odp,.key"
              onChange={handleSlideFileChange}
              className="block w-full cursor-pointer rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-500 transition-colors hover:border-primary-400 hover:bg-primary-50/40 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700/50 dark:text-gray-400 file:mr-4 file:cursor-pointer file:rounded-full file:border-0 file:bg-primary-600 file:px-4 file:py-2 file:text-sm file:font-bold file:text-gray-900 hover:file:bg-primary-700"
              required
            />
            <p className="mt-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">Supported formats: PPT, PPTX, PDF, ODP, KEY</p>
            {slideErrors.file && <p className="mt-1.5 text-sm text-red-600">{slideErrors.file}</p>}
            {slideForm.file && (
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                {slideForm.file.name} ({(slideForm.file.size / (1024 * 1024)).toFixed(2)} MB)
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsSlideUploadModalOpen(false)}
              fullWidth
            >
              Cancel
            </Button>
            <Button type="submit" fullWidth>
              Upload Presentation
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Lecture Confirmation Modal */}
      {lectureToDelete && (
        <Modal
          isOpen={!!lectureToDelete}
          onClose={() => setLectureToDelete(null)}
          title="Delete Lecture"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              Are you sure you want to delete this lecture? This action cannot be undone.
            </p>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setLectureToDelete(null)}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => handleDeleteLecture(lectureToDelete)}
                fullWidth
              >
                Delete Lecture
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Slide Confirmation Modal */}
      {slideToDelete && (
        <Modal
          isOpen={!!slideToDelete}
          onClose={() => setSlideToDelete(null)}
          title="Delete Presentation"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              Are you sure you want to delete this presentation? This action cannot be undone.
            </p>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setSlideToDelete(null)}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => handleDeleteSlide(slideToDelete)}
                fullWidth
              >
                Delete Presentation
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Recommended Video Modal */}
      <Modal
        isOpen={isAddVideoModalOpen}
        onClose={() => !addVideoLoading && setIsAddVideoModalOpen(false)}
        title="Add Recommended Video"
        size="md"
      >
        <form onSubmit={handleAddVideo} className="space-y-5">
          {videoErrors.general && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 dark:bg-red-900/20 dark:text-red-400 px-4 py-3 rounded-2xl text-sm">
              {videoErrors.general}
            </div>
          )}
          <Input
            label="Video Title"
            placeholder="e.g., Sorting Algorithms Explained"
            value={videoForm.title}
            onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
            error={videoErrors.title}
            fullWidth
            required
            disabled={addVideoLoading}
          />
          <Input
            label="YouTube URL"
            placeholder="https://www.youtube.com/watch?v=..."
            value={videoForm.youtubeUrl}
            onChange={(e) => setVideoForm({ ...videoForm, youtubeUrl: e.target.value })}
            error={videoErrors.url}
            fullWidth
            required
            disabled={addVideoLoading}
          />
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsAddVideoModalOpen(false)} fullWidth disabled={addVideoLoading}>
              Cancel
            </Button>
            <Button type="submit" loading={addVideoLoading} fullWidth>
              Add Video
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Video Confirmation Modal */}
      {videoToDelete && (
        <Modal isOpen={!!videoToDelete} onClose={() => setVideoToDelete(null)} title="Delete Video" size="sm">
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              Are you sure you want to remove this recommended video?
            </p>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setVideoToDelete(null)} fullWidth>Cancel</Button>
              <Button type="button" variant="danger" onClick={() => handleDeleteVideo(videoToDelete)} fullWidth>Delete Video</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Generate Quiz Modal */}
      <Modal
        isOpen={isGenerateQuizModalOpen}
        onClose={() => setIsGenerateQuizModalOpen(false)}
        title="Generate Quiz"
        size="lg"
      >
        <form onSubmit={handleGenerateQuiz} className="space-y-5">
          {quizErrors.general && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 dark:bg-red-900/20 dark:text-red-400 px-4 py-3 rounded-2xl text-sm">
              {quizErrors.general}
            </div>
          )}
          <Input
            label="Quiz Title"
            placeholder="e.g., Midterm Review Quiz"
            value={quizForm.title}
            onChange={(e) => setQuizForm({ ...quizForm, title: e.target.value })}
            fullWidth
            required
            disabled={false}
          />

          {/* Lecture Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Lectures
            </label>
            <div className="space-y-2 max-h-40 overflow-y-auto border-2 border-gray-200 dark:border-secondary-600 rounded-2xl p-3">
              {course?.lectures.length === 0 && (
                <p className="text-sm text-gray-500">No lectures available</p>
              )}
              {course?.lectures.map((lecture) => (
                <label key={lecture.lecture_id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-secondary-700 p-1.5 rounded-lg">
                  <input
                    type="checkbox"
                    checked={quizForm.selectedLectures.includes(lecture.lecture_id)}
                    onChange={() => toggleLectureSelection(lecture.lecture_id)}
                    disabled={false}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{lecture.title}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Slide Selection */}
          {slides.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Slides
              </label>
              <div className="space-y-2 max-h-32 overflow-y-auto border-2 border-gray-200 dark:border-secondary-600 rounded-2xl p-3">
                {slides.map((slide) => (
                  <label key={slide.slide_id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-secondary-700 p-1.5 rounded-lg">
                    <input
                      type="checkbox"
                      checked={quizForm.selectedSlides.includes(slide.slide_id)}
                      onChange={() => toggleSlideSelection(slide.slide_id)}
                      disabled={false}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{slide.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Recommended Videos Selection (only show processed ones) */}
          {recommendedVideos.filter(v => v.has_transcript).length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Recommended Videos
              </label>
              <div className="space-y-2 max-h-32 overflow-y-auto border-2 border-gray-200 dark:border-secondary-600 rounded-2xl p-3">
                {recommendedVideos.filter(v => v.has_transcript).map((video) => (
                  <label key={video.video_id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-secondary-700 p-1.5 rounded-lg">
                    <input
                      type="checkbox"
                      checked={quizForm.selectedRecommendedVideos.includes(video.video_id)}
                      onChange={() => toggleRecommendedVideoSelection(video.video_id)}
                      disabled={false}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{video.title}</span>
                    <span className="text-xs text-green-600 dark:text-green-400 ml-auto">Transcribed</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Documents Selection (course material files) */}
          {courseFiles.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Documents
              </label>
              <div className="space-y-2 max-h-40 overflow-y-auto border-2 border-gray-200 dark:border-secondary-600 rounded-2xl p-3">
                {[...contentFolders.map(f => ({ id: f.folder_id as string | null, name: f.name })), { id: null, name: 'Files' }].map(group => {
                  const groupFiles = courseFiles.filter(f => (f.folder_id ?? null) === group.id);
                  if (groupFiles.length === 0) return null;
                  return (
                    <div key={group.id ?? 'root'}>
                      <p className="px-1 pt-1 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">{group.name}</p>
                      {groupFiles.map(file => {
                        const eligible = file.text_status !== 'unsupported' && file.text_status !== 'failed';
                        return (
                          <label key={file.file_id} className={`flex items-center gap-3 p-1.5 rounded-lg ${eligible ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-secondary-700' : 'opacity-60'}`}>
                            <input
                              type="checkbox"
                              checked={quizForm.selectedFiles.includes(file.file_id)}
                              onChange={() => toggleFileSelection(file.file_id)}
                              disabled={!eligible}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{file.title || file.file_name}</span>
                            {file.text_status === 'ready' && (
                              <span className="text-xs text-green-600 dark:text-green-400 ml-auto flex-shrink-0">Ready</span>
                            )}
                            {(file.text_status === 'pending' || !file.text_status) && (
                              <span className="text-xs text-amber-600 dark:text-amber-400 ml-auto flex-shrink-0">Processing</span>
                            )}
                            {!eligible && (
                              <span className="text-xs text-gray-400 ml-auto flex-shrink-0" title={file.text_error || undefined}>Not supported</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Number of Questions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Number of Questions: {quizForm.numQuestions}
            </label>
            <input
              type="range"
              min={3}
              max={30}
              value={quizForm.numQuestions}
              onChange={(e) => setQuizForm({ ...quizForm, numQuestions: parseInt(e.target.value) })}
              disabled={false}
              className="w-full h-2 bg-gray-200 dark:bg-secondary-600 rounded-lg appearance-none cursor-pointer accent-primary-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>3</span>
              <span>30</span>
            </div>
          </div>

          {/* Question mix: MCQ vs open ended */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Question mix: {quizForm.numQuestions - Math.min(quizForm.openEndedCount, quizForm.numQuestions)} multiple choice, {Math.min(quizForm.openEndedCount, quizForm.numQuestions)} open ended
            </label>
            <input
              type="range"
              min={0}
              max={quizForm.numQuestions}
              value={Math.min(quizForm.openEndedCount, quizForm.numQuestions)}
              onChange={(e) => setQuizForm({ ...quizForm, openEndedCount: parseInt(e.target.value) })}
              className="w-full h-2 bg-gray-200 dark:bg-secondary-600 rounded-lg appearance-none cursor-pointer accent-primary-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>All multiple choice</span>
              <span>All open ended</span>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Open ended questions are generated with a grading rubric and are scored by you.</p>
          </div>

          {/* Bloom's taxonomy levels */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Bloom's levels (optional)
            </label>
            <div className="flex flex-wrap gap-2">
              {[
                { level: 1, name: 'Remember' },
                { level: 2, name: 'Understand' },
                { level: 3, name: 'Apply' },
                { level: 4, name: 'Analyze' },
                { level: 5, name: 'Evaluate' },
                { level: 6, name: 'Create' },
              ].map(({ level, name }) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => toggleBloomLevel(level)}
                  className={`rounded-full px-3 py-1.5 text-xs font-bold border-2 transition-colors ${
                    quizForm.bloomLevels.includes(level)
                      ? 'bg-primary-600 text-gray-900 border-primary-600'
                      : 'border-gray-200 text-gray-600 hover:border-primary-300 dark:border-secondary-600 dark:text-gray-300'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Leave all unselected for a natural mix.</p>
          </div>

          {/* Difficulty */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Difficulty
            </label>
            <div className="flex flex-wrap gap-2">
              {([
                { value: '', name: 'Mixed' },
                { value: 'easy', name: 'Easy' },
                { value: 'medium', name: 'Medium' },
                { value: 'hard', name: 'Hard' },
              ] as const).map(({ value, name }) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setQuizForm({ ...quizForm, difficulty: value })}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold border-2 transition-colors ${
                    quizForm.difficulty === value
                      ? 'bg-primary-600 text-gray-900 border-primary-600'
                      : 'border-gray-200 text-gray-600 hover:border-primary-300 dark:border-secondary-600 dark:text-gray-300'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>

          {/* Attempts allowed */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Attempts allowed
            </label>
            <div className="flex flex-wrap gap-2">
              {([
                { value: 1, name: '1' },
                { value: 2, name: '2' },
                { value: 3, name: '3' },
                { value: 0, name: 'Unlimited' },
              ] as const).map(({ value, name }) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setQuizForm({ ...quizForm, maxAttempts: value })}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold border-2 transition-colors ${
                    quizForm.maxAttempts === value
                      ? 'bg-primary-600 text-gray-900 border-primary-600'
                      : 'border-gray-200 text-gray-600 hover:border-primary-300 dark:border-secondary-600 dark:text-gray-300'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Students cannot retake the quiz once they use all attempts.</p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsGenerateQuizModalOpen(false)} fullWidth>
              Cancel
            </Button>
            <Button type="submit" fullWidth>
              Generate Quiz
            </Button>
          </div>
        </form>
      </Modal>


      {/* Publish Quiz / Edit Schedule Modal */}
      <Modal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        title={scheduleModalMode === 'publish' ? 'Publish quiz' : 'Edit schedule'}
        size="sm"
      >
        <form onSubmit={handleScheduleModalSubmit} className="space-y-5">
          {scheduleError && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-700 dark:text-red-400 px-4 py-3 rounded-2xl text-sm">
              {scheduleError}
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
              Available from
            </label>
            <input
              type="datetime-local"
              value={scheduleForm.availableFrom}
              onChange={(e) => setScheduleForm({ ...scheduleForm, availableFrom: e.target.value })}
              className="block w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors duration-200"
            />
            <p className="mt-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              Leave empty to open immediately
            </p>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
              Due date
            </label>
            <input
              type="datetime-local"
              value={scheduleForm.dueAt}
              onChange={(e) => setScheduleForm({ ...scheduleForm, dueAt: e.target.value })}
              className="block w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors duration-200"
            />
            <p className="mt-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              Leave empty for no deadline
            </p>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
              Attempts allowed
            </label>
            <input
              type="number"
              min={0}
              max={10}
              value={scheduleForm.maxAttempts}
              onChange={(e) => setScheduleForm({ ...scheduleForm, maxAttempts: e.target.value })}
              className="block w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors duration-200"
            />
            <p className="mt-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              0 means unlimited attempts
            </p>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            Times are in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone.replace('_', ' ')}). Students see them converted to theirs.
          </p>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setIsScheduleModalOpen(false)} fullWidth>
              Cancel
            </Button>
            <Button type="submit" loading={scheduleSaving} fullWidth>
              {scheduleModalMode === 'publish' ? 'Publish quiz' : 'Save schedule'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Quiz Confirmation Modal */}
      {quizToDelete && (
        <Modal isOpen={!!quizToDelete} onClose={() => setQuizToDelete(null)} title="Delete Quiz" size="sm">
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              Are you sure you want to delete this quiz? All student attempts will also be deleted.
            </p>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setQuizToDelete(null)} fullWidth>Cancel</Button>
              <Button type="button" variant="danger" onClick={() => handleDeleteQuiz(quizToDelete)} fullWidth>Delete Quiz</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Generate Scaffold Wizard Modal */}
      <Modal
        isOpen={isGenerateScaffoldModalOpen}
        onClose={() => { setIsGenerateScaffoldModalOpen(false); setScaffoldWizardStep(1); setScaffoldErrors({}); }}
        title={
          scaffoldWizardStep === 1 ? 'Step 1: Select Sources'
          : scaffoldWizardStep === 2 ? 'Step 2: Choose Topics to Assess'
          : 'Step 3: Configure & Generate'
        }
        size="lg"
      >
        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-5">
          {[1, 2, 3].map((step) => (
            <React.Fragment key={step}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                step === scaffoldWizardStep
                  ? 'bg-primary-600 text-gray-900'
                  : step < scaffoldWizardStep
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
              }`}>
                {step < scaffoldWizardStep ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : step}
              </div>
              {step < 3 && <div className={`flex-1 h-0.5 ${step < scaffoldWizardStep ? 'bg-green-500' : 'bg-gray-200 dark:bg-secondary-700'}`}></div>}
            </React.Fragment>
          ))}
        </div>

        {scaffoldErrors.general && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 dark:bg-red-900/20 dark:text-red-400 px-4 py-3 rounded-2xl text-sm mb-4">
            {scaffoldErrors.general}
          </div>
        )}

        {/* Step 1: Select Lectures */}
        {scaffoldWizardStep === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-500">
              Choose the lectures and documents to generate question sets from. Topics will be extracted from what you select.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Lectures
              </label>
              <div className="space-y-1 max-h-64 overflow-y-auto border-2 border-gray-200 dark:border-secondary-600 rounded-2xl p-3">
                {course?.lectures.length === 0 && (
                  <p className="text-sm text-gray-500">No lectures available. Upload lectures first</p>
                )}
                {course?.lectures.map((lecture) => (
                  <label key={lecture.lecture_id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-secondary-700 p-2 rounded-lg">
                    <input
                      type="checkbox"
                      checked={scaffoldForm.selectedLectures.includes(lecture.lecture_id)}
                      onChange={() => toggleScaffoldLectureSelection(lecture.lecture_id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-gray-700 dark:text-gray-300">{lecture.title}</span>
                      {lecture.topics && lecture.topics.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {lecture.topics.slice(0, 4).map((t, i) => (
                            <span key={i} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-secondary-700 text-gray-500 dark:text-gray-400">{t}</span>
                          ))}
                          {lecture.topics.length > 4 && (
                            <span className="text-[11px] text-gray-500">+{lecture.topics.length - 4} more</span>
                          )}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
              {scaffoldForm.selectedLectures.length > 0 && (
                <p className="text-xs text-primary-600 dark:text-primary-400 mt-1.5">{scaffoldForm.selectedLectures.length} lecture{scaffoldForm.selectedLectures.length !== 1 ? 's' : ''} selected</p>
              )}
            </div>

            {/* Document Selection (course material files) */}
            {courseFiles.filter(f => f.text_status !== 'unsupported' && f.text_status !== 'failed').length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Documents
                </label>
                <div className="space-y-1 max-h-48 overflow-y-auto border-2 border-gray-200 dark:border-secondary-600 rounded-2xl p-3">
                  {courseFiles
                    .filter(f => f.text_status !== 'unsupported' && f.text_status !== 'failed')
                    .map((file) => (
                      <label key={file.file_id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-secondary-700 p-2 rounded-lg">
                        <input
                          type="checkbox"
                          checked={scaffoldForm.selectedFiles.includes(file.file_id)}
                          onChange={() => toggleScaffoldFileSelection(file.file_id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{file.title || file.file_name}</span>
                          {file.topics && file.topics.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {file.topics.slice(0, 4).map((t, i) => (
                                <span key={i} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-secondary-700 text-gray-500 dark:text-gray-400">{t}</span>
                              ))}
                              {file.topics.length > 4 && (
                                <span className="text-[11px] text-gray-500">+{file.topics.length - 4} more</span>
                              )}
                            </div>
                          )}
                        </div>
                        {(file.text_status === 'pending' || !file.text_status) && (
                          <span className="text-xs text-amber-600 dark:text-amber-400 flex-shrink-0">Processing</span>
                        )}
                      </label>
                    ))}
                </div>
                {scaffoldForm.selectedFiles.length > 0 && (
                  <p className="text-xs text-primary-600 dark:text-primary-400 mt-1.5">{scaffoldForm.selectedFiles.length} document{scaffoldForm.selectedFiles.length !== 1 ? 's' : ''} selected</p>
                )}
              </div>
            )}

            {/* Slide Selection */}
            {slides.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Include Slides (Optional)
                </label>
                <div className="space-y-1 max-h-32 overflow-y-auto border-2 border-gray-200 dark:border-secondary-600 rounded-2xl p-3">
                  {slides.map((slide) => (
                    <label key={slide.slide_id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-secondary-700 p-1.5 rounded-lg">
                      <input
                        type="checkbox"
                        checked={scaffoldForm.selectedSlides.includes(slide.slide_id)}
                        onChange={() => toggleScaffoldSlideSelection(slide.slide_id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{slide.title}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => { setIsGenerateScaffoldModalOpen(false); setScaffoldWizardStep(1); }} fullWidth>
                Cancel
              </Button>
              <Button type="button" onClick={handleScaffoldStep1Next} loading={scaffoldTopicsLoading} fullWidth>
                {scaffoldTopicsLoading ? 'Reading documents...' : 'Next: Choose Topics'}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Choose Topics */}
        {scaffoldWizardStep === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-500">
              These topics were extracted from your selected lectures. Choose which topics you want to create assessment scaffolds for. One scaffold will be generated per topic.
            </p>

            {scaffoldAvailableTopics.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 dark:bg-secondary-700/50 rounded-2xl">
                <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No topics found in the selected lectures.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Make sure your lectures have been processed and topics have been extracted.</p>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Available Topics ({scaffoldAvailableTopics.length})
                  </label>
                  <button
                    type="button"
                    onClick={selectAllTopics}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                  >
                    {scaffoldForm.selectedTopics.length === scaffoldAvailableTopics.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="space-y-1 max-h-64 overflow-y-auto border-2 border-gray-200 dark:border-secondary-600 rounded-2xl p-3">
                  {scaffoldAvailableTopics.map((topic) => (
                    <label key={topic} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-secondary-700 p-2 rounded-lg">
                      <input
                        type="checkbox"
                        checked={scaffoldForm.selectedTopics.includes(topic)}
                        onChange={() => toggleScaffoldTopicSelection(topic)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{topic}</span>
                    </label>
                  ))}
                </div>
                {scaffoldForm.selectedTopics.length > 0 && (
                  <div className="mt-1.5">
                    <p className="text-xs text-primary-600 dark:text-primary-400">
                      {scaffoldForm.selectedTopics.length} topic{scaffoldForm.selectedTopics.length !== 1 ? 's' : ''} selected, will generate {scaffoldForm.selectedTopics.length} scaffold{scaffoldForm.selectedTopics.length !== 1 ? 's' : ''} (~{scaffoldForm.selectedTopics.length * 2} min)
                    </p>
                    {scaffoldForm.selectedTopics.length > 10 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        Tip: Consider selecting 5-10 key topics for faster results. You can always generate more later.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => { setScaffoldWizardStep(1); setScaffoldErrors({}); }} fullWidth>
                Back
              </Button>
              <Button type="button" onClick={handleScaffoldStep2Next} fullWidth disabled={scaffoldAvailableTopics.length === 0}>
                Next: Configure
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Configure & Generate */}
        {scaffoldWizardStep === 3 && (
          <div className="space-y-5">
            {/* Summary */}
            <div className="bg-gray-50 dark:bg-secondary-700/50 rounded-2xl p-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Generation Summary</h4>
              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-500">
                <p>{scaffoldForm.selectedLectures.length} lecture{scaffoldForm.selectedLectures.length !== 1 ? 's' : ''} selected</p>
                <p>{scaffoldForm.selectedTopics.length} topic{scaffoldForm.selectedTopics.length !== 1 ? 's' : ''} to scaffold:</p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {scaffoldForm.selectedTopics.map((topic) => (
                    <span key={topic} className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border border-primary-200 dark:border-primary-800">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Preset Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Bloom's Level Range
              </label>
              <div className="grid grid-cols-2 gap-3">
                {SCAFFOLD_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => setScaffoldForm({ ...scaffoldForm, preset: preset.id })}
                    className={`relative text-left p-3 rounded-2xl border-2 transition-all duration-200 ${
                      scaffoldForm.preset === preset.id
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-500'
                        : 'border-gray-200 dark:border-secondary-600 hover:border-gray-300 dark:hover:border-secondary-500'
                    }`}
                  >
                    {preset.recommended && (
                      <span className="absolute -top-2 right-2 px-2 py-0.5 rounded-full text-[11px] font-bold bg-primary-600 text-gray-900">
                        Recommended
                      </span>
                    )}
                    <div className="font-bold text-sm text-gray-900 dark:text-white mb-1">{preset.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{preset.desc}</div>
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5,6].map((level) => {
                        const included = preset.levels.includes(level);
                        const colors = BLOOM_COLORS[level];
                        return (
                          <span
                            key={level}
                            className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${
                              included
                                ? `${colors.bg} ${colors.text} ${colors.darkBg} ${colors.darkText}`
                                : 'bg-gray-100 text-gray-400 dark:bg-secondary-700 dark:text-gray-600'
                            }`}
                            title={`L${level}: ${BLOOM_LABELS[level]}`}
                          >
                            {level}
                          </span>
                        );
                      })}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setScaffoldWizardStep(2)} fullWidth>
                Back
              </Button>
              <Button type="button" onClick={handleGenerateScaffolds} fullWidth>
                Generate {scaffoldForm.selectedTopics.length} Scaffold{scaffoldForm.selectedTopics.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Publish Scaffolds as Quiz Modal */}
      <Modal isOpen={isPublishAsQuizModalOpen} onClose={() => setIsPublishAsQuizModalOpen(false)} title="Publish as Quiz" size="md">
        {(() => {
          const stats = getPublishStats();
          return (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Quiz Title</label>
                <Input
                  value={publishAsQuizTitle}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPublishAsQuizTitle(e.target.value)}
                  placeholder="Enter quiz title"
                />
              </div>

              <div className="bg-gray-50 dark:bg-secondary-700/50 rounded-2xl p-4 space-y-2">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-semibold">{selectedScaffolds.size}</span> scaffold{selectedScaffolds.size !== 1 ? 's' : ''} selected
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-semibold text-green-600 dark:text-green-400">{stats.mcqCount}</span> MCQ question{stats.mcqCount !== 1 ? 's' : ''} (auto-graded)
                </p>
                {stats.openEndedCount > 0 && (
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-semibold text-primary-600 dark:text-primary-400">{stats.openEndedCount}</span> open-ended question{stats.openEndedCount !== 1 ? 's' : ''} (self-assessed with rubric)
                  </p>
                )}
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                The quiz will be created as a draft. You can review and publish it under Assessments, in the Quizzes list.
              </p>

              <div className="flex gap-3 pt-2">
                <Button variant="secondary" onClick={() => setIsPublishAsQuizModalOpen(false)} fullWidth>Cancel</Button>
                <Button
                  onClick={handlePublishAsQuiz}
                  loading={publishAsQuizLoading}
                  disabled={(stats.mcqCount + stats.openEndedCount) === 0 || !publishAsQuizTitle.trim()}
                  fullWidth
                >
                  Create Quiz ({stats.mcqCount + stats.openEndedCount} questions)
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Edit Course Home Modal */}
      <Modal
        isOpen={isEditHomeModalOpen}
        onClose={() => !homeSaving && setIsEditHomeModalOpen(false)}
        title="Edit Course Home"
        size="md"
      >
        <form onSubmit={handleSaveHome} className="space-y-5">
          {homeErrors.general && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 dark:bg-red-900/20 dark:text-red-400 px-4 py-3 rounded-2xl text-sm">
              {homeErrors.general}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Welcome message
            </label>
            <textarea
              placeholder="Welcome students to your course and tell them what to expect"
              value={homeForm.welcomeMessage}
              onChange={(e) => setHomeForm({ ...homeForm, welcomeMessage: e.target.value })}
              disabled={homeSaving}
              className="block w-full px-4 py-2.5 border-2 border-gray-200 rounded-2xl bg-white text-gray-900 placeholder-gray-400 dark:bg-secondary-700 dark:text-white dark:border-secondary-600 dark:placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors duration-200"
              rows={4}
            />
          </div>

          <Input
            label="Class schedule"
            placeholder="e.g., Mon and Wed, 10:00 to 11:30 AM, Room 204"
            value={homeForm.classSchedule}
            onChange={(e) => setHomeForm({ ...homeForm, classSchedule: e.target.value })}
            fullWidth
            disabled={homeSaving}
          />

          <Input
            label="Class meeting link"
            type="url"
            placeholder="https://zoom.us/j/..."
            value={homeForm.classMeetingLink}
            onChange={(e) => setHomeForm({ ...homeForm, classMeetingLink: e.target.value })}
            fullWidth
            disabled={homeSaving}
          />

          <Input
            label="Office hours"
            placeholder="e.g., Thursdays 2:00 to 4:00 PM, or by appointment"
            value={homeForm.officeHours}
            onChange={(e) => setHomeForm({ ...homeForm, officeHours: e.target.value })}
            fullWidth
            disabled={homeSaving}
          />

          <Input
            label="Office hours link"
            type="url"
            placeholder="https://zoom.us/j/..."
            value={homeForm.officeHoursLink}
            onChange={(e) => setHomeForm({ ...homeForm, officeHoursLink: e.target.value })}
            fullWidth
            disabled={homeSaving}
          />

          <Input
            label="Class location"
            placeholder="e.g. Science Hall, Room 204"
            value={homeForm.classLocation}
            onChange={(e) => setHomeForm({ ...homeForm, classLocation: e.target.value })}
            fullWidth
            disabled={homeSaving}
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-bold text-gray-700 dark:text-gray-300">Course begins</label>
              <input
                type="date"
                value={homeForm.courseStart}
                onChange={(e) => setHomeForm({ ...homeForm, courseStart: e.target.value })}
                disabled={homeSaving}
                className="block w-full rounded-2xl border-2 border-gray-200 bg-white px-4 py-2.5 text-gray-900 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-bold text-gray-700 dark:text-gray-300">Course ends</label>
              <input
                type="date"
                value={homeForm.courseEnd}
                onChange={(e) => setHomeForm({ ...homeForm, courseEnd: e.target.value })}
                disabled={homeSaving}
                className="block w-full rounded-2xl border-2 border-gray-200 bg-white px-4 py-2.5 text-gray-900 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-bold text-gray-700 dark:text-gray-300">
              Weekly meeting days
            </label>
            <div className="flex flex-wrap gap-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => {
                const active = homeForm.meetingDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    disabled={homeSaving}
                    onClick={() =>
                      setHomeForm({
                        ...homeForm,
                        meetingDays: active
                          ? homeForm.meetingDays.filter((d) => d !== day)
                          : [...homeForm.meetingDays, day],
                      })
                    }
                    className={`rounded-full border-2 px-3 py-1.5 text-xs font-bold transition-colors ${
                      active
                        ? 'border-primary-500 bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-secondary-600 dark:text-gray-400 dark:hover:bg-secondary-700'
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              Used to place class meetings on everyone's calendar
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-bold text-gray-700 dark:text-gray-300">Starts</label>
              <input
                type="time"
                value={homeForm.meetingStart}
                onChange={(e) => setHomeForm({ ...homeForm, meetingStart: e.target.value })}
                disabled={homeSaving}
                className="block w-full rounded-2xl border-2 border-gray-200 bg-white px-4 py-2.5 text-gray-900 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-bold text-gray-700 dark:text-gray-300">Ends</label>
              <input
                type="time"
                value={homeForm.meetingEnd}
                onChange={(e) => setHomeForm({ ...homeForm, meetingEnd: e.target.value })}
                disabled={homeSaving}
                className="block w-full rounded-2xl border-2 border-gray-200 bg-white px-4 py-2.5 text-gray-900 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white"
              />
            </div>
          </div>

          <Input
            label="Contact email"
            type="email"
            placeholder="e.g., instructor@university.edu"
            value={homeForm.contactEmail}
            onChange={(e) => setHomeForm({ ...homeForm, contactEmail: e.target.value })}
            fullWidth
            disabled={homeSaving}
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsEditHomeModalOpen(false)}
              fullWidth
              disabled={homeSaving}
            >
              Cancel
            </Button>
            <Button type="submit" loading={homeSaving} fullWidth>
              Save Changes
            </Button>
          </div>
        </form>
      </Modal>

      {/* New / Rename Folder Modal */}
      <Modal
        isOpen={isFolderModalOpen}
        onClose={() => !folderSaving && setIsFolderModalOpen(false)}
        title={folderModalMode === 'create' ? 'New Folder' : 'Rename Folder'}
        size="sm"
      >
        <form onSubmit={handleFolderModalSubmit} className="space-y-5">
          {folderModalError && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 dark:bg-red-900/20 dark:text-red-400 px-4 py-3 rounded-2xl text-sm">
              {folderModalError}
            </div>
          )}
          <Input
            label="Folder Name"
            placeholder="e.g., Week 1 Readings"
            value={folderNameInput}
            onChange={(e) => setFolderNameInput(e.target.value)}
            fullWidth
            required
            disabled={folderSaving}
          />
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setIsFolderModalOpen(false)} fullWidth disabled={folderSaving}>
              Cancel
            </Button>
            <Button type="submit" loading={folderSaving} fullWidth>
              {folderModalMode === 'create' ? 'Create Folder' : 'Save Name'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Folder Confirmation Modal */}
      {folderToDelete && (
        <Modal isOpen={!!folderToDelete} onClose={() => setFolderToDelete(null)} title="Delete Folder" size="sm">
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              Are you sure you want to delete "{folderToDelete.name}"? All files inside this folder will be deleted too. This action cannot be undone.
            </p>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setFolderToDelete(null)} fullWidth>Cancel</Button>
              <Button type="button" variant="danger" onClick={handleDeleteFolder} fullWidth>Delete Folder</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete File Confirmation Modal */}
      {fileToDelete && (
        <Modal isOpen={!!fileToDelete} onClose={() => setFileToDelete(null)} title="Delete File" size="sm">
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              Are you sure you want to delete this file? This action cannot be undone.
            </p>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setFileToDelete(null)} fullWidth>Cancel</Button>
              <Button type="button" variant="danger" onClick={() => handleDeleteFile(fileToDelete)} fullWidth>Delete File</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* New / Edit Assignment Modal */}
      <Modal
        isOpen={isAssignmentModalOpen}
        onClose={() => !assignmentSaving && setIsAssignmentModalOpen(false)}
        title={assignmentModalMode === 'create' ? 'New assignment' : 'Edit assignment'}
        size="md"
      >
        <form onSubmit={handleAssignmentModalSubmit} className="space-y-5">
          {assignmentModalError && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-700 dark:text-red-400 px-4 py-3 rounded-2xl text-sm">
              {assignmentModalError}
            </div>
          )}

          <Input
            label="Title"
            placeholder="e.g., Homework 3: Sorting Algorithms"
            value={assignmentForm.title}
            onChange={(e) => setAssignmentForm({ ...assignmentForm, title: e.target.value })}
            fullWidth
            required
            disabled={assignmentSaving}
          />

          <div>
            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
              Instructions
            </label>
            <textarea
              placeholder="Describe what students should submit and how it will be graded"
              value={assignmentForm.instructions}
              onChange={(e) => setAssignmentForm({ ...assignmentForm, instructions: e.target.value })}
              disabled={assignmentSaving}
              rows={5}
              className="block w-full px-4 py-2.5 border-2 border-gray-200 rounded-2xl bg-white text-gray-900 placeholder-gray-400 dark:bg-secondary-700 dark:text-white dark:border-secondary-600 dark:placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors duration-200"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                Available from
              </label>
              <input
                type="datetime-local"
                value={assignmentForm.availableFrom}
                onChange={(e) => setAssignmentForm({ ...assignmentForm, availableFrom: e.target.value })}
                disabled={assignmentSaving}
                className="block w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors duration-200"
              />
              <p className="mt-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                Leave empty to open immediately
              </p>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                Due date
              </label>
              <input
                type="datetime-local"
                value={assignmentForm.dueAt}
                onChange={(e) => setAssignmentForm({ ...assignmentForm, dueAt: e.target.value })}
                disabled={assignmentSaving}
                className="block w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors duration-200"
              />
              <p className="mt-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                Leave empty for no deadline
              </p>
            </div>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            Times are in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone.replace('_', ' ')}). Students see them converted to theirs.
          </p>

          {assignmentModalMode === 'create' && (
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
              The assignment is created as a draft. Publish it from its detail page when it's ready for students.
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setIsAssignmentModalOpen(false)} fullWidth disabled={assignmentSaving}>
              Cancel
            </Button>
            <Button type="submit" loading={assignmentSaving} fullWidth>
              {assignmentModalMode === 'create' ? 'Create assignment' : 'Save changes'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Assignment Confirmation Modal */}
      {assignmentToDelete && (
        <Modal isOpen={!!assignmentToDelete} onClose={() => setAssignmentToDelete(null)} title="Delete Assignment" size="sm">
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              Are you sure you want to delete this assignment? All student submissions will also be deleted.
            </p>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setAssignmentToDelete(null)} fullWidth>Cancel</Button>
              <Button type="button" variant="danger" onClick={() => handleDeleteAssignment(assignmentToDelete)} fullWidth>Delete Assignment</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Scaffold Confirmation Modal */}
      {scaffoldToDelete && (
        <Modal isOpen={!!scaffoldToDelete} onClose={() => setScaffoldToDelete(null)} title="Delete Question Set" size="sm">
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              Are you sure you want to delete this scaffold? This action cannot be undone.
            </p>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setScaffoldToDelete(null)} fullWidth>Cancel</Button>
              <Button type="button" variant="danger" onClick={() => handleDeleteScaffold(scaffoldToDelete)} fullWidth>Delete Scaffold</Button>
            </div>
          </div>
        </Modal>
      )}
      <FilePreviewModal
        file={previewFile ? {
          title: previewFile.title,
          file_name: previewFile.file_name,
          kind: previewFile.kind === 'link' ? 'link' : 'file',
          url: previewFile.kind === 'link' ? (previewFile.url || '') : `${getStaticBaseUrl()}${previewFile.file_path}`,
        } : null}
        onClose={() => setPreviewFile(null)}
      />

      {/* Add link modal */}
      <Modal isOpen={isAddLinkModalOpen} onClose={() => setIsAddLinkModalOpen(false)} title="Add a link" size="md">
        <form onSubmit={handleAddLink} className="space-y-5">
          {linkError && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-700 dark:text-red-400 px-4 py-3 rounded-2xl text-sm">
              {linkError}
            </div>
          )}
          <div>
            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">Link</label>
            <input
              type="url"
              required
              placeholder="https://..."
              value={linkForm.url}
              onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })}
              className="block w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors duration-200"
            />
            {(() => {
              const info = linkForm.url.trim().startsWith('https://') ? analyzeLink(linkForm.url.trim()) : null;
              if (!info) return null;
              if (info.isYouTube) {
                return (
                  <p className="mt-2 rounded-2xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 dark:bg-amber-900/25 dark:text-amber-300">
                    This is a YouTube video. Adding it under Recommended videos instead gives you transcripts and quiz support.
                  </p>
                );
              }
              return (
                <p className="mt-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                  {info.embedUrl
                    ? `Recognized as ${info.provider}. Students can view it embedded right inside the course.`
                    : `This will show as an external link that opens in a new tab. If the page has readable text, it can also be used for quiz generation.`}
                </p>
              );
            })()}
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">Title (optional)</label>
            <input
              type="text"
              placeholder="Shown to students"
              value={linkForm.title}
              onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })}
              className="block w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors duration-200"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setIsAddLinkModalOpen(false)} fullWidth>
              Cancel
            </Button>
            <Button type="submit" loading={linkSaving} fullWidth>
              Add link
            </Button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
};

export default CourseDetailPage;
